/**
 * Single source of truth for enums and shared types.
 * Mirrors the Postgres enums in docs/4-schema.md.
 *
 * ALL tunable numbers live here and are GENERATED into docs/parameters.md by
 * scripts/gen-parameters.mjs. Prose must reference a parameter by name and
 * never restate its value; `npm run docs:check` enforces this.
 */

/**
 * Extensible on purpose. Which values exist, and whether same-gender matching
 * is enabled, is a POLICY decision that is still open; see
 * docs/2-safety.md#the-gender-model--unresolved-and-blocking.
 * Blocking for the romance track and for single-gender spaces.
 */
export const GENDER = ['male', 'female'] as const;
export type Gender = (typeof GENDER)[number];

/**
 * Largest JSON response a one-off D1 backfill reads through `execFileSync`.
 *
 * NOT A PRODUCT PARAMETER, and the only reason it sits in this package is that
 * two Node scripts under `apps/api/scripts/` share it. `gen-parameters.mjs` reads
 * object and list blocks only, so a bare constant like this one never reaches
 * docs/parameters.md — which is correct here and would be a bug for anything a
 * screen can be affected by. Put a tunable number in a named group instead.
 */
export const D1_BACKFILL_MAX_OUTPUT_BYTES = 64 * 1024 * 1024;

/** Faith is a dimension, not the app's frame (docs/1). All of it optional. */
export const RELIGION = [
  'muslim',
  'christian',
  'jewish',
  'hindu',
  'sikh',
  'buddhist',
  'spiritual',
  'agnostic',
  'atheist',
  'other',
  'prefer_not_to_say',
] as const;
export type Religion = (typeof RELIGION)[number];

export const OBSERVANCE = [
  'not_religious',
  'cultural',
  'practising',
  'devout',
  'prefer_not_to_say',
] as const;
export type Observance = (typeof OBSERVANCE)[number];

export const ACCOUNT_STATE = [
  'onboarding',
  'active',
  'paused',
  'graduated',
  'shadow_limited',
  'suspended',
  'deleted',
] as const;
export type AccountState = (typeof ACCOUNT_STATE)[number];

export const VERIFICATION_STATUS = [
  'unverified',
  'selfie_pending',
  'selfie_verified',
  'id_verified',
  'rejected',
] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUS)[number];

/*
 * THERE IS NO REPLY SCOPE. `REPLY_SCOPE` / `ReplyScope` used to live here with
 * three values — `everyone`, `followers`, `space_members` — and the whole idea is
 * gone rather than trimmed. ANYONE WHO CAN SEE A POST CAN REPLY TO IT.
 *
 * The old note argued, correctly, that there must be no `same_gender` value
 * (docs/5 § D260731-1135): a gender-scoped audience is a room you opt into, not a switch on
 * a post, because admission to anything gender-scoped rests on self-declaration
 * and selfie verification proves liveness rather than gender (docs/2). That
 * reasoning holds and it turns out to reach further than the one value it was
 * aimed at. Every scope narrower than "everyone" is the same move: it takes a
 * public remark and makes replying to it conditional on who you already are to the
 * author. `followers` is that condition wearing its friendliest face — you may
 * answer me once you have subscribed to me — which is the follower count doing
 * social work in a product that refuses to show follower counts (docs/5 § D260801-1119).
 * This app exists to get strangers talking, and the highest-weighted familiarity
 * signal it has is a public back-and-forth (docs/1 § Familiarity). A control whose
 * only function is to prevent one was working against the point.
 *
 * WHAT REPLACES IT FOR THE CASE IT WAS BUILT FOR. `followers` was the brigading
 * answer. Brigading is a moderation problem and it now gets moderation tools:
 * block, mute and report hang off every piece of public content (docs/2), and the
 * discovery slice is capped so nothing can go viral in the first place (docs/3).
 * Those act on the people doing it. A scope acts on everybody who was going to
 * reply in good faith, which is the wrong end of the problem.
 *
 * AND `space_members` WAS NEVER A SCOPE, IT WAS A RESTATEMENT. A sealed space's
 * posts are returned only to its members by the query layer (see `PublicSpace`),
 * so "only this space may reply" is what "everyone" already means inside one. It
 * was a second mechanism saying what visibility had already decided, and the two
 * could disagree.
 */

/**
 * What may be attached to a public post.
 *
 * NO VIDEO IN V1, deferred, not refused (docs/5 D260731-0006). Video posts are wanted;
 * they are gated on re-sizing the moderation floor, because worst-case review
 * volume is currently computable partly on their absence, and a clip costs a
 * human reviewer real time in a way a still does not. `'video'` is added HERE
 * when that lands. This is about video POSTS; video calling is a separate
 * decision and stays voice-only (docs/3).
 *
 * A GIF is an image format, so it ships now, but an autoplaying loop would
 * walk straight through "no animation over 400ms, and respect
 * `prefers-reduced-motion`". GIFs are therefore stored as a distinct kind from
 * a still, so the client can render one as a poster the reader taps to play
 * rather than as motion nobody asked for. That rule is about motion rather
 * than format, so it applies to video unchanged when video arrives.
 */
export const POST_MEDIA_KIND = ['image', 'gif'] as const;
export type PostMediaKind = (typeof POST_MEDIA_KIND)[number];

/**
 * No `same_gender_only` tier, for the same reason there is no `same_gender`
 * reply scope (docs/5 D260731-1135): docs/2 already recorded that the gender promise
 * does not hold, and a photo shown on that promise is the worst place to make
 * one we cannot keep. Photos are public, or they are earned per-pair.
 */
export const PHOTO_TIER = [
  'public',
  /** Revealed at Friends. The required mirror shot's tier; see docs/1. */
  'connection_stage2',
  'connection_stage3',
] as const;
export type PhotoTier = (typeof PHOTO_TIER)[number];

export const MEDIA_KIND = [
  /** Required. Captured in-app, unedited. Public from stage 0. */
  'lead_selfie',
  /** Required. Captured in-app, unedited. PRIVATE until Friends (docs/1). */
  'lead_mirror',
  /**
   * Any photograph that is not one of the two lead shots — and since
   * docs/5 § D260808-2252, that means a still attached to an open letter.
   *
   * NO `post_image` KIND, AND THAT IS D1 RATHER THAN TASTE. This list is the
   * `media_kind_check` CONSTRAINT (`db/schema.ts`), and SQLite cannot alter a
   * CHECK without rebuilding the table — which for `media` means a `DROP TABLE`
   * that fires `user.avatar_media_id`'s ON DELETE SET NULL and takes every
   * account's photograph with it, because D1 cannot honour the
   * `PRAGMA foreign_keys=OFF` drizzle wraps a rebuild in (CLAUDE.md,
   * docs/5 § D260805-0328). A value added here is not a line of TypeScript; it is a
   * table rebuild.
   *
   * WHAT DISTINGUISHES A STILL ON A LETTER IS THE LETTER IT IS ON —
   * `media.post_id`, which is a column an `ALTER TABLE ADD COLUMN` can create
   * safely and which any query that needs the distinction can read. The fact
   * that would have been in the kind is `captured_in_app`, and it is already its
   * own column: a lead shot is captured through a single-use token, a still is
   * chosen from the device.
   */
  'photo',
  /* NO `banner`. The social card has no cover photo (docs/5 § D260801-1640), so there is
     no upload that could produce one, and a media kind nothing can create is a
     column that eventually gets filled by something else. */
  'voice',
] as const;
export type MediaKind = (typeof MEDIA_KIND)[number];

/**
 * What kind of room a space is.
 *
 * `general` IS NEW AND IT EXISTS BECAUSE A SPACE IS NOW REQUIRED (see
 * `PostData.space`). Every public post belongs to a room, which raises the obvious
 * question of where an ordinary remark about nothing in particular goes — and the
 * answer must not be "a topic space it is not really about", because that fills
 * every topic with off-topic posts and makes the topics worthless.
 *
 * It is its own kind rather than a `topic` called "General" for two reasons. A
 * client has to be able to find the default without matching on a title, which is
 * user-facing copy and translatable; and a general space is genuinely a different
 * object from a topic — nothing is off-topic in it, so the moderation posture and
 * the discovery weighting both differ from a room people joined for a subject.
 * There is exactly one, and it is never sealed.
 */
export const SPACE_KIND = ['general', 'womens', 'mens', 'faith', 'topic'] as const;
export type SpaceKind = (typeof SPACE_KIND)[number];

/**
 * Rooms: how many a client draws at once, and what somebody may ask for when
 * the room they wanted is not there (docs/5 § D260805-1233, § D260805-1500,
 * § D260805-1500b).
 */
export const SPACE_DEFAULTS = {
  /**
   * HOW MANY ROOMS AN OFFER IS — the handful the Feed's empty state puts in front
   * of somebody who has joined nothing.
   *
   * IT NO LONGER GOVERNS THE SEARCH FIELD'S PANEL, which was the other half of its
   * job until docs/5 § D260805-1500. That panel is the whole directory now, a page at a
   * time, because a cap of six was the mechanism holding the no-ranking rule in
   * place and the ranking is the thing that decision reopened. What remains here is
   * the genuine offer: an empty feed is not a place to put a scrolling index, and
   * six rooms somebody is not in is one glance rather than a browse.
   *
   * THE ORDER IS NOW A RANKING, AND THAT IS THE CHANGE. It used to be `space.ordinal`
   * with your own memberships lifted; it is `SPACE_PAGE`'s order — most written-in
   * first — filtered to the rooms you are not in. § D260805-1500 is where that is
   * argued, including the cost.
   */
  SPACE_OFFER: 6,

  /**
   * HOW MANY ROOMS ONE PAGE OF THE DIRECTORY IS, for the Feed's search panel, which
   * fetches the next page as the last row of this one comes into view.
   *
   * SIZED SO THE FIRST PAGE OVERFLOWS A PHONE AND THE SECOND IS ALREADY IN FLIGHT
   * BEFORE ANYBODY REACHES IT. A room row is 48px, so ten would end inside the
   * viewport on a tall phone and make the list look finished; twenty is about two
   * screens, which is the shortest page that never shows its own bottom edge on
   * first paint. Larger is worse rather than cheaper — the count on every row is an
   * aggregate over open letters, so the page size is the width of that scan.
   */
  SPACE_PAGE: 20,

  /**
   * THE LONGEST NAME SOMEBODY MAY SUGGEST A ROOM BY (docs/5 § D260805-1500b).
   *
   * A ROOM NAME, NOT A SENTENCE. The seeded titles run to about half this — *Knitting
   * and crochet* is nineteen characters — and the cap is what stops the field being
   * used as a message to whoever reads the suggestions. It is enforced on the server,
   * because a `maxLength` on an input is a hint to a browser.
   */
  SPACE_SUGGESTION_MAX_CHARS: 40,

  /** The shortest, matching the floor on a room's own slug. Two characters is not a
   *  name anybody could act on. */
  SPACE_SUGGESTION_MIN_CHARS: 3,

  /**
   * HOW MANY DIFFERENT ROOMS ONE ACCOUNT MAY SUGGEST IN A DAY.
   *
   * THE PER-NAME RULE IS NOT THIS ONE AND DOES NOT NEED A NUMBER: the same person
   * suggesting the same name twice is one row, held by a unique index, so the second
   * attempt changes nothing and is not an error (docs/5 § D260805-1500b). This is the
   * bound on the OTHER shape of spam — one account inventing forty names — and it is
   * deliberately low, because suggesting a room is a thing somebody does when a room
   * they looked for was not there, which happens once or twice.
   */
  SPACE_SUGGESTIONS_PER_DAY: 3,
} as const;

/**
 * A space as a client ever sees one. Mirrors `spaces` in docs/4.
 *
 * `sealed` is womens/mens, and it is NOT a UI flag: the query layer returns a
 * sealed space's posts only to its members and keeps them out of every feed
 * candidate source (docs/4). What the
 * client owes is the copy; the seal is partial, and docs/2 requires it to be
 * described that way rather than as protection we cannot deliver.
 */
export interface PublicSpace {
  id: string;
  slug: string;
  kind: SpaceKind;
  title: string;
  sealed: boolean;
  /*
   * NO `personal`. Page-only letters are removed until they can be their own
   * effort (docs/5 § D260806-1109b): every letter is filed in a room, so there is no
   * destination whose audience is one card and nothing about a space that a client
   * has to draw differently.
   */
}

/**
 * A room as it appears in a LIST of rooms: the room, plus how much has been written
 * in it (docs/5 § D260805-1500).
 *
 * IT IS A SEPARATE TYPE RATHER THAN A FIELD ON `PublicSpace`, and that is the whole
 * of what keeps the number out of the places it must not be. `toPublicSpace` is what
 * every other surface composes from — the room's own screen, the foot of an open
 * letter, a profile's room chips — and adding `letters` there would have put a count
 * on all of them by default, which is the *room screen* growing a tally
 * (§ D260803-1933 refuses one, and that refusal stands). Here the count has to be
 * asked for, by a caller that has actually counted.
 *
 * WHAT THE NUMBER IS: open letters standing in the room, not comments under them and
 * not members. It is the same figure for every viewer — see `letterCountsInSpace`,
 * which is where the one exception a viewer can cause is argued.
 */
export interface SpaceListing extends PublicSpace {
  letters: number;
}

/**
 * Communication capacity for a PAIR. See docs/1-product.md.
 *
 * Four stages, down from seven. The stages that used to sit above MESSAGES
 * drip-fed access to two people who had already committed and taken their only
 * slot, which protected nobody, and pushed first synchronous contact to
 * 8–10 weeks. Ramirez et al. (2015) found a curvilinear effect where moving to
 * face-to-face after ~6 weeks of text-only DAMPENS relational outcomes, so the
 * old ladder was inside the harmful zone.
 *
 * EVERY STAGE IS REACHED BY WRITING, AND NONE BY WAITING (docs/5 § D260801-1622) — and
 * since § D260802-2151, by writing TO EACH OTHER. The only thing that moves a pair is a
 * returned interaction, `RETURNED_POINTS` prices the four of them, and neither
 * person can move this alone by any amount of effort. There is nothing to
 * accept, nothing to answer, and nothing that opens because a clock ran out.
 *
 * THE NAMES SAY WHAT THE RELATIONSHIP IS, NOT WHAT IT UNLOCKS (§ D260802-2151). LETTERS,
 * TALKING and TOGETHER were named after channels, so the ladder read as a list
 * of features being handed over; FRIENDS, CONNECTED and PARTNERED name the two
 * people instead. The channels are unchanged and still live in
 * `STAGE_CAPABILITIES` — they are a consequence of a stage, never its point.
 *
 * THE GOVERNING INVARIANT: only one connection may ever be above FRIENDS.
 * Enforced by `connection_slots`' role-agnostic primary key in Postgres, not
 * here. Note that partial unique indexes on connections(a_id)/(b_id) do NOT
 * hold: because pairs are ordered by UUID, a profile occupies a different
 * column depending on its partner, so both indexes pass with two active
 * connections, and the obvious test passes too.
 *
 * Friendship lives at FRIENDS permanently, on identical pacing to the romance
 * track: the length nudge turns off and the ladder stops. Never instant,
 * never a call.
 */
export const STAGE = {
  STRANGERS: 0,
  ACQUAINTED: 1,
  FRIENDS: 2,
  CONNECTED: 3,
  PARTNERED: 4,
} as const;
export type Stage = (typeof STAGE)[keyof typeof STAGE];

/**
 * From here up, a connection is exclusive and publicly marked — and this is the
 * one line in the ladder that engagement cannot carry a pair across (§ D260802-2151).
 * Everything below it happens on its own; this needs both people to have
 * declared, privately, and is the only decision anywhere on the ladder.
 */
export const EXCLUSIVE_FROM: Stage = STAGE.CONNECTED;

/** What each stage opens. A stage-up OFFERS a channel; it never opens one. */
export const STAGE_CAPABILITIES: Record<
  Stage,
  { label: string; channel: string | null; callsPerWeek: number | 'unlimited' }
> = {
  0: { label: 'Strangers', channel: null, callsPerWeek: 0 },
  1: { label: 'Acquainted', channel: null, callsPerWeek: 0 },
  2: {
    label: 'Friends',
    channel: 'letters arriving next morning',
    callsPerWeek: 0,
  },
  3: { label: 'Connected', channel: 'messages, rate-limited', callsPerWeek: 1 },
  4: { label: 'Partnered', channel: 'unlimited', callsPerWeek: 'unlimited' },
};

/**
 * WHAT ONE INTERACTION IS WORTH TO A PAIR (docs/5 § D260804-2357, superseding § D260802-2151's
 * accumulation rule; the prices themselves are D260802-2151's and unchanged).
 *
 * ONE RULE GOVERNS THE WHOLE LADDER: each side may fill at most HALF of a
 * level, so a level closes only when both people have done their half. What
 * they do toward you counts the moment they do it — a like on your open letter
 * moves the meter — and it stops moving at the halfway mark until you answer
 * with something of your own. See `pairPoints`, which is where the arithmetic
 * lives.
 *
 * THIS REPLACES "NOTHING COUNTS UNTIL IT COMES BACK" AND KEEPS ITS ONE
 * LOAD-BEARING PROPERTY. D260802-2151 scored an interaction only once the same kind came
 * back the other way, which made the ladder unmovable by one person — the
 * property that made it safe to draw. The half rule keeps exactly that, because
 * half a level is not a level: **no amount of one-sided effort crosses a rung**,
 * so the wall docs/1 § Anti-harvesting leans on is the same height it was. What
 * changes is that the first half is legible while it happens, instead of a
 * meter that sits at zero through a real conversation and then jumps.
 *
 * WHY THE VISIBLE HALF LEAKS NOTHING, WHICH IS THE ONE THING THIS HAD TO CLEAR.
 * A meter that moves on somebody else's action is, on its face, the engagement
 * tally I5 forbids and the differencing channel § D260802-2151 closed. It survives
 * because **every act that can move your half of the bar is already, separately,
 * visible to you**: a like, a comment and a follow each land on the
 * notifications surface (`NOTIFICATION_KIND`), and a letter arrives in the loft.
 * So differencing the bar tells a viewer something they were already told
 * plainly, which is not a channel. The quantisation stays regardless
 * (`BOND_LADDER.FRACTION_STEPS`), because it is what keeps the bar from
 * resolving *which* act it was. The once-a-day settling that stood beside it is
 * gone (docs/5 § D260808-0213) — it was closing a timing channel this same argument
 * had already made redundant.
 *
 * IT IS STILL SYMMETRIC, AND THAT IS ARITHMETIC RATHER THAN CARE. `pairPoints`
 * reads the two directed totals through `min` and an absolute difference, both
 * of which are indifferent to which side is which — so the figure on your screen
 * is the figure on theirs, always, exactly as it was under D260802-2151.
 *
 * THE PRICES ARE ORDERED BY WHAT THEY COST TO GIVE, not by what they mean. A
 * like is a tap and is priced like one; a letter is the most expensive thing in
 * the product — one per pair per day, a soft target of
 * `LETTER_SOFT_TARGET_CHARS`, and the artefact the whole product exists to
 * produce — so one letter is worth about a full day of replies, because it is.
 * The cheap signals are what let a pair who have never written to each other
 * move at all, which is the whole of what the first stage is for.
 *
 * THE CHEAP SIGNALS ARE CAPPED PER DAY AND THE EXPENSIVE ONES ARE NOT, because
 * the expensive ones are capped already. `LETTERS_PER_PAIR_PER_DAY` and
 * `PER_PAIR_PUBLIC_REPLIES_PER_DAY` put a real ceiling on letters and replies;
 * likes have no per-pair cap anywhere in the product, so without
 * `LIKES_PER_DAY` two accounts could like each other into a stage
 * in an afternoon. That would not merely be farmable — it would make the
 * cheapest, most content-free act the fastest route up, which is docs/5 § D260730-1836's
 * finding restated exactly.
 *
 * IT IS A CAP, NOT A CLOCK. Nothing here requires that anything be spread out,
 * there is no distinct-days term and no span multiplier, and none may be added
 * (docs/5 § D260801-1622). Two people who spend one evening genuinely replying to each
 * other have done the thing the ladder measures; the caps only stop that
 * evening from being infinite, which is the job D260801-1622 already gives them.
 */
export const INTERACTION_POINTS = {
  /** A like. Cheap to give, so cheap to score. */
  LIKE: 1,
  /* NO `REPOST`, BECAUSE THERE IS NO SENDING ON (docs/5 § D260806-1109). It scored 2 —
     between a like and a comment — and the act it scored does not exist: what
     somebody can now do with a letter is like it, comment on it, save it or share
     its address. Nothing inherited the points. A pair's meter moves on what they
     said to each other, and adding the 2 to LIKE would have quietly doubled the
     value of the cheapest gesture in the product. */
  /** A public comment: the smallest unit of an actual conversation. */
  REPLY: 5,
  /** A letter, and the one interaction that can carry a pair up the ladder
   *  almost on its own. Hard-capped at one a day, so this is also the ceiling
   *  on how fast a correspondence moves — by design. */
  LETTER: 20,
  /**
   * Giving somebody breadcrumbs (docs/5 § D260807-0202, § D260808-2014).
   *
   * BETWEEN A TAP AND A SENTENCE, because that is what it is: more deliberate
   * than a like — it is aimed at one person, it costs something, and it can be
   * done once a day — and nothing like a comment, which is a piece of the
   * conversation the ladder exists to measure.
   *
   * NOT IN `CHEAP`, AND IT NEEDS NO DAY CAP OF ITS OWN. `LIKES_PER_DAY` exists
   * because a like has no per-pair ceiling anywhere else in the product;
   * `GIFTS_PER_FRIEND_PER_DAY` is that ceiling for this, enforced by a unique
   * day key in the ledger, so a second cap here would bound something already
   * bounded — the same argument that leaves `LETTER` uncapped here.
   *
   * IT IS NOT A WAY TO BUY THE LADDER, and three separate things make that
   * true. `GIFT_POINTS_BEFORE_LETTERS` stops bread alone from reaching a stage
   * gate — which is what has to be said now that bread may be given to somebody
   * you do not write to (docs/5 § D260808-2014), and what used to be said by the
   * correspondence being required. One a day per person means crumbs are never
   * the binding constraint, the calendar is; a purse of a thousand buys exactly
   * the same 2 points a purse of one does. And the half rule still holds, so
   * somebody gifting into silence stops at half a level however long they keep
   * it up — which is the same answer this module gives every other one-sided
   * signal.
   */
  GIFT: 2,
  /**
   * The most ONE PERSON may bank from bread toward a pair that is below Friends
   * (docs/5 § D260808-2014).
   *
   * THIS IS I1 IN A NUMBER, and it is the whole price of letting bread be given
   * to a stranger. A thread — the private channel — is opened by `settlePair` at
   * Friends, which `stageEntryPoints` puts at 24. Two people giving each other
   * bread daily bank `2 × min(given, received)` = 4 a day, so with no cap they
   * would cross 24 in six days and arrive at a private channel having never
   * interacted in public. That is exactly the path docs/1 I1 says has no price,
   * so this is not tuning: it is the invariant, written as an integer.
   *
   * SIX EACH, SO TWELVE MUTUAL, WHICH IS THE TOP OF ACQUAINTED II. Perfectly
   * matched bread banks 6 each way and `pairPoints` returns 12 — the last point
   * of Acquainted II, with the whole of Acquainted III and every rung of Friends
   * out of reach. Acquainted grants nothing at all (`STAGE_CAPABILITIES` gives
   * it no channel and no calls), so what bread reaches on its own is a chip on a
   * card, which is what "it moves things slightly" has to mean here.
   *
   * PER PERSON RATHER THAN PER PAIR, AND THAT IS LOAD-BEARING. A single pair
   * budget would let somebody giving bread into silence spend all of it alone,
   * so the day it was finally returned the reciprocity this whole rule exists to
   * reward would bank nothing. A budget each means an unanswered giver exhausts
   * only their own, and the half rule already makes their unanswered half worth
   * nothing until it is matched.
   *
   * IT DOES NOT CAP THE PAIR, ONLY THE BREAD. A pair who reach 22 points by
   * commenting on each other in public may cross into Friends on bread, and that
   * is correct — I1 asks that the path RUN THROUGH mutual public interaction,
   * not that bread be inert. What it forbids is bread being the whole path, and
   * 12 of a required 24 cannot be.
   *
   * IT LAPSES AT FRIENDS, WHICH IS NOT A LOOPHOLE. Above the gate there is
   * nothing left to cross — `earnedStage` stops at Friends, and Connected takes
   * a mutual declaration — so a pair who already write to each other bank bread
   * exactly as they did before this existed. No correspondence's behaviour
   * changed.
   */
  GIFT_POINTS_BEFORE_LETTERS: 6,
  /**
   * Most a pair can bank in a day from likes — the one interaction with no cap
   * of its own. A few each way; everything past it is warmth rather than
   * progress.
   *
   * IT WAS `LIKES_AND_REPOSTS_PER_DAY` and the number has not moved
   * (docs/5 § D260806-1109). Sending on is gone, so the cap now bounds the only
   * uncapped interaction left; the figure was set against what a day of real
   * back-and-forth is worth rather than against how many kinds of tap could feed
   * it, which is why removing one of the two does not change it.
   *
   * COUNTED PER DIRECTION, because the two directions are now banked
   * separately (`pairPoints`). Three each way, not three between them: a shared
   * budget would mean one person's likes could exhaust the other's allowance,
   * which is a way to interfere with somebody else's half of a meter.
   */
  LIKES_PER_DAY: 3,
} as const;

/**
 * The shape of the ladder: the levels each stage holds, listed by what each one
 * costs.
 *
 * ONE ARRAY PER STAGE, AND ITS LENGTH IS THE LEVEL COUNT (docs/5 § D260808-0309).
 * A separate `*_LEVELS` count beside a flat `*_LEVEL_POINTS` was two sets of
 * numbers describing one ladder, which is the failure `stageEntryPoints` is
 * already written to avoid one level up.
 *
 * A STAGE GETS ONE LEVEL PER DISTINGUISHABLE CHUNK OF WHAT IT TAKES TO LEAVE
 * IT, which is why the counts are uneven. Evenly spreading 4/4/4 across the
 * three would put numerals on Acquainted that change for no describable reason
 * and squeeze Friends, where a pair spends most of their life and a friendship
 * spends all of it.
 *
 * THE COSTS RISE INSIDE A STAGE, AND THEY MAY NEVER FALL (§ D260808-0309). A
 * rising ramp is what lets the gate be cheap without making the ladder cheap:
 * the first rung of a stage is one exchange and the last is a season of them, so
 * what used to be spent getting IN to Friends is now spent climbing it. The
 * "may never fall" half is not taste — `pairPoints` clamps a one-sided surplus
 * at half of the level being filled, and a cost that DROPPED between two levels
 * would shrink that clamp faster than the mutual term grows, which is a pair's
 * figure going backwards. `bond.test.ts` asserts the ramp is non-decreasing for
 * exactly this reason.
 *
 * THE COSTS ARE SET AGAINST THE CAPS, not picked. A pair at
 * `PER_PAIR_PUBLIC_REPLIES_PER_DAY` banks at most 25 a day each way before
 * letters and about 45 after, so Acquainted is an evening of real back-and-forth
 * and the top of Friends is months of correspondence — the pace docs/5
 * § D260801-1622 blessed when it deleted the fortnight ("four days of six real
 * conversations is better evidence than fourteen days of two").
 *
 * PARTNERED HAS NO LEVELS AT ALL — see `ViewerRelation.points`. A figure that
 * counts toward nothing is not a target, and Partnered is the only place in the
 * product where there is nothing left to reach.
 */
export const BOND_LADDER = {
  /**
   * Two people finding out whether they enjoy each other, in public — and it is
   * now a short stage rather than a wall (docs/5 § D260808-0309).
   *
   * TWENTY-THREE IS "A COUPLE OF LIKES AND A COMMENT OR TWO, BOTH WAYS". Two
   * likes and two comments each way is 12 given each, which is `matched` 24 and
   * clears `stageEntryPoints(FRIENDS)` exactly. It was 105, which took ten
   * comments each way and could not be done in a day — a fortnight-shaped gate
   * built out of counters, in front of the one thing the product is for.
   */
  ACQUAINTED_LEVELS: [4, 8, 11],
  /**
   * The most-lived-in stage in the product, and the whole of it for every
   * friendship, so it gets the most room — eight rungs since docs/5
   * § D260808-0309, and the ramp is where the ladder's length now lives. The
   * first is one exchange of letters; the last is worth more than the whole of
   * Acquainted and Friends I–IV together. The top level is unbounded: see
   * `bondLevel`.
   */
  FRIENDS_LEVELS: [20, 30, 45, 60, 80, 105, 135, 170],
  /** Shorter than Friends on purpose: it is the run-up to a decision, not a
   *  place to live. Untouched by § D260808-0309 — the romance track is deferred
   *  (`earnedStage` stops at Friends) and its pacing is tuned when it ships. */
  CONNECTED_LEVELS: [90, 120, 150],
  /**
   * How many steps a rendered fraction may have — the anti-differencing figure,
   * and the reason `StageProgress.fraction` is safe to draw at all.
   *
   * A BAR THAT MOVES WHEN THEY LIKE SOMETHING IS AN ENGAGEMENT TALLY WITH THE
   * DIGITS FILED OFF, which I5 forbids to everybody at every privacy level. At
   * full precision a viewer who was inactive all day could difference the bar
   * the next morning and read off exactly what the other person did. Quantising
   * to 8 steps makes the smallest visible move larger than the most expensive
   * single interaction, so no reading of the bar resolves one act.
   *
   * IT USED TO HAVE BELT AND BRACES, and the braces are gone: the fraction was
   * also recomputed once a day rather than live, which closed the timing channel
   * as well as the amount. It settles on the interaction now (docs/5 § D260808-0213),
   * so this constant is the whole of the defence and the reason it cannot be
   * relaxed. Quantising in the client would be theatre — a client handed the
   * true value has already been handed the channel.
   */
  FRACTION_STEPS: 8,
} as const;

/**
 * A level's numeral, indexed by the level. Not decoration, and not local to a
 * screen.
 *
 * ARABIC NUMERALS IN THIS POSITION READ AS A QUANTITY — "3" invites "of what?",
 * and the answer would be a ratio of a pair to a target. `III` reads as a name
 * for a place, the way a chapter or a monarch is numbered, and it cannot be
 * mistaken for a count of letters, which is the number already sitting next to
 * it in every reader's head.
 *
 * IT IS IN THE SHARED PACKAGE BECAUSE IT IS A FUNCTION OF THE LADDER (docs/5
 * § D260808-0309). It was written out twice — once on the chip, once on the
 * server that files milestone labels — and both copies stopped at `V` because
 * the deepest stage had five levels. Friends has eight now, and the chip's copy
 * had no fallback, so the card would have rendered an empty numeral rather than
 * anything anybody could have noticed in a review. `bond.test.ts` asserts this
 * covers the deepest stage, which is the check that could not exist while the
 * array lived next to the markup.
 */
export const LEVEL_NUMERAL = [
  '',
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
] as const;

/** The ladder above, keyed by stage, which is how every caller wants it. */
const LADDER: Record<Stage, readonly number[]> = {
  0: [],
  1: BOND_LADDER.ACQUAINTED_LEVELS,
  2: BOND_LADDER.FRIENDS_LEVELS,
  3: BOND_LADDER.CONNECTED_LEVELS,
  4: [],
};

/** How many levels a stage has. 0 at Strangers and at Partnered, for opposite
 *  reasons: nothing has started, and nothing is left to reach. */
export function levelsIn(stage: Stage): number {
  return LADDER[stage].length;
}

/**
 * Which level a stage-relative total is filling, and that level's cost.
 *
 * THE ONE PLACE THE RAMP IS WALKED. `bondLevel` needs it to draw a meter and
 * `halfOfLevel` needs it to size a clamp, and a second walk written at either
 * call site is a second copy of the ladder's shape.
 *
 * PAST THE TOP IT ANSWERS WITH THE TOP, because the last level of every stage
 * is unbounded — a pair can sit at Friends VIII indefinitely and keep banking.
 */
function levelAt(
  stage: Stage,
  into: number,
): { level: number; cost: number; filled: number } {
  const costs = LADDER[stage];
  let left = Math.max(0, into);
  let cost = 0;
  let level = 0;
  for (const next of costs) {
    level += 1;
    cost = next;
    if (left < next) return { level, cost, filled: left };
    left -= next;
  }
  /* Past the top of the stage, or at a stage with no levels at all — in which
     case the caller has already decided this figure means nothing. */
  return { level, cost, filled: cost };
}

/**
 * The pair's total at which a stage's first level begins.
 *
 * DERIVED, NEVER AUTHORED. Writing the entry points out beside the level costs
 * would be two sets of numbers describing one ladder, and two sets of numbers
 * describing one thing are two sets of numbers that eventually disagree.
 */
export function stageEntryPoints(stage: Stage): number {
  let total = 0;
  for (let s = 0 as number; s < stage; s++) {
    for (const cost of LADDER[s as Stage]) total += cost;
  }
  /* The first returned interaction is what makes two people Acquainted, so
     stage 1 begins at 1 rather than at 0: zero points is a stranger. */
  return stage === STAGE.STRANGERS ? 0 : total + 1;
}

/**
 * Where a pair's total puts them inside a stage: which level, and how far
 * through it.
 *
 * THE STAGE IS PASSED IN RATHER THAN DERIVED, and that is not an oversight.
 * Points alone cannot tell you a pair is Connected — crossing that line takes a
 * mutual declaration (`EXCLUSIVE_FROM`), so a pair may sit at the top of
 * Friends with more than enough points and stay there indefinitely, which is a
 * perfectly good place to be. Points bank while they do, and count from the
 * moment they cross.
 *
 * Returns `null` where there are no levels to be at: Strangers, and Partnered.
 */
export function bondLevel(
  stage: Stage,
  points: number,
): { level: number; fraction: number } | null {
  const levels = levelsIn(stage);
  if (!levels) return null;

  const { level, cost, filled } = levelAt(
    stage,
    points - stageEntryPoints(stage),
  );
  /*
    THE TOP LEVEL OF A STAGE IS UNBOUNDED, and its fraction is 1 rather than a
    number that keeps resetting. Nothing draws a bar there — a meter is only
    ever drawn toward a rung engagement alone can reach, and the top of Friends
    is not one of them for a pair on the romance track (a full bar under a line
    you have not crossed is the product saying you are one decision behind, on
    every visit, which docs/5 § D260801-1120 forbids) nor for a friendship (which is not
    on the way anywhere and must never be drawn as a romance that stalled).
  */
  const raw = level === levels ? 1 : filled / cost;

  /* Quantised before it leaves, never after: a client that receives the true
     value has already been handed the differencing channel, whatever it then
     chooses to draw. See BOND_LADDER.FRACTION_STEPS. */
  const steps = BOND_LADDER.FRACTION_STEPS;
  return { level, fraction: Math.round(raw * steps) / steps };
}

/**
 * The most one person may contribute to a single level: half of it, rounded
 * down (docs/5 § D260804-2357).
 *
 * IT IS HALF OF THE LEVEL BEING FILLED, NOT HALF OF THE STAGE (docs/5
 * § D260808-0309). While every level of a stage cost the same there was nothing
 * to choose between the two; on a ramp there is, and a clamp fixed at the
 * stage's first level would be worth 10 points against a rung of 170 — an
 * unanswered letter would stop counting almost immediately at the top of Friends
 * and the visible half of the bar, which is the whole of what § D260804-2357
 * bought, would fade out as a pair got closer.
 *
 * `mutual` IS THE PAIR'S MATCHED TERM, and passing that rather than their total
 * is what keeps this from being circular: completed levels fall out of `matched`
 * alone (see `pairPoints`), so the level this sizes against is settled before
 * the clamp it sizes is applied.
 *
 * ROUNDED DOWN, AND ODD COSTS MAKE THE ROUNDING LIVE rather than decorative:
 * half of 11 is 5, and two 5s come to 10 — one short of the level. `ceil` would
 * instead let two halves come to 12 and overshoot it.
 *
 * THE PARITY IS NOT WHAT KEEPS A RUNG SAFE, THOUGH, and it is worth being exact
 * about that because it would be easy to think it was. A rung is crossed on the
 * `matched` term alone, and `matched` cannot rise without both people paying —
 * so no choice of rounding here could produce a one-sided crossing. `floor` is
 * chosen because a cap should not exceed the thing it caps, which is a statement
 * about this function rather than about the ladder.
 *
 * A STRANGER PAIR IS WORKING ON THE FIRST LEVEL OF ACQUAINTED, which is why
 * stage 0 answers with Acquainted I's figure rather than with 0. `LADDER` gives
 * Strangers and Partnered no levels because neither has a rung to be part of the
 * way toward; the half-cap is about the level being FILLED, and for a pair with
 * nothing yet that is Acquainted I.
 */
export function halfOfLevel(stage: Stage, mutual = 0): number {
  if (!levelsIn(stage)) {
    return Math.floor(BOND_LADDER.ACQUAINTED_LEVELS[0] / 2);
  }
  const { cost } = levelAt(stage, mutual - stageEntryPoints(stage));
  return Math.floor(cost / 2);
}

/**
 * A pair's total, from the two DIRECTED totals of what each has given the other
 * (docs/5 § D260804-2357). This is the figure `bondLevel` and `ViewerRelation.points`
 * both consume, and the only place the half rule is implemented.
 *
 * THE WHOLE RULE IN THREE TERMS:
 *
 *   matched   = 2 × min(given, received)   — what both sides have paid for
 *   unmatched = |given − received|          — what one side has paid alone
 *   total     = matched + min(unmatched, halfOfLevel(stage, matched))
 *
 * WHY `min` AND AN ABSOLUTE DIFFERENCE RATHER THAN A BRANCH ON WHO IS AHEAD.
 * Both are symmetric functions of the two totals, so the result cannot depend
 * on which person is asking — which is what lets the server send one figure to
 * both people and is the property `ViewerRelation` is documented on. A branch
 * ("if I am ahead…") would compute two different numbers for one pair, and the
 * bug that produces is the bar disagreeing across two screens, which is
 * unfalsifiable from either one of them.
 *
 * WHY COMPLETED LEVELS FALL OUT OF `matched` ALONE, which is what makes this
 * well-defined rather than circular. A level closes only when both sides have
 * given half of it; that is exactly `min(given, received) ≥ cost / 2`, i.e.
 * `matched ≥ cost`. So the mutual term decides which rung a pair is on and the
 * one-sided term only ever fills the rung they are standing on — it can never
 * carry them over it, however lopsided the pair becomes. It is also why the
 * clamp is sized from `matched` rather than from the result: the level is
 * settled before the clamp that fills it is chosen (docs/5 § D260808-0309).
 *
 * ZERO UNTIL BOTH HAVE DONE SOMETHING, and this is a product judgement rather
 * than arithmetic. `matched === 0` short-circuits, so a stranger who likes one
 * open letter does not thereby become Acquainted with its author. Without it,
 * every passing like would put a relation chip on a card and *Acquainted* would
 * stop meaning anything — the ladder's first rung is supposed to say two people
 * have met, and one tap in one direction is not that. Once a pair is on the
 * ladder at all, either side may fill half a level unanswered, which is the
 * behaviour § D260804-2357 asked for.
 *
 * MONOTONIC IN BOTH ARGUMENTS, which the ladder needs because nothing on it may
 * ever go backwards. Raising the lagging total by one raises `matched` by two
 * and lowers `unmatched` by one, for a net gain; raising the leading total
 * raises `unmatched` alone, and that term is clamped rather than subtracted. So
 * no interaction by either person can lower the pair's figure.
 *
 * ON A RAMP THAT ARGUMENT NEEDS ONE MORE CLAUSE, and it is why `BOND_LADDER`
 * forbids a stage's costs from falling (docs/5 § D260808-0309). Raising the
 * lagging total also moves `matched`, which can move the level the clamp is
 * sized against; while costs only ever rise, the clamp only ever rises with it
 * and the net gain above survives. A cost that fell between two levels could
 * shrink the clamp by more than the two points gained, and a pair would watch
 * their figure go down because one of them wrote something.
 */
export function pairPoints(
  stage: Stage,
  given: number,
  received: number,
): number {
  const matched = mutualPoints(given, received);
  if (matched === 0) return 0;
  const unmatched = Math.abs(given - received);
  return matched + Math.min(unmatched, halfOfLevel(stage, matched));
}

/**
 * The `matched` term on its own — what both sides have paid for, and the only
 * part of a pair's figure that one person cannot move.
 *
 * EXPORTED BECAUSE THE COMMUNITY LEVEL IS BUILT OUT OF IT (`pairStanding`,
 * docs/5 § D260808-0209), and it has to be the same arithmetic rather than the
 * same shape written twice: two spellings of "what came back" are two ladders
 * that disagree the first time either is tuned.
 */
export function mutualPoints(given: number, received: number): number {
  return 2 * Math.min(given, received);
}

/**
 * Starting values only. Tune against real cohort data; see docs/5-decisions.md.
 *
 * EVERY GATE HERE COUNTS ENGAGEMENT. Not one of them counts elapsed time, and
 * none may be added that does (docs/5 § D260801-1622). The reason is arithmetic rather
 * than taste: letters are capped at LETTERS_PER_PAIR_PER_DAY and public replies
 * at PER_PAIR_PUBLIC_REPLIES_PER_DAY, so content is *already* rate-limited by
 * time. A day counter on top of a content counter measures the same thing
 * twice, and it lets a year of silence advance a pair, which is hollow — the
 * argument D260730-1840 made for rings, applied to stages.
 *
 * The practical consequence is that a pair always knows what to expect: things
 * move when the two of them write to each other, and never otherwise.
 *
 * THE STAGE GATES THEMSELVES ARE NO LONGER HERE. `ACQUAINTED_CONVERSATIONS` and
 * `LETTERS_CONVERSATIONS` are gone, replaced by `RETURNED_POINTS` and
 * `BOND_LADDER` (docs/5 § D260802-2151): a conversation count could only ever describe
 * one kind of interaction, and the ladder now counts all four. What is left
 * below is pacing — caps and floors on individual acts, none of which advance
 * anybody.
 */
/*
 * STRAYS_PER_WEEK, MAX_OUTSTANDING_STRAYS, STRAY_MAX_FINDERS and STRAY_DRIFT_DAYS
 * WERE IN HERE, and they are gone with the object (docs/5 § D260803-1954). There are two
 * kinds of letter now — an open letter, public and in a room, and a direct letter
 * to somebody you already write to — so there is no circulation window, no finder
 * count and no outstanding-one rule left to size.
 *
 * OUTSIDE THE OBJECT LITERAL ON PURPOSE. `scripts/gen-parameters.mjs` treats every
 * comment line inside the braces as the note belonging to the NEXT key, so a
 * tombstone in there is published into docs/parameters.md against an unrelated
 * parameter — which is exactly what it did to CROSS_GENDER_REPLIES_PER_DAY before
 * this comment moved up here.
 */
export const PACING_DEFAULTS = {
  /**
   * Substantive letters each side before CONNECTED becomes available.
   *
   * NEVER RENDER THIS AS A COUNTER. "2 of 4" is a quota, and quotas produce
   * filler, the streak literature is unambiguous that counted exchanges
   * converge on content-free minimums (photos of the floor captioned
   * "streak"), and recipients resent them. Gate on reciprocated depth,
   * assessed with the low-information classifier; surface it as a sentence
   * ("you've been writing since March"), never as a meter.
   */
  SUBSTANTIVE_LETTERS_EACH: 4,
  /**
   * A SOFT nudge, not a hard floor, a hard minimum produced padding, and the
   * low-information classifier already stops "hey :)" counting toward
   * advancement. The composer shows progress; it does not block.
   */
  LETTER_SOFT_TARGET_CHARS: 350,
  /**
   * One letter per pair per day, friendship or romance. The generous ceiling
   * is what makes the cap painless: you can say everything, you just say it
   * once a day. The composer QUEUES for tomorrow rather than refusing, so the
   * cap can only ever schedule effort, never destroy it.
   */
  LETTERS_PER_PAIR_PER_DAY: 1,
  LETTER_MAX_CHARS: 8000,
  /** Voice letters: a real floor, client-enforced and server-validated. */
  VOICE_LETTER_MIN_SECONDS: 30,
  VOICE_LETTER_MAX_SECONDS: 300,
  /**
   * Letters resolve to the recipient's local hour the NEXT day. Store the
   * local calendar DAY and resolve the instant at dispatch, a precomputed
   * timestamptz is an hour wrong across a DST boundary, and people travel.
   */
  LETTER_DELIVERY_HOUR_LOCAL: 8,
  /** Spread each timezone's burst: hash(recipient_id) % this, in seconds. */
  LETTER_DELIVERY_JITTER_SECONDS: 900,
  /**
   * How long a letter you wrote to YOURSELF stays with the pigeon
   * (docs/5 § D260807-2009).
   *
   * SECONDS RATHER THAN THE DAWN FLIGHT, AND IT IS THE ONLY PLACE PACING IS
   * SHORTENED. The flight is what a letter IS here — it is sealed, it is gone,
   * and it comes back to you as post — and a drawer that skipped it would be a
   * notes field. A drawer that kept the overnight one would be a notes field you
   * cannot read until tomorrow. So the journey is kept whole and its length is
   * cut to the shortest interval that still reads as a journey: long enough that
   * the envelope is genuinely shut and you have let go of the words, short
   * enough to sit through.
   *
   * NOTHING ELSE MAY READ THIS. It is not a discount that can be spent on a
   * letter to another person — docs/1 § Cosmetics never touch pacing forbids a
   * shorter delivery outright, and that rule is about the ONE letter this
   * parameter cannot reach. It applies where there is no second person, which is
   * why it lives next to the delivery hour rather than replacing it.
   */
  SELF_LETTER_FLIGHT_SECONDS: 24,
  /**
   * Cap on correspondences awaiting a reply FROM THE OTHER PERSON. Answered
   * ones don't count, so an actively reciprocating user is never blocked.
   *
   * This is Hinge's tested shape (Your Turn Limits: 8 unanswered → +20%
   * responsiveness), not a raw concurrency cap. It punishes broadcast
   * behaviour, which is the body-photo harvesting pattern (docs/1), while
   * leaving sociable users alone.
   *
   * ASYMMETRIC BY SIDE. Jung et al. (randomised, N=6,327): expanding women's
   * daily capacity 30→60 raised chats initiated 113%, while expanding men's
   * had a negative cross-side effect. Capping the under-represented side is
   * the expensive mistake. This value is for the over-represented side.
   */
  MAX_UNANSWERED_CORRESPONDENCES: 8,
  /** Reciprocal letters each way before the lead mirror photo is revealed. */
  LETTERS_BEFORE_MIRROR_REVEAL: 2,
  /** Lead photos expire and prompt a one-tap retake. Correspondence is never
   *  interrupted over a photo, only romance-track participation pauses. */
  LEAD_PHOTO_EXPIRY_MONTHS: 12,
  /** Cross-gender public replies per day. Per-pair cap is separate. */
  CROSS_GENDER_REPLIES_PER_DAY: 25,
  /**
   * Reciprocal letters each way per ring. Rings measure ACCUMULATED CONTENT
   * ONLY, never elapsed time. Because letters are already capped at
   * LETTERS_PER_PAIR_PER_DAY, content is inherently rate-limited by time, so
   * counting time as well would double-count it AND would let a year of
   * silence advance a friendship, which is hollow.
   *
   * Counted as min(letters_a_to_b, letters_b_to_a), so rings are reciprocal
   * by construction: unanswered letters earn nothing.
   */
  LETTERS_PER_RING: 50,
  /**
   * Per-pair public replies per day, EACH DIRECTION.
   *
   * THIS IS THE ONLY THING STANDING BETWEEN A PAIR AND A SPRINT, now that no
   * gate counts days. It is enough: the stage gates count *conversations* with
   * reciprocated back-and-forth, and this cap means neither person can hold up
   * more than a few turns of one a day. Real time still passes; it just is not
   * counted a second time (docs/5 § D260801-1622).
   */
  PER_PAIR_PUBLIC_REPLIES_PER_DAY: 5,
  /** Minimum received volume before a responsiveness rate is computed at all. */
  RESPONSIVENESS_VOLUME_FLOOR: 5,
} as const;

/**
 * Public posting. These are the numbers docs/1 used to write out in prose,
 * which rule 1 of the doc set forbids for exactly the reason it happened here:
 * an unvalidatable number drifts away from the code that enforces it.
 */
export const POST_DEFAULTS = {
  /**
   * Short enough that a post is a remark rather than an essay, the essay
   * belongs in a letter, where LETTER_MAX_CHARS is sixteen times larger. The
   * composer counts down, and since an author may rewrite what they wrote
   * (docs/5 D260805-0418) the limit is enforced on the edit as well — which is why
   * it is a CHECK on the table and not only a rule in the composer.
   */
  POST_MAX_CHARS: 500,
  /**
   * The floor, and it is deliberately low (docs/5 § D260804-2358).
   *
   * A MINIMUM IS A DIFFERENT INSTRUMENT FROM A MAXIMUM AND HAS TO BE SIZED
   * DIFFERENTLY. The ceiling shapes what a post IS; a floor only has to stop
   * the degenerate case — the empty body, the single stray character, the "." —
   * from occupying a room's column as though it were a remark. Anything higher
   * starts rejecting sentences people meant, and docs/8 § C already records
   * what happens when a length gate bites for real: the writing gets padded to
   * clear it. So this refuses non-content and nothing else.
   *
   * IT IS NOT A QUALITY BAR AND MUST NEVER BE RAISED INTO ONE. The product's
   * answer to a thin post is the low-information classifier, which declines to
   * COUNT it toward anything, rather than a length rule that declines to accept
   * it. The two are not interchangeable: a floor a person can see is a target
   * they will write up to, which is docs/5 § D260730-1836's finding about quotas applied
   * to characters.
   */
  POST_MIN_CHARS: 2,
  /**
   * Stills per open letter (docs/5 § D260808-2252).
   *
   * IT WAS FOUR, FOR A MOSAIC THAT NOTHING COULD FILL. Four is the number an
   * X-style 2×2 grid wants, and the grid was scaffold — there was no upload
   * pipeline behind it, so the number was never met by anything a person could
   * make. Now that a still can be attached, the number is set by how the stills
   * are DRAWN: prints laid on the letter at slight angles, overlapping
   * (`PostMediaGrid`). Three fit across a phone's column with each one's subject
   * still legible; a fourth turns the pile into a texture.
   *
   * IT IS ALSO WHAT A CORRESPONDENCE APP NEEDS. Three photographs is an
   * afternoon shown to somebody; the fourth is the beginning of an album, and an
   * album is a feed of its own.
   */
  POST_MAX_IMAGES: 3,
  /** A poll needs enough choices to be a choice, but not a survey. */
  POLL_MIN_OPTIONS: 2,
  POLL_MAX_OPTIONS: 6,
  POLL_OPTION_MAX_CHARS: 120,
  /**
   * OPEN LETTERS PER PERSON PER DAY (docs/5 § D260804-2358).
   *
   * A RATION, NOT A RATE LIMIT, and the difference is which failure it is
   * built for. A rate limit exists to protect a server from a script; this
   * exists to protect a reading surface from a person, and the number is
   * therefore set by what a room can absorb rather than by what the database
   * can take. Six is enough that nobody with something to say runs out, and
   * few enough that no single account can be most of what anyone read today —
   * which is the same anti-influencer argument FEED_DISCOVERY_REACH_CAP makes
   * about one post, made about one author.
   *
   * IT REPLACES NOTHING AND OVERLAPS NOTHING. docs/3 already decayed marginal
   * reach past ~3 posts a day, and that is a RANKING response: post more and
   * each one travels less. This is an absolute one — past six there is no
   * seventh — and the two are complementary, because a decay curve alone never
   * closes the door on a flood, it only makes the flood cheap to serve.
   *
   * COMMENTS ARE NOT RATIONED BY THIS. A comment is an answer to somebody, and
   * a cap on answering would punish exactly the behaviour the product wants;
   * PER_PAIR_PUBLIC_REPLIES_PER_DAY and CROSS_GENDER_REPLIES_PER_DAY are the
   * pacing that governs replies, and they are per-pair rather than per-author
   * for the same reason.
   */
  POSTS_PER_DAY: 6,
  /**
   * Threads are SINGLE-LEVEL: you reply to a post, never to a reply. Nesting
   * is what turns a public conversation into a private one held in front of an
   * audience, and familiarity(A, B) is built from back-and-forth in the open.
   */
  MAX_REPLY_DEPTH: 1,
} as const;

/**
 * One of the two attachments an open letter carries (docs/5 § D260805-1220).
 *
 * A GIF IS AN EMBED, NOT AN UPLOAD, and that is the whole reason it is the
 * attachment that shipped first. Nothing is stored, transcoded, scanned or served
 * by us: a chosen GIF is a provider id and two URLs on the post's own row, which
 * costs a row's width and no bytes. A still is the other half of that sentence —
 * an upload, our bytes, our bill — and it ships now that there is a pipeline
 * behind it (docs/5 § D260808-2252, IMAGE_DEFAULTS). What did not change is the
 * classifier: docs/2 § Moderation records what is and is not enforced before
 * publication, for both of them.
 *
 * THE PROVIDER IS KLIPY, reached through our own Worker so that the key never
 * reaches a client and one cache serves every viewer of the same search. It was
 * Tenor until Google closed that API (docs/5 § D260805-1521), which is the reason
 * these numbers are here rather than in the fetch: a provider is replaceable and
 * the product's own limits are not.
 */
export const GIF_DEFAULTS = {
  /**
   * GIFs per request, and therefore per page of the picker.
   *
   * The provider accepts 8 at least and 50 at most. Twenty-four is two full
   * screens of a two-column grid on a phone, which is the unit the picker actually
   * pages by — a page that ends mid-screen reads as the end of the results rather
   * than as more to come.
   */
  GIF_PAGE: 24,
  /**
   * How long a search may be. Not a product limit — a bound on what goes into a
   * URL we build against somebody else's API, which is the kind of thing that
   * has to have a number rather than a hope.
   */
  GIF_QUERY_MAX_CHARS: 64,
  /**
   * How many GIFs one account may star.
   *
   * A CEILING RATHER THAN A QUOTA (docs/5 § D260730-1836): nothing counts up to
   * it on screen and nothing is spent, because a favourite is a bookmark and a
   * visible allowance would turn it into a currency. Two hundred is roughly
   * where a grid you scroll stops being a set you recognise, and it is a number
   * nobody using the feature as intended will meet.
   */
  GIF_FAVOURITES_MAX: 200,
  /**
   * How many recently sent GIFs a DEVICE remembers — a device rather than an
   * account on purpose (docs/5 § D260805-1221): recents are local, favourites
   * are yours everywhere.
   */
  GIF_RECENTS_MAX: 24,
  /**
   * The provider's content filter, at the strictest setting it offers.
   *
   * `g` AND NEVER LOOSER. docs/2 makes every image in this product subject to
   * pre-publication classification, and an embed is the one image we do not
   * classify ourselves — so the provider's filter is the whole of that control
   * here, and there is no reading of this product in which a looser setting is
   * the right trade. It is a parameter rather than a literal in the fetch so
   * that it is one value, in the doc set, beside the sentence saying why.
   *
   * THE VALUE IS THE PROVIDER'S VOCABULARY, WHICH IS WHY IT CHANGED. Tenor called
   * the strictest setting `high` and KLIPY calls it `g`, out of `g`, `pg`, `pg-13`
   * and `r` (docs/5 § D260805-1521). The parameter did not become less strict when
   * it was renamed — but a value that reads as a word in someone else's dictionary
   * is a value to re-read when the dictionary changes, not to translate by feel.
   *
   * KLIPY ALSO KEEPS A FILTER ACCOUNT-SIDE, in its Partner Panel, including a
   * keyword blocklist. That is a second control in a place this repository cannot
   * assert; the decision entry says so plainly rather than letting the sent
   * parameter imply it is the whole of the setting.
   */
  GIF_CONTENT_FILTER: 'g',
  /**
   * The longest description a GIF carries. The provider writes one, the sender
   * may rewrite it, and it is what a screen reader reads — so it is bounded like
   * every other field somebody can type into.
   */
  GIF_ALT_MAX_CHARS: 240,
  /**
   * How long a page of SEARCH results may be reused, in seconds.
   *
   * THE CACHE IS THE WHOLE COST STORY (docs/5 § D260805-1748). A GIF page is the one
   * response in this API that is identical for everybody, so one reader's search can
   * answer everybody else's — and the provider's free tier is counted in requests per
   * hour, not in bytes. An hour is a big cut in that count for a result nobody can
   * tell is an hour old: "thank you" does not move, and neither does the set of GIFs
   * that match it.
   */
  GIF_CACHE_SECONDS: 3600,
  /**
   * How long the TRENDING page may be reused, in seconds — six times longer.
   *
   * IT IS THE HOTTEST KEY IN THE PRODUCT and the slowest-moving thing behind it.
   * Every picker opens on it (docs/5 § D260805-1221: an empty field is not an empty
   * screen), so it is the one request that would otherwise scale with the number of
   * people who tap the GIF button rather than with the number of distinct things they
   * search. Six hours means four provider calls a day for it, whether ten people open
   * the picker or ten thousand.
   *
   * WHY NOT LONGER. "Trending" that is a day old is a lie a reader can notice, and the
   * saving from six hours to twenty-four is four requests against a quota measured in
   * hundreds per hour. The number buys nothing after this point.
   */
  GIF_TRENDING_CACHE_SECONDS: 21_600,
} as const;

/**
 * A STILL ON AN OPEN LETTER: what the device must do to it, and what the server
 * refuses (docs/5 § D260808-2252).
 *
 * THE DEVICE SCALES AND THE SERVER JUDGES, and that split is forced rather than
 * chosen: a Worker cannot decode an image inside its CPU budget, so there is no
 * server-side resize to fall back on. The client decodes the file it was given,
 * draws it into a canvas at a bounded edge, and re-encodes; the server reads the
 * header of what arrives and refuses anything that skipped that. Every number
 * below is therefore enforced TWICE, on purpose — the client's copy is what
 * makes an ordinary photograph work, the server's is what makes a crafted
 * request fail (`lib/image.ts` on both sides).
 *
 * A CANVAS RE-ENCODE IS ALSO THE VALIDITY CHECK, and it is a stronger one than
 * any header sniff: bytes that will not decode into a bitmap cannot be scaled,
 * so a truncated JPEG, a renamed PDF and an SVG announcing itself as a
 * photograph are all refused on the device with a sentence, before an upload
 * starts. Metadata dies in the same step — a canvas carries no EXIF — and the
 * server strips it again anyway (`stripMetadata`), because "the client already
 * did it" is not a property a server can verify.
 */
export const IMAGE_DEFAULTS = {
  /**
   * The longest edge that is ever sent, in pixels.
   *
   * SET BY THE WIDEST THING THAT DRAWS IT, not by what a camera produces. A
   * still is read at a phone column's width — under 500 CSS pixels, so about
   * 1500 device pixels on a 3× screen — and the one surface that could want more
   * is the full-size viewer, which is the same column at full height. 1600 covers
   * both with a margin and is a quarter of the pixels of a modern phone capture,
   * which is the whole point: a 12-megapixel photograph is 4 MB of somebody's
   * data allowance to look at a picture 1500 pixels wide.
   *
   * BIGGER THAN THE AVATAR'S 1024, and deliberately: an avatar is a face in a
   * 160px circle, a still is the letter's subject.
   */
  IMAGE_MAX_EDGE: 1600,
  /**
   * The smallest edge that counts as a photograph.
   *
   * Below this an attachment is an icon, a tracking pixel or a mistake — and it
   * is drawn at column width, where 24 pixels of source is a smear. The refusal
   * is on the device, where the person can pick a different file.
   */
  IMAGE_MIN_EDGE: 64,
  /**
   * The most lopsided shape that may be sent, as a ratio of long edge to short.
   *
   * NOT A TASTE RULE — the print is cropped to a readable box whatever arrives,
   * so a panorama is welcome and is drawn as one. This refuses the shapes that
   * are not pictures of anything: a 1×4000 strip is a spacer, a rule, or an
   * attempt to make a letter's layout misbehave. Twelve is past every real
   * panorama and short of all of those.
   */
  IMAGE_MAX_ASPECT: 12,
  /**
   * The ceiling on one still AFTER the device has scaled it, in bytes.
   *
   * A CAP RATHER THAN A RESIZE, exactly as the avatar route's is: the client is
   * responsible for the downscale and the server for refusing anything that
   * skipped it. 2 MB is generous for a 1600px JPEG — one is typically 200–400 KB
   * — and small enough that a full-resolution capture is rejected rather than
   * stored. The client re-encodes at falling quality until it fits, so the
   * ceiling is reached by a slightly softer photograph rather than by a refusal.
   */
  IMAGE_MAX_BYTES: 2 * 1024 * 1024,
  /**
   * The JPEG quality the device encodes at, before any fitting pass.
   *
   * Lower than the avatar's 0.9 because this is a scene rather than a face at
   * 160px, and 0.82 is the point where a photograph at 1600px stops improving
   * visibly per byte spent. A still with transparency is encoded as PNG instead
   * and this does not apply to it (`lib/image.ts`).
   */
  IMAGE_JPEG_QUALITY: 0.82,
  /**
   * The longest description a still carries. The same bound as a GIF's, and for
   * the same reason: it is what a screen reader reads, and it is typed.
   */
  IMAGE_ALT_MAX_CHARS: 240,
  /**
   * How many stills one account may upload in a day.
   *
   * IT IS NOT A SECOND RATION AND IT IS NOT ABOUT PACING. POSTS_PER_DAY already
   * bounds how much anybody publishes, and this bounds something else entirely:
   * an upload endpoint spends OUR storage the moment it is called, before any
   * letter exists, so a client that uploads and never sends still costs bytes.
   * Set well above POSTS_PER_DAY × POST_MAX_IMAGES so that changing your mind
   * about a photograph — attach, remove, attach another — is free, and far below
   * what a script would want.
   */
  IMAGE_UPLOADS_PER_DAY: 60,
} as const;

/**
 * The card a person fills in, and the only fields any form in this product
 * collects about a person (docs/5 § D260805-0242).
 *
 * THESE WERE WRITTEN THREE TIMES BEFORE THEY LIVED HERE — once in the zod
 * schema on `POST /me/onboarding`, once in the zod schema on `PATCH /me`, and
 * once as a `maxLength` on the signup form. A ceiling the client counts down to
 * and the server refuses at has to be one number: two copies mean a form that
 * lets somebody finish a sentence the server then rejects, which is the worst
 * version of a limit because the writing is already done.
 */
export const PROFILE_CARD = {
  /**
   * The handle's floor and ceiling, and the pattern below is built from them.
   *
   * THREE is the floor because two-character handles are a scarce namespace
   * people fight over. TWENTY is the ceiling because a handle is rendered
   * beside a display name in a row that also has to hold a chip.
   */
  HANDLE_MIN_CHARS: 3,
  HANDLE_MAX_CHARS: 20,
  /**
   * A display name. Longer than a handle because it is a name rather than an
   * address — "Abdurrahman al-Ghazzawi" is 23 characters and is somebody's
   * actual name, and a limit that refuses it is a limit that tells them their
   * name is wrong.
   */
  DISPLAY_NAME_MAX_CHARS: 40,
  /** A city, CITY-LEVEL AND NOTHING FINER (docs/5 § D260731-0207). Long enough for the
   *  longest real one plus a country, since people write "Doha, Qatar". */
  CITY_MAX_CHARS: 80,
  /**
   * A bio, and it is deliberately a remark rather than a paragraph.
   *
   * Shorter than POST_MAX_CHARS: a card is one line about a person and the
   * timeline under it is what they actually think. A bio long enough to be an
   * essay is a bio people write a personal ad in, which is the dating panel
   * docs/5 § D260803-1552 took off the card, retyped as free text.
   */
  BIO_MAX_CHARS: 280,
  /**
   * The name somebody gives their bird (docs/1 § Your pigeon). Short, because it
   * is drawn on a nameplate under a 44px bird inside a fixed 80px cell on the
   * card — a longer one truncates rather than widening the pill, so anything
   * past this is a name its owner never sees in full.
   */
  BIRD_NAME_MAX_CHARS: 24,
  /**
   * How long somebody must wait between handle changes (docs/5 § D260805-0242).
   *
   * FRICTION, NOT PROTECTION, and the distinction is the whole of what this number
   * is for. Giving up a handle RELEASES it — immediately, to anybody, with no
   * redirect left behind — so this cool-down protects nothing about the old name.
   * What it protects is the new one: a handle is how the people you write to
   * remember who you are, and it should not be able to change twice in an
   * afternoon.
   *
   * FOURTEEN BECAUSE IT IS THE NEAREST THING TO A NORM. X rate-limits changes
   * without documenting a figure; Instagram's own window around a username change
   * is two weeks. Long enough that a name is a name, short enough that somebody
   * who mistyped theirs at signup is not stuck with it for a month.
   */
  HANDLE_CHANGE_COOLDOWN_DAYS: 14,
  /**
   * The shortest password the product accepts.
   *
   * TWELVE RATHER THAN EIGHT, and length is the only rule: no character classes,
   * no forced symbol, no expiry. NIST 800-63B has said since 2017 that composition
   * rules push people toward `Password1!` and back toward reuse, and that length is
   * what actually buys entropy — so the one number here is the long one.
   *
   * IT IS SHARED BECAUSE THE FORM HAS TO SAY IT. The rule lived only in Better
   * Auth's config, which meant a signup form could refuse a password AFTER it was
   * typed and submitted, and could only paraphrase why. A minimum somebody can see
   * before they choose is the difference between a rule and an ambush.
   */
  MIN_PASSWORD_CHARS: 12,
  /**
   * The age gate (docs/8 § A), enforced on the date of birth at onboarding and
   * never rendered back as an age.
   *
   * CLIENT AND SERVER BOTH CHECK IT, which is not a duplicated rule but a
   * duplicated *reading* of one number: the client sets the date field's `max`
   * from it so the picker cannot produce a refusable date, and the server
   * refuses one anyway because a client is not a gate.
   */
  MINIMUM_AGE_YEARS: 18,
} as const;

/**
 * The shape of a handle: `^[a-z0-9_]{3,20}$`, built from the two lengths above
 * so the pattern and the numbers cannot disagree.
 *
 * LOWERCASE ONLY, rather than case-preserving-but-case-insensitive. The second
 * design needs a fold for comparison and a display copy for rendering, and
 * every place that forgets the fold is a duplicate-handle bug. One case means
 * the column is the comparison.
 *
 * SHARED, BECAUSE THE FORM AND THE SCHEMA HAD SEPARATE COPIES. The client needs
 * it to say "not that" while somebody is still typing; the server needs it
 * because a client is not a validator. What it deliberately does NOT carry is
 * the reserved-name list — that stays on the server and is answered by
 * `GET /me/handle`, so there is one authority on whether a name may be had.
 */
export const HANDLE_PATTERN = new RegExp(
  `^[a-z0-9_]{${PROFILE_CARD.HANDLE_MIN_CHARS},${PROFILE_CARD.HANDLE_MAX_CHARS}}$`,
);

/**
 * The two refusals both tiers have to word identically, and the only user-facing
 * copy in this file.
 *
 * COPY DOES NOT NORMALLY LIVE HERE — a server message belongs to the server and
 * the client prints what it is sent (see `ApiError`). These two are the exception
 * because the client refuses first, before any request: the form says "not that"
 * while somebody is still typing, which is the whole reason it is a good form. Two
 * independently written versions of one rule is how a field ends up saying
 * "3–20 characters" while the schema enforces something else.
 *
 * THE HANDLE SENTENCE IS BUILT FROM THE PARAMETERS rather than typed out, so the
 * numbers in the prose cannot drift from the numbers in the pattern — which is the
 * doc set's first rule (docs/parameters.md) applied to an error message.
 */
export const HANDLE_SHAPE_MESSAGE =
  `Use ${PROFILE_CARD.HANDLE_MIN_CHARS}–${PROFILE_CARD.HANDLE_MAX_CHARS} characters: ` +
  'lowercase letters, numbers and underscores.';

/** The 18+ gate's refusal (docs/8 § A). One clause, and it does not argue. */
export const ADULTS_ONLY_MESSAGE = 'Dove is for adults.';

/* ---------------------------------------------------------------------------
 * Naming somebody inside a letter
 * ------------------------------------------------------------------------ */

/**
 * The numbers behind `@` (docs/5 § D260809-0021).
 *
 * THE WHOLE FEATURE IS BOUNDED BY THESE FOUR, and that is deliberate: the
 * picker does no work on the network, so the only costs it can run up are the
 * size of the list it holds and the length of the pass it makes over it. Both
 * are named here rather than typed into a component, because a limit the client
 * counts against and the server slices at has to be one number.
 */
export const MENTIONS = {
  /**
   * How many people the picker holds, and therefore how big one response is.
   *
   * IT IS A CEILING ON THE WIRE, NOT ON THE GRAPH. Your own graph can be
   * larger; what this bounds is how much of it travels to a device and gets
   * scanned on every keystroke. At `MentionCandidate`'s four short fields this
   * is tens of kilobytes at the very top end and a fraction of that for anybody
   * real — one response, cached, against a request per character.
   *
   * THE SLICE IS NOT ARBITRARY WHERE IT BITES: the server orders by closeness
   * before it cuts, so what a person loses at the boundary is the follower they
   * have never written to, never the correspondent.
   */
  CANDIDATES_MAX: 400,
  /**
   * How many names the menu offers at once.
   *
   * FIVE, BECAUSE THE MENU SITS OVER A KEYBOARD. It is drawn above the writing
   * on a phone, so every extra row is a line of the letter hidden behind it, and
   * a list somebody scrolls is a list they read instead of glance at. If the
   * right person is not in five, the next character narrows it — which is faster
   * than a scroll, and making that true is what the ranking is for.
   */
  MENU_MAX: 5,
  /**
   * How many people one letter may name.
   *
   * IT IS AN ANTI-BLAST RULE, and it is why this number is small. Naming
   * somebody sends them a notification, so an unbounded list is a broadcast
   * primitive: one letter, four hundred interruptions, and no ration spent past
   * the first. Six is more people than a sentence needs and far fewer than a
   * mailing list wants.
   *
   * IT ALSO KEEPS THE LOOKUP INSIDE D1's 100-PARAMETER CEILING. Resolving the
   * names a letter carries is one `IN (...)`, and this is what bounds it.
   * Anything past the sixth name stays text: it still reads, it does not notify.
   */
  PER_LETTER_MAX: 6,
  /**
   * The shortest thing that opens the menu, counted after the `@`.
   *
   * ZERO, WHICH IS A CHOICE AND NOT AN OVERSIGHT. Typing `@` alone offers the
   * people you write to most, which is the point of a bounded list: with no
   * search behind it, answering "who might you mean" from the first character
   * costs nothing, and the one-tap case is the common one.
   */
  MIN_QUERY_CHARS: 0,
} as const;

/**
 * Every handle a piece of writing names, in order, deduplicated.
 *
 * ONE PARSER, SHARED, BECAUSE THREE TIERS READ THE SAME STRING. The composer
 * marks what it found, the renderer decides what to draw as a link, and the
 * server decides who to notify. Three copies of this rule is three chances for
 * a letter to link a name that never got a notification, or to notify somebody
 * whose name was not drawn as a link.
 *
 * WHAT COUNTS. An `@` at the start of the text or after a character that is not
 * a letter, digit or underscore, then a handle, then something that is not one
 * of those characters. The preceding rule is what stops an email address from
 * naming anybody: `sara@example.com` has a letter before the `@`. The following
 * rule is what stops a 21-character run from naming the first twenty. Trailing
 * punctuation is outside the handle pattern already, so "ask @yasmin." names
 * `yasmin`.
 *
 * IT IS DELIBERATELY NOT ASKED WHETHER ANYBODY HOLDS THE NAME. This is the
 * syntactic half; whether a handle resolves is a question for whoever has the
 * table, and docs/5 § D260809-0021 says why the two halves stay apart.
 */
export function parseMentions(body: string): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  for (const span of splitMentions(body)) {
    if (!span.handle || seen.has(span.handle)) continue;
    seen.add(span.handle);
    found.push(span.handle);
  }
  return found;
}

/**
 * One run of a body: prose, or a name with the `@` still attached to it.
 *
 * THE `text` OF A NAMED SPAN INCLUDES THE `@` so a renderer can print the span
 * and nothing else. A component that rebuilds the sigil is a component that can
 * forget to.
 */
export type MentionSpan = { text: string; handle?: string };

/**
 * A body split into prose and the names inside it, for rendering.
 *
 * THE RENDERER MUST NOT RUN THE PATTERN ITSELF. A component that re-derives
 * where the mentions are is a second parser, and what it produces when it drifts
 * is silent: text that links where nothing was notified. `parseMentions` reads
 * this function rather than the other way round, so there is exactly one place
 * that decides what an `@` means.
 */
export function splitMentions(body: string): MentionSpan[] {
  const out: MentionSpan[] = [];
  const re = new RegExp(
    `(^|[^a-z0-9_])@([a-z0-9_]{${PROFILE_CARD.HANDLE_MIN_CHARS},${PROFILE_CARD.HANDLE_MAX_CHARS}})(?![a-z0-9_])`,
    'gi',
  );
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body))) {
    const at = m.index + (m[1]?.length ?? 0);
    const end = m.index + m[0].length;
    if (at > cursor) out.push({ text: body.slice(cursor, at) });
    out.push({ text: body.slice(at, end), handle: (m[2] ?? '').toLowerCase() });
    cursor = end;
  }
  if (cursor < body.length) out.push({ text: body.slice(cursor) });
  return out;
}

/**
 * Somebody the picker may offer, and the smallest type that draws a row.
 *
 * IT IS DELIBERATELY NOT A `PublicAuthor`. That type carries `verified`, which
 * is a safety signal for a person deciding whether to write to a stranger — and
 * every row in this list is, by construction, not a stranger, so the mark has
 * nothing to say here. What is carried instead is `closeness`, which exists to
 * sort and never to render: no row reads "correspondent", because the standing
 * of a pair is not a badge on a menu.
 *
 * FOUR SHORT FIELDS TIMES `MENTIONS.CANDIDATES_MAX` IS THE WHOLE COST OF THIS
 * FEATURE ON THE WIRE. Anything added here is paid once per person per refresh,
 * so the bar for a fifth field is that a row cannot be drawn without it.
 *
 * WHICH IS WHY THERE IS NO BIRD ON IT. A byline carries `bird`, and the picker
 * deliberately does not: a menu row is a photograph or a monogram, exactly like
 * every other list of people in the product, and five bird portraits over a
 * keyboard would be five WebGL captures to answer "which Sarah".
 */
export interface MentionCandidate {
  handle: string;
  name: string;
  /** Their photograph, as a `media` id — see `PublicAuthor.avatarMediaId`. */
  avatarMediaId?: string;
  /**
   * How close this pair is, as a sort key. Higher is closer:
   * `CLOSENESS.CORRESPONDENT` down to `CLOSENESS.FOLLOWER`. It never becomes a
   * label, a badge or a count.
   */
  closeness: number;
}

/**
 * The order the picker prefers people in, and the only meaning `closeness` has.
 *
 * IT RANKS ONE MENU AND NOTHING ELSE. No surface sorts by it and no ranking path
 * reads it. It is not a score about a person: it answers "which of the two
 * Sarahs did you mean", and the honest answer is the one you write to.
 */
export const CLOSENESS = {
  /** You already exchange letters. The strongest signal in the product. */
  CORRESPONDENT: 3,
  /** You follow each other. */
  MUTUAL: 2,
  /** You follow them. */
  FOLLOWING: 1,
  /** They follow you. */
  FOLLOWER: 0,
} as const;
/* --------------------------------------------------------------------------
 * Where somebody is
 * ----------------------------------------------------------------------- */

/**
 * A PLACE AS THIS PRODUCT KNOWS ONE: a city, the region around it, and a country
 * code. Nothing finer exists in any of the three shapes it arrives in — the edge's
 * reading of an IP address, a reverse geocode of a coarse fix, or the string already
 * on a card (docs/5 § D260809-1521).
 *
 * NO COORDINATES, AND THERE IS NOWHERE TO PUT ANY. The type is the boundary: a
 * latitude that reached a card would be a latitude that reached everybody who can
 * read one, and banded distance exists precisely so that never happens (docs/8 § C).
 * A fix is rounded and spent inside the one function that asks for it, and what
 * comes back out is this.
 */
export interface Place {
  city: string;
  /** The state, province or governorate, where the source names one. */
  region?: string;
  /** ISO 3166-1 alpha-2, upper case — `'QA'`, not `'Qatar'`. */
  countryCode?: string;
}

export const PLACE = {
  /**
   * How much of a device's fix survives before it is sent anywhere, in decimal
   * places of latitude and longitude.
   *
   * TWO IS ABOUT 1.1 KM, which is a district rather than a doorstep, and it is
   * chosen against the thing being looked up rather than for a round number: a
   * city-level reverse geocode returns the same city anywhere inside a kilometre,
   * so the third decimal buys nothing and discloses a street. Rounding happens on
   * the DEVICE, before the request leaves it — the precise fix never becomes
   * somebody else's log line, ours included.
   */
  COORD_DECIMALS: 2,
  /**
   * How long the device is given to produce a fix, in milliseconds.
   *
   * A COLD FIX IS SLOW AND THIS IS NOT A BLOCKING FIELD: the city is optional on
   * the card, so the honest failure is to stop asking and let somebody finish the
   * form. Long enough for a phone that has to wake its radio, short enough that
   * nobody wonders whether the button worked.
   */
  FIX_TIMEOUT_MS: 8_000,
  /**
   * How old a cached fix may be and still be used, in milliseconds.
   *
   * THE POINT OF THE FIELD IS A CITY, and a city is where the device was five
   * minutes ago as surely as it is where the device is now. Accepting a warm fix
   * is what makes the second tap instant and what keeps the GPS off.
   */
  FIX_MAX_AGE_MS: 300_000,
  /**
   * How long the lookup itself is given, in milliseconds.
   *
   * Shorter than the fix above, because by this point there is nothing to wait for
   * but one small HTTP response over a connection the device has already proved.
   */
  LOOKUP_TIMEOUT_MS: 5_000,
  /**
   * The countries whose region is written as part of a place name.
   *
   * A DISPLAY CONVENTION, NOT A DATA RULE, and the reason it is a list is that no
   * property of the data distinguishes the two cases. "Austin, United States" is
   * missing the word every American would say, and "Doha, Ad Dawhah, Qatar" is a
   * governorate nobody says out loud — but the lookup returns a `principalSubdivision`
   * for both, and the two strings are indistinguishable to a rule about emptiness or
   * duplication. So the countries where a subdivision is customarily part of the
   * address are named here, and everywhere else the place is the city and the
   * country, which is what `CITY_MAX_CHARS` was sized for.
   *
   * WRONG IN BOTH DIRECTIONS IS CHEAP: a missing region reads as a slightly plainer
   * city, and an extra one reads as a slightly longer one. Add a country when
   * somebody from it says the string looks wrong.
   */
  REGION_COUNTRIES: ['US', 'CA', 'AU', 'BR', 'IN', 'MX'],
} as const;

/**
 * A place as one line on a card — `'Austin, Texas, United States'`, `'Doha, Qatar'`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SHARED BECAUSE BOTH TIERS FORMAT ONE. The client formats what a device found so
 * the person sees the string before they save it; the server formats what the edge
 * read off their connection, to suggest at signup. Two spellings of this would put
 * a different address on the same city depending on which of the two produced it —
 * and since the result is STORED rather than recomputed, the disagreement would be
 * permanent and per-row.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THE COUNTRY IS A NAME AND ARRIVES AS A CODE. `Intl.DisplayNames` is the whole of
 * that translation, in both runtimes, so there is no country table to maintain and
 * no country table to get out of date. English, deliberately: the string is written
 * once into a column and then read by everybody, so it cannot be in the writer's
 * language without being in the wrong language for every reader.
 *
 * PARTS THAT REPEAT EACH OTHER COLLAPSE. Sources disagree about city-states —
 * `'Singapore'` arrives as city, region and country — and three of a word is the
 * kind of thing that makes a real feature look broken.
 *
 * IT CANNOT EXCEED `CITY_MAX_CHARS`, because it is written to a column of that width
 * by a request the server validates. What it sheds to get there is ordered by what
 * the reader needs least: the region, then the country, and only the city itself is
 * ever cut — a place ending in "…, United S" reads as a bug, and a long city reads as
 * a long city.
 */
export function formatPlace(place: Place): string {
  const country = countryName(place.countryCode);
  const city = place.city.trim();
  if (!city) return '';

  const regionCountries: readonly string[] = PLACE.REGION_COUNTRIES;
  const region = regionCountries.includes((place.countryCode ?? '').toUpperCase())
    ? (place.region ?? '').trim()
    : '';

  /*
   * THREE ATTEMPTS, EACH SHEDDING THE PART THE READER NEEDS LEAST, and each one
   * REBUILT rather than filtered. Filtering a part out by value deletes the CITY
   * whenever the two strings are equal — the collapse in `line` keeps the first of a
   * repeated pair, so the survivor of `city === region` is the city, and
   * `part !== region` would then remove it. A card reading "United States" was one
   * line away.
   *
   * THE COUNTRY GOES TOO RATHER THAN BEING CUT IN HALF. A place ending in
   * "…, United S" reads as a broken field; the city on its own reads as a long name,
   * which is what it is. Only the city itself is ever truncated, and only when it
   * alone is longer than the column.
   */
  const full = line(city, region, country);
  if (full.length <= PROFILE_CARD.CITY_MAX_CHARS) return full;

  const terse = line(city, '', country);
  if (terse.length <= PROFILE_CARD.CITY_MAX_CHARS) return terse;

  /* A cut can land on a separator, and ", " at the end of a place reads as a
     truncation bug rather than as a long name. */
  return city.slice(0, PROFILE_CARD.CITY_MAX_CHARS).replace(/[\s,]+$/, '');
}

/** The parts as one line: empties dropped, repeats collapsed, first one wins. */
function line(...parts: string[]): string {
  const kept: string[] = [];
  for (const part of parts) {
    if (!part) continue;
    /* Case-folded, so `'washington'` beside `'Washington'` is still one word. */
    if (kept.some((seen) => seen.toLowerCase() === part.toLowerCase())) continue;
    kept.push(part);
  }
  return kept.join(', ');
}

/**
 * `'QA'` → `'Qatar'`, and the code itself when that cannot be done.
 *
 * THE FALLBACK IS THE CODE RATHER THAN NOTHING, because a card reading
 * `'Austin, US'` is a card that is merely terse, whereas one reading `'Austin'`
 * has quietly lost a fact. `Intl.DisplayNames` needs ICU data that a stripped
 * runtime can lack, and it throws on a malformed code — an edge reading of a
 * connection through Tor is `'T1'`, and an unknown one is `'XX'`.
 */
function countryName(code: string | undefined): string {
  if (!code) return '';
  const upper = code.trim().toUpperCase();
  if (NOT_A_COUNTRY.has(upper)) return '';
  /* Two letters or nothing. `Intl.DisplayNames` THROWS on anything outside the
     region-subtag grammar rather than falling back, so the shape is checked here
     instead of being discovered in a catch. */
  if (!/^[A-Z]{2}$/.test(upper)) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(upper) ?? upper;
  } catch {
    return upper;
  }
}

/**
 * Codes that arrive in a country slot and are not countries.
 *
 * ALL THREE ARE REAL VALUES CLOUDFLARE REPORTS, and `ZZ` is the one that has to be
 * listed rather than reasoned about: it is a legitimate CLDR subtag whose English
 * name is the literal string **"Unknown Region"**, so without this the honest answer
 * "we could not tell" would render on somebody's card as *Springfield, Unknown
 * Region*. `XX` is Cloudflare's own version of the same non-answer, and `T1` is a
 * connection arriving over Tor.
 */
const NOT_A_COUNTRY = new Set(['XX', 'ZZ', 'T1']);

/**
 * EVERY BIRD HAS A NAME (docs/5 § D260805-0242). Nobody's companion is nameless, and
 * nothing anywhere renders an unnamed one.
 *
 * A DEFAULT RATHER THAN A PROMPT, and that is the whole design. The alternative
 * was a required field in onboarding — one more thing to answer at minute two,
 * about an object the person has not met yet, for a name most people would type
 * something placeholder into anyway. So the bird arrives already called
 * something, and renaming it is an edit rather than a task.
 *
 * DERIVED, NOT STORED. `defaultBirdName` is a pure function of the account's own
 * id, so it is stable for the life of the account, identical on the server and
 * the client, and it needs no column, no backfill and no migration for the
 * accounts that already exist. The column holds a name only once somebody has
 * chosen one — which means clearing the field returns the bird to its default
 * rather than to nothing.
 *
 * PLAIN, SHORT, AND NOT CUTE. These are read on a nameplate under a 44px bird
 * beside somebody's real name; a joke would be a joke on the card of every
 * person who never opened the wardrobe.
 */
export const DEFAULT_BIRD_NAMES = [
  'Pip', 'Wren', 'Sable', 'Juno', 'Ash', 'Nour', 'Kite', 'Marlow',
  'Fen', 'Sol', 'Bea', 'Corvo', 'Dusty', 'Sadie', 'Rafi', 'Olive',
  'Perch', 'Quill', 'Sparrow', 'Nib', 'Cloud', 'Bramble', 'Tolu', 'Zaid',
] as const;

/**
 * The name a bird has before anybody renames it.
 *
 * Seeded from the account id with a fixed-width string hash rather than
 * `Math.random`, because the same account must get the same bird's name on every
 * read, in every process, forever — a companion whose name changes when the
 * server restarts is not a companion.
 */
export function defaultBirdName(accountId: string): string {
  let hash = 0;
  for (let i = 0; i < accountId.length; i += 1) {
    /* djb2, kept in 32 bits. The values only have to be well spread across 24
       buckets, so this is deliberately the cheapest thing that is. */
    hash = (hash * 33 + accountId.charCodeAt(i)) | 0;
  }
  return DEFAULT_BIRD_NAMES[Math.abs(hash) % DEFAULT_BIRD_NAMES.length]!;
}

/**
 * The plumages, by id (docs/5 § D260805-0318).
 *
 * HERE RATHER THAN BESIDE THE COLOURS, because two tiers need the id set and only
 * one of them can see a hex value. `pigeon-plumage.ts` in the client owns what each
 * one *looks like* — eight colours per entry, consumed by an SVG and a 3D palette —
 * and none of that is a fact the server has any use for. What the server needs is
 * the closed set, so that `PATCH /me` can refuse a plumage that does not exist
 * instead of storing a string the whole app then has to render defensively.
 *
 * A GROWING SET, AND THE ONE PLACE IT GROWS. Adding a plumage is: one entry here,
 * one entry in the client's colour table (the type makes forgetting it a build
 * error), `npm run docs:params`. No migration, no backfill, and deliberately no
 * `CHECK` constraint on the column — see `user.plumage` in the API's schema for
 * why that one is the exception to the rule that closed sets are constrained.
 *
 * ORDER IS WHAT THE CLOSET SHOWS FIRST, AND IT IS NEVER A RANKING
 * (docs/5 § D260807-2304). It used to say "authored and meaningless", which stopped
 * being true the moment the pastels were deliberately put at the front: order is
 * now a real editorial choice about what a person sees before they scroll. The
 * prohibition it was protecting is unchanged and is the part that matters — **no
 * rarity, no tier, no drops, nothing scarcer than anything else**
 * (docs/1 § Anti-status). Every entry is free from day one and reachable in one
 * scroll, so being third or twenty-third costs nothing and earns nothing. The
 * moment position implies scarcity, value or achievement, this is a ranking and
 * the rule is broken.
 *
 * TWO GROUPS DO NOT IDENTIFY THEMSELVES BY THEIR THROAT: the pastels at the top
 * and the monochromes at the end (docs/5 § D260807-0312, § D260807-2304). Both are
 * told apart by BODY. That is a fact about the colours, which live in the client —
 * recorded here only so that a new *chromatic* plumage is not authored to sit
 * between two of them by mistake.
 */
export const PLUMAGE_IDS = [
  /* The pastels lead. See the note above: first is not best, it is just first. */
  'blush', 'peach', 'rose', 'lilac',
  'slate', 'ash', 'ink', 'fawn', 'snow', 'indigo', 'copper', 'dove',
  'moss', 'sand', 'rust', 'pewter', 'plum', 'fern', 'storm', 'clay',
  'onyx', 'chalk', 'umber', 'amber',
] as const;

export type PlumageId = (typeof PLUMAGE_IDS)[number];

/**
 * The plumage this build falls back to.
 *
 * NOT WHAT A NEW ACCOUNT WEARS (docs/5 § D260806-1425) — onboarding assigns one from
 * the account's own id. What follows is why THIS value is the fallback.
 *
 * ONE CONSTANT, BECAUSE THERE WERE THREE. The column defaulted to `indigo`, the
 * fixtures said `indigo`, and the client's fallback for an unrecognised id was
 * `PLUMAGES[0]` — `slate`. So a bird whose plumage failed to load did not look
 * like a new bird, it looked like a *different* bird, which is the one thing this
 * value has to get right.
 */
export const DEFAULT_PLUMAGE_ID: PlumageId = 'indigo';

/** Whether a string is a plumage. The server's guard, and the client's. */
export function isPlumageId(value: unknown): value is PlumageId {
  return (PLUMAGE_IDS as readonly unknown[]).includes(value);
}

/**
 * Which bird stands on the sill, by id (docs/5 § D260805-1237).
 *
 * SEPARATE FROM PLUMAGE BECAUSE THEY ARE DIFFERENT KINDS OF CHOICE. A plumage is
 * a recolour: sixty-four pixels, applied to whichever bird is loaded, and it
 * travels onto every post beside a person's name. A species is a different mesh,
 * a different atlas and a different set of measured proportions — so switching it
 * reloads a model rather than repainting one, and it is only ever seen on your own
 * sill. Both are stored per account for the same reason: a choice that resets when
 * you open the app on a phone instead of a laptop is not a choice, it is a
 * preference the app keeps losing.
 *
 * A SPECIES IS ONE DESCRIPTOR, NOT A BRANCH. `three/species/` holds one file per
 * bird carrying every number measured off that asset, and the client's `SPECIES`
 * table is keyed by this type — so adding an id here without adding a descriptor
 * is a build error, exactly as it is for a plumage without colours.
 *
 * ORDER IS AUTHORED AND MEANINGLESS, and carries no rarity, for all the reasons
 * set out above `PLUMAGE_IDS`. Both birds are free from day one; the pigeon is a
 * reproportioned derivative of the sparrow rather than a second purchase, so
 * there was never a cost to gate it behind.
 */
export const SPECIES_IDS = ['sparrow', 'pigeon'] as const;

export type SpeciesId = (typeof SPECIES_IDS)[number];

/**
 * The bird an account has before anybody opens the closet.
 *
 * THE SPARROW, WHICH IS THE BIRD THAT WAS ALREADY THERE. Every existing account
 * has been watching one since docs/5 § D260804-1313, and a new default would have
 * silently swapped the bird on every sill that had never made a choice — the same
 * failure `DEFAULT_PLUMAGE_ID` exists to prevent, one tier up. The pigeon is an
 * alternative, not a replacement. *
 * IT IS NO LONGER WHAT A NEW ACCOUNT WEARS (docs/5 § D260806-1425). Onboarding
 * assigns a bird from the account's own id, so this is now a FALLBACK and a column
 * default rather than a starting point: it is what a build draws for a value it does
 * not recognise, and what a migration puts in a new column. The argument below is
 * kept because it is still the reason THIS value is the sparrow rather than
 * something else.
 */
export const DEFAULT_SPECIES_ID: SpeciesId = 'sparrow';

/** Whether a string names a bird. The server's guard, and the client's. */
export function isSpeciesId(value: unknown): value is SpeciesId {
  return (SPECIES_IDS as readonly unknown[]).includes(value);
}

/**
 * The posture the bird is caught in, by id (docs/5 § D260806-1118).
 *
 * WHAT A POSE IS. The bird beside a person's name is a still photograph of their
 * own model, and this is which moment of it. Both birds carry the same eighteen
 * authored clips, so a pose is a clip plus the phase of it that reads as a
 * picture — the phase is a MEASUREMENT and lives on the client's species
 * descriptor beside every other number measured off the asset. This tier stores
 * the id and knows nothing about clips, exactly as it stores a plumage id and
 * knows nothing about hex values.
 *
 * WHY THESE EIGHT AND NOT THE EIGHTEEN THERE ARE. Two different kinds of reason,
 * and they are worth keeping apart.
 *
 * **A product rule, which needs no looking at.** `Attack`, `Death`, `Fear` and
 * `Hit` are in both GLBs and none of them may ever appear here: docs/1 P1 says an
 * empty sill renders as an empty sill and never as a sad, dirty or neglected
 * animal, and a closet offering *your bird, frightened* as a thing to wear beside
 * your name on every letter breaks that at the root.
 *
 * **A judgement about whether a clip has a picture in it, which was made in front
 * of the pictures** (`bird-portrait-review.html`, `?strip=`). `Eat` is the sill's
 * peck and holds as a bird face-down on stone. `Run` is `Walk` with a shorter
 * contact and is indistinguishable at 44px. `Swim` has no legible instant. `Roll`
 * was in this set as `tumbling` until it was drawn at 120px, where a bird halfway
 * through a 360° tumble turns out to be an unidentifiable egg with feet — the
 * clearest case there has been for building the review page before choosing the
 * set. `Bounce` was the runner-up and is genuinely good; there is no rule against
 * a ninth pose, and it stays out only because eight fills the drawer's grid.
 *
 * ORDER IS AUTHORED AND MEANINGLESS, and carries no rarity — see `PLUMAGE_IDS`
 * for the full argument. Every pose is free from day one; a pose is a camera
 * shutter, and there is nothing to gate.
 */
export const BIRD_POSE_IDS = [
  'rest',
  'glance',
  'greeting',
  'walking',
  'hopping',
  'flying',
  'dancing',
  'sitting',
] as const;

export type BirdPoseId = (typeof BIRD_POSE_IDS)[number];

/**
 * The posture this build falls back to.
 *
 * NOT WHAT A NEW ACCOUNT WEARS (docs/5 § D260806-1425) — onboarding assigns a
 * posture from the account's own id. This is the value substituted for a pose id
 * this build does not recognise, and the column default.
 *
 * `rest` is `Idle_A` at phase zero — the breath the sill has always dropped the
 * bird into, and a settled bird standing square is the one posture that reads at
 * every size a portrait is drawn at, which is what makes it the safe substitute.
 */
export const DEFAULT_BIRD_POSE_ID: BirdPoseId = 'rest';

/** Whether a string names a posture. The server's guard, and the client's. */
export function isBirdPoseId(value: unknown): value is BirdPoseId {
  return (BIRD_POSE_IDS as readonly unknown[]).includes(value);
}

/**
 * A bird nobody has chosen yet — a species, a plumage and a posture, assigned
 * from the account's own id (docs/5 § D260806-1425).
 *
 * WHY AN ACCOUNT IS NOT HANDED THE SAME BIRD AS EVERYBODY ELSE. Until this
 * existed, every one of the three columns had a single default, so **every
 * account that had never opened the closet was the same picture** — one indigo
 * sparrow at rest, beside every name in the product. That is a worse failure now
 * than it was before the portrait shipped: the bird is the one piece of a person
 * they chose (docs/1 § Your pigeon), and a feed where it is identical on every row
 * says the opposite — that it is furniture. It also collapsed the whole point of
 * addressing a portrait by its appearance, since one address served everyone.
 *
 * ASSIGNED, NOT RANDOM, AND THE DIFFERENCE MATTERS TWICE. `defaultBirdName`
 * directly above makes the argument for a bird's *name* and it holds for the rest
 * of it: the same account must get the same bird on every read, in every process.
 * Here it also makes the backfill **idempotent** — `assign-appearances.ts` can be
 * run twice, or interrupted halfway, without reshuffling anybody, which is not
 * true of `random()` in a migration.
 *
 * IT IS AN ASSIGNMENT AND NEVER A CHOICE, which is why it is applied ONCE and
 * stored rather than derived on every read like a bird's name is. A person who
 * opens the closet and picks is overwriting an assignment, and nothing may ever
 * put it back — so this is called at the moment an account starts existing
 * (`POST /me/onboarding`) and by the one-off backfill, and nowhere else.
 *
 * NO RARITY, AND THERE IS NOTHING HERE TO WANT. Every species, plumage and pose
 * is free from day one and none is scarcer than another (docs/1 § Anti-status), so
 * an assignment cannot be lucky or unlucky. That is what makes handing one out
 * without asking acceptable at all — the same move on a set with a rare tail would
 * be a loot box.
 */
export function assignedAppearance(accountId: string): {
  species: SpeciesId;
  plumage: PlumageId;
  pose: BirdPoseId;
} {
  return {
    species: pick(SPECIES_IDS, 'species', accountId),
    plumage: pick(PLUMAGE_IDS, 'plumage', accountId),
    pose: pick(BIRD_POSE_IDS, 'pose', accountId),
  };
}

/**
 * One entry from a closed set, chosen by hashing the id under a per-dimension
 * salt.
 *
 * THE AVALANCHE STEP IS NOT OPTIONAL, AND LEAVING IT OUT IS NOT A SUBTLE BUG — it
 * costs 114 of the 128 available birds. The first version of this was djb2 with a
 * salted seed, exactly like `defaultBirdName`, and it measured **perfectly uniform
 * on every dimension separately and catastrophically correlated across them**: 14
 * distinct appearances out of 128, with plumage and pose locked to each other.
 *
 * The arithmetic, because it is worth being able to spot again. djb2 is
 * `h = h * 33 + c`, and **33 ≡ 1 (mod 8)** — so multiplying by it never carries
 * anything into the low three bits, which is precisely what `% 8` reads. Two
 * different salts therefore leave the low bits differing by a *constant*, and two
 * dimensions taken mod 8 become fixed rotations of one another. Salting harder,
 * or salting at the front rather than the back, does nothing about this: no
 * arrangement of the input fixes a multiplier that cannot reach the bits being
 * used.
 *
 * So the accumulated hash is finished with murmur3's `fmix32`, which is a
 * published avalanche function: every output bit depends on every input bit, and
 * `% n` on any n is then honest. Re-measured after the change — 128 of 128
 * appearances, chi² about what uniformity predicts on each axis and jointly.
 * `spread.mjs` in the scratch dir is the instrument; the assertion lives in
 * `test/bird-assignment.test.ts` so it cannot regress silently.
 *
 * `defaultBirdName` above has the same weakness and is left alone deliberately: it
 * picks ONE value from ONE set, so there is no second dimension for it to
 * correlate with, and changing it would rename every unnamed bird in the product.
 */
function pick<T extends readonly string[]>(
  set: T,
  salt: string,
  accountId: string,
): T[number] {
  const seed = `${salt}:${accountId}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 33 + seed.charCodeAt(i)) | 0;
  }
  /* fmix32, from murmur3. `Math.imul` because a 32-bit multiply overflows a
     double and `*` would silently lose the low bits this depends on. */
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  /* `>>> 0` rather than `Math.abs`: abs of -2147483648 is not representable as a
     positive int32 and comes back negative, which would index off the front of
     the set. An unsigned shift has no such value. */
  return set[(hash >>> 0) % set.length]!;
}

/**
 * The portrait: one still of somebody's own bird, captured on their device
 * (docs/5 § D260806-1118).
 *
 * These numbers are the RENDERING CONTRACT, and that is a stronger claim than
 * "tunable". A portrait is content-addressed by `birdCode`, and one device's
 * capture is served to every other person who sees that bird — so two devices
 * disagreeing about the frame is not a small visual difference, it is two
 * different pictures behind one address. Everything that decides what the
 * capture looks like is therefore here, in the one package both tiers read, and
 * `EPOCH` below is what makes changing any of it safe.
 */
export const BIRD_PORTRAIT = {
  /**
   * Bumped whenever a change would alter the pixels: the frame, the camera, the
   * lights, the size, the encoding.
   *
   * IT IS THE FIRST SEGMENT OF EVERY CODE, so bumping it rotates every cache key
   * in one move — every stored render is orphaned rather than stale, every device
   * re-captures once, and nothing has to be found and purged. Without it a
   * renderer change would leave the old pixels sitting at the address the new
   * code computes, and the bug would be invisible to whoever shipped it: their
   * own device re-renders, and only somebody else's cache hit shows the old bird.
   */
  EPOCH: 1,

  /**
   * The captured square, in pixels.
   *
   * ONE SIZE FOR EVERY SURFACE, and not a device-pixel-ratio multiple of any of
   * them. The portrait is drawn at 22px in a notification row and at 96px on a
   * card, so a per-surface size would fragment the cache by where the bird
   * happened to be first seen. 256 covers the largest of those at 3x and costs
   * about 10 KB as a PNG of a flat-shaded bird on transparency.
   */
  PX: 256,

  /**
   * The height of the orthographic frame, in bird-heights.
   *
   * FIXED ACROSS EVERY POSE AND EVERY SPECIES, which is the whole reason it is a
   * constant rather than a fit to each pose's bounding box. Framing each capture
   * to its own contents makes the bird change size when you change its posture
   * and makes a sparrow and a pigeon come out the same height, which is the one
   * thing a set of portraits must not do — `bird-review.ts` records the same
   * mistake being made with a camera orbit and eight birds at eight sizes. The
   * MEASURED, NOT GUESSED, and the first guess was wrong twice. Every pose's reach
   * from this frame's own centre is printed by `bird-portrait-review.html`, which
   * fails if any of them overflows AND fails if the largest fills less than 80% —
   * a frame with room to spare is a bird drawn small in a mostly empty PNG, which
   * is the other way to get this wrong. The binding pose is `hopping`, at 95%; the
   * settled bird everybody starts with fills 77%.
   */
  FRAME_UNITS: 1.45,

  /**
   * Where the frame's centre sits, in bird-heights above the feet.
   *
   * Above the middle, because a bird's mass is low and its head is what a
   * portrait is of — the same composition `review-harness.ts` aims at, arrived at
   * there by pointing the camera at the eye line rather than the mass centre.
   */
  EYE_LEVEL: 0.56,

  /**
   * Camera azimuth and elevation, in degrees.
   *
   * THREE-QUARTER, NOT SIDE-ON, and that is the point of replacing a flat
   * drawing with a render: a profile view of a model is a picture a good SVG
   * could have been. `bird-review.ts` composes the plumage row at the same
   * azimuth, so a plumage signed off there is signed off on this camera.
   */
  AZIMUTH: 35,
  ELEVATION: 8,

  /**
   * The ceiling on a portrait the server will store.
   *
   * A cap rather than a re-encode, for the reason `media.ts` gives: a Worker
   * cannot decode an image inside its CPU budget. A 256px PNG of a flat-shaded
   * bird measures well under this; anything near it is not a portrait.
   */
  MAX_BYTES: 64 * 1024,

  /**
   * The floor under a portrait, which is how an EMPTY render is caught
   * (docs/5 § D260808-0218).
   *
   * A capture taken while the GPU has taken the context away succeeds in every
   * observable way — `toBlob` hands back a real, well-formed, correctly-sized PNG
   * with nothing drawn in it. Every other check passes: it is a still PNG, it is
   * 256 square, it is under the ceiling. So the one property that separates it
   * from a bird is how much it compresses to, and that is not a heuristic here
   * but a measured gap: across all 320 appearances this build can draw (both
   * species, every plumage, every pose) the SMALLEST real portrait is 17,045
   * bytes and the median is 23,100, while a fully transparent one is 2,364. This
   * sits between the two with room on both sides — about 2x above an empty PNG
   * and 2x below the smallest bird — so it cannot refuse a real render and cannot
   * admit an empty one.
   *
   * IT GUARDS THREE DOORS, and the server's is the one that matters most: a
   * portrait is written once and served to everybody, so a single device that
   * offered an empty capture would leave that person invisible beside their own
   * name, for every reader, permanently.
   */
  MIN_BYTES: 8 * 1024,

  /**
   * How long a code may be, in characters.
   *
   * Bounded because it is a path segment, a cache key and a column value. The
   * eight fixed dimensions spell about thirty characters; the rest is headroom
   * for the dress-up layer `birdCode` reserves room for.
   */
  MAX_CODE_CHARS: 96,

  /**
   * How many appearances one account may seed into the shared bucket in a day.
   *
   * NOT A RATION, AND NOTHING A PERSON CAN SPEND IT ON. Seeding is invisible: a
   * device captures its own bird either way, and a refused seed costs its owner
   * nothing at all. This is the ceiling on how much bucket one account can fill
   * by cycling its own appearance, and it is generous against real use — changing
   * a plumage, a pose and a bird in one sitting is three — while bounding the
   * cost of somebody scripting a loop through the closet.
   */
  MAX_SEEDS_PER_DAY: 24,
} as const;

/**
 * Everything that decides what somebody's bird looks like.
 *
 * `extras` IS THE FUTURE, AND IT IS WHY THIS IS A STRING AT ALL. The three named
 * fields are the closed sets that exist today. A cosmetic layer is coming back
 * (docs/5 § D260804-1313 parked sixteen of them), and it will arrive as
 * `{ cap: 'flat', ring: 'brass' }` in here — one entry in a record, no new
 * dimension in a wire type, no migration, and every previously-captured portrait
 * left addressable because its code never mentioned a cap.
 */
export interface BirdAppearance {
  species: SpeciesId;
  plumage: PlumageId;
  pose: BirdPoseId;
  /** Named parts, one per slot. Sorted by key when compiled, so two spellings of
   *  one bird cannot become two portraits. */
  extras?: Readonly<Record<string, string>>;
}

/** The separator between a code's segments, and the one between an extra's key
 *  and its value. Neither may occur inside an id — `isCodeWord` enforces it. */
const CODE_SEP = '.';
const CODE_PAIR = '-';

/** What an id may be made of: lowercase, digits, and nothing that could end a
 *  segment, escape a path, or need encoding in a URL. */
function isCodeWord(value: string): boolean {
  return /^[a-z0-9]+$/.test(value);
}

/**
 * Compile an appearance into the one string that names it
 * (docs/5 § D260806-1118).
 *
 * THE OUTPUT IS AN ADDRESS, NOT A SERIALISATION, and every rule below exists to
 * make one appearance have exactly one spelling:
 *
 *   - **The epoch leads**, so a renderer change rotates every key at once.
 *   - **The three fixed dimensions are positional**, in this order, always
 *     present. A portrait needs all three, so omitting a default would buy
 *     nothing and cost a second spelling of the same bird.
 *   - **Extras are `key-value`, sorted by key.** Sorted rather than authored,
 *     because a client is free to build the record in any order and two orders
 *     would be two objects in the bucket for one bird.
 *
 * It is computed SERVER-SIDE for everybody else's bird (`toPublicAuthor`) so
 * that every viewer of one person asks for the same address, and client-side for
 * your own so the closet can preview a bird nobody has saved yet.
 */
export function birdCode(appearance: BirdAppearance): string {
  const { species, plumage, pose, extras } = appearance;
  const parts = [`b${BIRD_PORTRAIT.EPOCH}`, species, plumage, pose];
  for (const key of Object.keys(extras ?? {}).sort()) {
    const value = extras![key]!;
    if (!isCodeWord(key) || !isCodeWord(value)) {
      throw new Error(`birdCode: '${key}-${value}' is not a code word`);
    }
    parts.push(`${key}${CODE_PAIR}${value}`);
  }
  const code = parts.join(CODE_SEP);
  if (code.length > BIRD_PORTRAIT.MAX_CODE_CHARS) {
    throw new Error(`birdCode: '${code}' is longer than maxCodeChars`);
  }
  return code;
}

/**
 * Read a code back, or `null` if it is not one this build understands.
 *
 * `null` RATHER THAN A THROW, AND NEVER A PARTIAL PARSE. Three different callers
 * hand this an untrusted string — a request path, a column written by a seed
 * script, a response from a server one release ahead — and all three want the
 * same answer to all of "malformed", "from a future epoch" and "names a bird I
 * have never heard of": *I cannot draw this*, which every surface already
 * handles by drawing the fallback bird.
 *
 * IT IS ALSO THE SERVER'S ONLY VALIDATION, and it is deliberately strict about
 * aliasing rather than lenient: the route re-compiles what it parsed and refuses
 * the request unless the result is character-for-character what arrived. An
 * unknown extra therefore cannot be stored, which is what keeps a client one
 * release ahead from seeding portraits at addresses this build cannot compute.
 */
export function parseBirdCode(code: string): BirdAppearance | null {
  if (code.length > BIRD_PORTRAIT.MAX_CODE_CHARS) return null;
  const parts = code.split(CODE_SEP);
  if (parts.length < 4) return null;
  const [epoch, species, plumage, pose, ...rest] = parts as [
    string, string, string, string, ...string[],
  ];
  if (epoch !== `b${BIRD_PORTRAIT.EPOCH}`) return null;
  if (!isSpeciesId(species) || !isPlumageId(plumage) || !isBirdPoseId(pose)) {
    return null;
  }
  const extras: Record<string, string> = {};
  for (const pair of rest) {
    const at = pair.indexOf(CODE_PAIR);
    if (at <= 0) return null;
    const key = pair.slice(0, at);
    const value = pair.slice(at + 1);
    if (!isCodeWord(key) || !isCodeWord(value)) return null;
    /* A repeated key would compile to one entry and therefore to a shorter code
       than the one that arrived, which the round-trip check would catch anyway.
       Refused here so the reason is the actual reason. */
    if (key in extras) return null;
    extras[key] = value;
  }
  const appearance: BirdAppearance = { species, plumage, pose };
  if (rest.length) appearance.extras = extras;
  return appearance;
}

/**
 * Whether `code` is exactly what this build would have written for the bird it
 * describes.
 *
 * The server's gate, and the reason a bucket holds one object per bird rather
 * than one per spelling of a bird. Rejects unsorted extras, a repeated key, a
 * future epoch and an id nobody here knows, in one comparison.
 */
export function isCanonicalBirdCode(code: string): boolean {
  const parsed = parseBirdCode(code);
  return parsed !== null && birdCode(parsed) === code;
}

/**
 * The feed. These numbers exist to make one thing structurally impossible:
 * an influencer (docs/5 § D260801-1119).
 *
 * A follow graph plus a public engagement count is the machinery that
 * manufactures one. A post does well, the author gains followers, the
 * followers raise the reach of the next post, and the loop closes. None of the
 * individual parts look harmful. Removing any one of them breaks the loop, and
 * this product removes all three.
 */
export const FEED_DEFAULTS = {
  /**
   * The share of a settled feed that comes from MUTUAL follows.
   *
   * A target, not a quota, and it is emergent: a new account has no mutuals
   * and so reads almost entirely discovery, and the share climbs toward this
   * as they build their people. Never enforced downward — an account with
   * eight hundred mutuals is not shown strangers to hit a ratio.
   */
  FEED_MUTUAL_TARGET_SHARE: 0.8,
  /**
   * THE ANTI-VIRALITY MECHANISM, and the only one that actually binds.
   *
   * A hard ceiling on how many NON-MUTUAL readers a single post may ever
   * reach. Once it is hit, the post stops being distributed no matter how well
   * it is performing — which is precisely the case a normal ranker treats as a
   * signal to distribute harder.
   *
   * Note what this does NOT cap: a post always reaches all of its author's
   * mutuals. Reach grows only by knowing more people, one at a time and with
   * their agreement, which is the slowest possible growth curve and the point.
   */
  FEED_DISCOVERY_REACH_CAP: 500,
  /** Posts from one author per page. A page is not someone's timeline. */
  FEED_MAX_POSTS_PER_AUTHOR: 2,
} as const;

/**
 * Personal invitations (docs/5 § D260805-0328).
 *
 * ONE LINK PER PERSON, AND IT IS THE ONLY WAY ANYBODY BRINGS ANYBODY HERE. docs/6
 * § Growth without SEO already makes an invite-only waitlist the third growth
 * channel and asks for referral codes outright; docs/7 launches invite-only. This
 * is that, and it is deliberately the smallest possible version of it.
 *
 * WHAT IS NOT IN HERE, because each absence is the decision:
 *
 *   - **No expiry.** A link somebody wrote down, forwarded, or pinned in a group
 *     chat must not rot. An expiring invitation is an urgency mechanic, which
 *     docs/1 § Never build refuses by name.
 *   - **No cap on how many people may use one**, and therefore no count of how
 *     many have. A ceiling has to be enforced with a refusal, a refusal has to be
 *     explained, and explaining it means telling somebody how many invitations
 *     they have left — which is a quota, and quotas produce filler (docs/5 § D260730-1836).
 *     Worse, the same number read the other way is *how many people I brought*,
 *     which is a vanity metric with a growth curve attached (docs/5 § D260801-1119).
 *   - **No reward.** Nothing is earned for inviting: not breadcrumbs, not a tier,
 *     not reach. `CREDIT_REASON` has no entry for it and must not gain one — a
 *     paid referral is a reason to broadcast a link, and broadcasting is the
 *     failure mode this feature has (see § D260805-0328 for what bounds it).
 */
export const INVITE_DEFAULTS = {
  /**
   * Characters in a code, lowercase hex — so 48 bits of CSPRNG entropy.
   *
   * SIZED AGAINST ENUMERATION RATHER THAN AGAINST TYPING, because nobody types
   * one: a code is only ever tapped inside a URL. It has to be unguessable
   * because resolving one returns a fact about a person — their name and their
   * bird — to a caller with no account (docs/5 § D260805-0328), which is the only
   * unauthenticated read in the product. At 2^48 a sweep is not a thing that
   * happens; at eight characters (2^32) it is an afternoon.
   */
  INVITE_CODE_CHARS: 12,
} as const;

/** The romance track (docs/1). Private until mutual, transparent after. */
export const ROMANCE_DEFAULTS = {
  /** Stage at which interest may be declared. Declaring IS the advance. */
  DECLARATION_MIN_STAGE: STAGE.FRIENDS,
  /**
   * Evidence-backed: D'Angelo & Toma (2017) on choice sets, Lee & Niederle
   * (2015) on scarce signals raising acceptance rates. Needs a DB trigger:
   * a TypeScript constant enforces nothing.
   */
  MAX_ACTIVE_DECLARATIONS: 3,
  /**
   * 45 rather than 90: a long private declaration with no presence signals and
   * no synchronous channel is close to a recipe for imagined intimacy.
   * Paused time does not count toward this.
   */
  DECLARATION_EXPIRY_DAYS: 45,
  /**
   * "Not yet" is a STATE, not a budget. No count, no tally, no history.
   * Stanley et al. (2017) and Dobson et al. (2023): perceived commitment
   * asymmetry predicts dissolution, and a visible running tally manufactures
   * exactly that perception.
   */
  PAUSE_LENGTH_DAYS: 7,
  /** Non-response resolves to "not yet" after this. No silent fourth option. */
  DECISION_GRACE_DAYS: 3,
  /** Asked AFTER this cooldown, never in the first minutes of an ending. */
  POST_ENDING_COOLDOWN_HOURS: 72,
} as const;

/**
 * The one boundary decision (TALKING → TOGETHER). Deliberately no fourth,
 * silent option: non-response resolves to `not_yet`, so there is never an
 * ambiguous stall for the other person to misread (docs/1).
 *
 * Unlike a one-sided declaration, these are VISIBLE TO BOTH parties.
 */
export const STAGE_CHOICE = ['keep_going', 'not_yet', 'end'] as const;
export type StageChoice = (typeof STAGE_CHOICE)[number];

/** After an ending. If either picks `strangers`, it's strangers. */
export const ENDING_OUTCOME = ['friends', 'strangers'] as const;
export type EndingOutcome = (typeof ENDING_OUTCOME)[number];

/**
 * Credits are earned for PRO-SOCIAL behaviour, never activity volume, that
 * would collide with "activity boosts; silence never punishes" (docs/1).
 * Cosmetic only. Never purchasable. Never affects ranking.
 *
 * **Called "breadcrumbs" everywhere a user can see** (docs/1 § Your dove).
 * There is ONE currency and ONE ledger. A separate playful currency alongside
 * this serious one was rejected in docs/5 D260731-1551: two balances means two sets of
 * anti-farming rules to keep in sync, and the sinks are identical.
 *
 * The four entries below `photo_fresh` were added with the dove tab. Every one
 * of them is either **reciprocal** (someone else has to act, so it cannot be
 * farmed alone) or **independent of usage volume** (`foraging` accrues while
 * the app is CLOSED). That property is the whole guard, see the note on
 * DOVE_DEFAULTS.
 *
 * `report_upheld` was REMOVED: paying for upheld reports rewards exactly the
 * behaviour docs/2 expects to be weaponised, and reporter_reputation already
 * handles the incentive (docs/2-safety.md).
 *
 * NEVER ADD: anything minted by scrolling, session length, session count, time
 * on screen, post volume, or a consecutive-day condition. The first four are on
 * docs/7's "deliberately not optimised" list precisely because they improve
 * when the product gets more addictive and worse at its job; the last is a
 * streak, which docs/1 bans outright.
 */
export const CREDIT_REASON = [
  'kind_ending',
  'responsiveness',
  'vouch_held',
  'stage_decision',
  'helpful_reply',
  'photo_fresh',
  /** A letter of yours was answered. Reciprocal, min(sent, received). */
  'dawn_flight',
  /** Accrues while the app is closed, once a day, capped. Not usage. */
  'foraging',
  /**
   * You replied to a newcomer's first post. Aimed squarely at docs/7's
   * "is this place alive" guardrail, P50 time-to-first-reply on a new user's
   * first post, which is the metric that decides whether removing the deck
   * was right (docs/5 D260730-2028). Capped at WELCOMES_PER_DAY so it cannot become a
   * reply-farming instruction.
   */
  'welcomed_newcomer',
  /**
   * The daily ration, added to a purse below `CRUMBS_RATION_FLOOR`
   * (docs/5 § D260806-2033). Independent of usage: see the parameter, which
   * carries the argument for why this is not the daily-open reward the list
   * below forbids.
   */
  'daily_ration',
  /**
   * Somebody you write to gave you breadcrumbs (docs/5 § D260806-2033b).
   * Reciprocal in the strongest sense there is — another person spent
   * something to put this row in your ledger.
   */
  'crumb_gift',
] as const;
export type CreditReason = (typeof CREDIT_REASON)[number];

/**
 * Which part of the bird a cosmetic occupies. One equipped item per kind, which
 * is what `profile_cosmetics.equipped` means in docs/4-schema.md.
 *
 * `plumage` is the free set, a full attractive set is free from day one
 * (docs/1), so no plumage may ever carry a cost. The other kinds are the
 * earned dress-up layer. The bird sits beside your name on every post, so a
 * purchasable one would be pay-to-look-better (docs/5 D260730-1838). Private
 * sill backgrounds are deliberately a separate catalogue.
 *
 * The wearable kinds are the four places on the traced silhouette big enough to
 * carry an object legibly at feed size. `cap` was added because **the artwork
 * has no visible leg**, so the two leg slots the schema originally imagined
 * could not both survive: the message capsule became a pouch on the flank, and
 * the head had to carry the dress-up weight instead (docs/5 D260731-1551).
 *
 * `breed` is deliberately NOT implemented: it needs a second traced silhouette,
 * and the accessory geometry is authored against the one we have. Listed so
 * nothing infers the enum is complete.
 */
export const COSMETIC_KIND = [
  'breed',
  'plumage',
  /** On the crown. */
  'cap',
  /** Tied at the iridescent throat. */
  'kerchief',
  /** The pouch the letter rides in, worn on the front flank. */
  'capsule',
  /** A band across the foot, named for the leg ring a racing pigeon wears. */
  'ring',
] as const;
export type CosmeticKind = (typeof COSMETIC_KIND)[number];

/**
 * The dove tab: a companion loop with the Tamagotchi's warmth and none of its
 * debt. Reasoning, evidence and the four rejected alternatives: docs/5 D260731-1551.
 *
 * The load-bearing property of every number here is that **none of them can be
 * moved by spending more time in the app.** Crumbs come from letters being
 * answered, comments being answered, newcomers being welcomed,
 * endings being handled kindly, and from foraging, which fills while the app
 * is CLOSED. Paying for attention is the mechanic docs/7 refuses to optimise
 * and the one the dark-pattern literature names first; it is also the exact
 * thing this tab would be if these sources were chosen carelessly.
 *
 * There is no hunger, no decay, no expiry and no meter that falls. Nothing
 * here is missable, seasonal-exclusive or time-limited, and a balance never
 * goes down except when its owner spends it.
 */
export const DOVE_DEFAULTS = {
  /**
   * Crumbs arriving with the morning delivery window, when a letter does.
   * ONCE A DAY MAXIMUM regardless of how many times the app is opened:
   * docs/7 declines to optimise daily sessions, so this must not reward them.
   */
  CRUMBS_PER_DAWN_FLIGHT: 3,
  /**
   * Minted when a letter of yours is ANSWERED, shaped like rings: on
   * min(from_you, from_them), so unanswered letters earn nothing and the
   * other person's participation is required. Unfarmable alone by
   * construction rather than by policy (docs/5 D260730-1840).
   */
  CRUMBS_PER_RECIPROCAL_LETTER: 5,
  /**
   * A public reply of yours that someone replied to. Reciprocal for the same
   * reason, and it is the highest-weighted familiarity signal there is
   * (docs/1 § Familiarity), so this pays for the behaviour the product
   * already wants rather than for volume.
   */
  CRUMBS_PER_ANSWERED_REPLY: 1,
  /** Replying to a newcomer's first post. See `welcomed_newcomer`. */
  CRUMBS_PER_WELCOME: 2,
  /**
   * Daily cap on welcomes that earn. Without it this reads as "reply to every
   * new account", which is how a kindness becomes a chore and then a spam
   * vector.
   */
  WELCOMES_PER_DAY: 5,
  /**
   * What a full sill holds when your dove has been out foraging.
   *
   * This is the honest answer to "earn crumbs while scrolling the feed", and
   * it is deliberately the inverse: the sill fills while the app is CLOSED and
   * stops at this cap, so there is never a reason to sit in the app waiting
   * for it. The app pays you to come back, never to stay.
   */
  FORAGE_DAILY_CRUMBS: 6,
  /**
   * Hours of the app being closed to fill the sill. Shorter than a day so an
   * ordinary evening visit finds a full one; capped so a week away finds
   * exactly the same full one. **Being away longer is never worth more, and
   * never worth less.**
   */
  FORAGE_FILL_HOURS: 6,
  /**
   * Crumbs to scatter feed on the sill, which is what sends your dove further
   * afield on its next flight and brings back a found stamp.
   *
   * Feeding is a GIFT, not maintenance. The bird is never hungry, so an empty
   * sill is drawn as an empty sill and never as a sad or neglected animal:
   * and no notification, badge or chore card may ever point at it (docs/1).
   *
   * ONE CRUMB, WHICH IS AS CHEAP AS A PRICE CAN BE WITHOUT BEING FREE
   * (docs/5 § D260806-2033). It was four when the balance was a fixture and
   * nothing could refill it; now that `CRUMBS_DAILY_RATION` does, the price no
   * longer has to be the thing that paces feeding. Free would have been wrong
   * in the other direction — a button with no cost is a button, and scattering
   * feed is meant to be a small deliberate gift rather than a fidget.
   */
  FEED_COST_CRUMBS: 1,
  /**
   * Found stamps per week, at most. Stamps are the gift half of the loop:
   * you can only give them away (docs/5 D260730-1838), and scarcity is what keeps a
   * found one feeling like chance rather than a dispenser.
   */
  FOUND_STAMPS_PER_WEEK: 2,
  /**
   * The ration: crumbs added once a calendar day to a purse that is below
   * `CRUMBS_RATION_FLOOR` (docs/5 § D260806-2033).
   *
   * USAGE-INDEPENDENT BY CONSTRUCTION, which is the only reason it may exist
   * beside docs/1 P2. It is not paid for opening the app, for a session, or
   * for a consecutive day: it is the answer to "the purse is empty and there
   * is nothing this person can do about it", and a purse over the floor gets
   * nothing however long its owner stares at the tab. Being away longer is
   * never worth more — a fortnight away accrues exactly one day's ration, the
   * same rule `FORAGE_DAILY_CRUMBS` already follows.
   *
   * IT IS A FLOOR AND NOT AN INCOME, and the distinction is the whole design:
   * somebody who never earns a crumb any other way still has a bird they can
   * feed, and somebody who does earn them is never handed more for it.
   */
  CRUMBS_DAILY_RATION: 10,
  /**
   * The balance under which the ration applies. At or above it, nothing is
   * added — so the ration cannot accumulate into a pile, and it can never be
   * the reason a purse is large.
   */
  CRUMBS_RATION_FLOOR: 30,
  /**
   * What giving somebody breadcrumbs costs the giver, and what it puts in
   * their purse (docs/5 § D260806-2033b).
   *
   * THE GIFT IS WORTH MORE THAN IT COSTS, DELIBERATELY. A one-for-one transfer
   * is an errand; this is the product paying for the generosity rather than
   * the giver, and it is the same argument the found stamp already makes —
   * the payoff of the loop is generosity toward another person (docs/1).
   *
   * IT CANNOT BE FARMED ALONE. There has to be somebody at the other end, and
   * `GIFTS_PER_FRIEND_PER_DAY` bounds what any one pair can be worth in a day.
   * It no longer takes a correspondence (docs/5 § D260808-2014); what took that
   * argument's place is `STRANGER_GIFTS_PER_DAY`, which bounds how many people
   * you do not write to may be given bread in one day.
   */
  CRUMBS_GIFT_COST: 1,
  CRUMBS_GIFT_RECEIVED: 5,
  /**
   * Added to `CRUMBS_GIFT_RECEIVED` for each level the pair has reached
   * (docs/5 § D260807-0202). `giftAmount` is the only place the two are
   * combined.
   *
   * IT PAYS FOR THE CORRESPONDENCE, NOT FOR THE PRESS. The level is the pair's
   * settled figure — the same one the chip draws — so a bigger gift is
   * evidence of letters that were already written and answered, and there is
   * no way to make today's gift worth more today. Giving more often does not
   * move it: `GIFTS_PER_FRIEND_PER_DAY` is still one.
   *
   * IT LEAKS NOTHING THE CHIP DOES NOT. The amount is a function of
   * `settledPoints`, which is already rendered as a level and a quantised
   * fraction on this exact pair's chip — so it moves when and only when the chip
   * beside it moves, including now that both move on the interaction rather than
   * overnight (docs/5 § D260808-0213). An amount computed from the LIVE total would
   * still be a per-pair engagement tally readable by differencing a number in a
   * button: it is the level's coarseness that closes that, not its age.
   */
  CRUMBS_GIFT_PER_LEVEL: 1,
  /**
   * How often one person may be given crumbs. Once a day, so the gesture stays
   * a gesture: a button that can be pressed forty times is a dispenser, and a
   * dispenser is not a gift.
   */
  GIFTS_PER_FRIEND_PER_DAY: 1,
  /**
   * How many people you do not write to may be given bread in one day
   * (docs/5 § D260808-2014).
   *
   * `GIFTS_PER_FRIEND_PER_DAY` BOUNDS A PAIR, AND A PAIR IS NOT THE SET AT RISK.
   * Your correspondents are a handful of people you had to earn; the people you
   * do not write to are everybody, so a per-person ceiling alone leaves the
   * gesture sprayable. This is the ceiling on the gesture itself.
   *
   * WHAT IT ACTUALLY BOUNDS IS THE OTHER PERSON'S SCREEN. A gift files a row
   * naming the giver, so without this an account could put its name in front of
   * a thousand strangers a day, one wordless tap at a time — which is the shape
   * of every poke-adjacent nuisance ever shipped. Three is enough for the
   * gesture to be real and far too few for it to be a broadcast.
   *
   * IT IS ALSO WHAT KEEPS THE PRODUCT'S SIDE HONEST. The product pays
   * `CRUMBS_GIFT_RECEIVED` against a cost of `CRUMBS_GIFT_COST`, so every gift
   * mints crumbs; while a gift needed a correspondence the mint was bounded by
   * how many people somebody actually writes to, and nothing else was bounding
   * it once that requirement went.
   */
  STRANGER_GIFTS_PER_DAY: 3,
} as const;

/**
 * What a gift to this particular pair puts in the recipient's purse
 * (docs/5 § D260807-0202).
 *
 * THE ONLY PLACE THE AMOUNT IS COMPUTED. The server charges and credits from
 * it, and the client draws the number on the button from it — one function, so
 * a button promising six and a ledger recording five is not a state this can
 * reach. The client's copy is a RENDER of the figure and never an input to it:
 * `giftCrumbs()` recomputes from the pair's own row and never reads an amount
 * off the wire, because a number a client sends is a number a client chooses.
 *
 * `points` MUST BE THE SETTLED TOTAL, never the live one — see
 * `CRUMBS_GIFT_PER_LEVEL` for the differencing channel that opens otherwise.
 *
 * NO LEVEL, NO BONUS. `bondLevel` returns null exactly at STRANGERS and at
 * PARTNERED, and the two cases are unlike. STRANGERS is now REACHED rather than
 * unreachable — bread may be given to somebody you do not write to
 * (docs/5 § D260808-2014) — and the base amount is the right answer for it: no
 * level, so no per-level bonus, which is the same sentence this function already
 * said and not a special case. PARTNERED is the top of a ladder the romance
 * track has not built yet and is
 * unreachable today. **When that track lands, this is one of the places that
 * has to be revisited** — a partner falling back to the base amount would be
 * the closest pair in the product receiving the smallest gift. It is left
 * wrong-but-unreachable rather than guessed at, the same call `toViewerRelation`
 * makes about the ceiling figure.
 */
export function giftAmount(stage: Stage, points: number): number {
  const level = bondLevel(stage, points)?.level ?? 0;
  return (
    DOVE_DEFAULTS.CRUMBS_GIFT_RECEIVED +
    level * DOVE_DEFAULTS.CRUMBS_GIFT_PER_LEVEL
  );
}

/**
 * The scenes that may sit behind a person's bird.
 *
 * BACKGROUNDS ARE THE ONE THING BREADCRUMBS BUY TODAY. They change only the
 * private dove tab, never the portrait beside somebody's name, so this remains
 * a keepsake rather than a way to buy status. The city is the scene every
 * account starts with; every other scene is permanent once bought and can be
 * previewed before a crumb moves.
 *
 * The prices deliberately fit the small economy: the three ordinary scenes
 * together cost thirty breadcrumbs and the aurora costs fifty. Someone with
 * roughly eighty breadcrumbs can therefore choose freely between variety now,
 * the one elaborate scene, or the complete set without a rarity ladder, sale,
 * or time pressure.
 */
export const DOVE_BACKGROUND_IDS = [
  'city',
  'treeline',
  'mountains',
  'coast',
  'aurora',
] as const;
export type DoveBackgroundId = (typeof DOVE_BACKGROUND_IDS)[number];

export interface DoveBackground {
  id: DoveBackgroundId;
  name: string;
  note: string;
  cost: number;
}

export const DOVE_BACKGROUNDS: readonly DoveBackground[] = [
  { id: 'city', name: 'City rooftops', note: 'The familiar view', cost: 0 },
  { id: 'treeline', name: 'Pine line', note: 'A quiet edge of woods', cost: 6 },
  { id: 'mountains', name: 'Mountain air', note: 'Two ridges beyond the sill', cost: 10 },
  { id: 'coast', name: 'Open coast', note: 'Headland, water, and one light', cost: 14 },
  { id: 'aurora', name: 'Northern lights', note: 'A bright night over dark pines', cost: 50 },
] as const;

export const DEFAULT_DOVE_BACKGROUND_ID: DoveBackgroundId = 'city';

/**
 * Where breadcrumbs go. `credit_events.amount` is signed, so a spend is a row
 * in the same ledger rather than a second table, and the ledger is the only
 * place a balance may be explained.
 *
 * Note there is no `spent_pacing`, and there never will be: the sinks are
 * private sill scenes, feed, and gifts. Nothing in this product accepts breadcrumbs in exchange
 * for a shorter delivery, more capacity, reach, ranking or an earlier reveal
 * (docs/1 § Cosmetics never touch pacing).
 *
 * `spent_cosmetic` is the legacy ledger name used for a private background;
 * the owned row says which scene, and nothing bought is worn publicly.
 * `gifted_crumbs` is still a gift (docs/5 § D260806-2033b).
 */
export const CRUMB_SPEND_REASON = [
  'spent_cosmetic',
  'spent_feed',
  'gifted_crumbs',
] as const;
export type CrumbSpendReason = (typeof CRUMB_SPEND_REASON)[number];

/**
 * One entry in the breadcrumb ledger, as rendered on the dove tab.
 *
 * Shown as a plain reverse-chronological list of what happened and what it
 * paid. No graph, no weekly total, no "you earned 40% more than last week":
 * a trend line on a cosmetic currency is a performance review nobody asked
 * for, and it would make a quiet fortnight look like a failure.
 */
export interface CrumbEvent {
  id: string;
  reason: CreditReason | CrumbSpendReason;
  /** Positive when earned, negative when spent. Rendered signed. */
  amount: number;
  /** Relative, like every other time in the product. */
  whenLabel: string;
  /** One clause naming what actually happened. Never a score. */
  detail: string;
}

/**
 * The purse, as the dove tab reads it (docs/5 § D260806-2033).
 *
 * ONE CALL FOR THE BALANCE AND THE LEDGER, because they are one fact: the
 * ledger is the only place a balance may be explained, and a client that could
 * hold one without the other would be able to render a number nothing accounts
 * for. It is also the call that accrues the daily ration, so asking what is in
 * the purse and topping it up are the same round trip.
 *
 * NO TOTALS, NO WINDOW, NO TREND. See `CrumbEvent`: a graph over a cosmetic
 * currency is a performance review nobody asked for.
 */
export interface CrumbPurse {
  crumbs: number;
  ledger: CrumbEvent[];
  /** The scene currently behind this person's bird. */
  background: DoveBackgroundId;
  /** Permanent purchases, including the free city scene. */
  ownedBackgrounds: DoveBackgroundId[];
}

/**
 * What a gift to one particular person would be, as a button needs to draw it
 * (docs/5 § D260806-2033b, § D260807-0202).
 *
 * ONE TYPE FOR BOTH ENTRANCES, because there is one gesture. The dove tab's
 * drawer carries a list of these and a card carries exactly one, and a button
 * that reads either has no way to behave differently on the two surfaces.
 *
 * `givenToday` IS A FACT ABOUT YOUR OWN DAY, never about theirs. It says the
 * button has been pressed, not that they have or have not given you anything —
 * one-sided interest is never observable (docs/1), and a "they gave you one
 * back" flag would be exactly that. It is also why this is NOT a field on
 * `ViewerRelation`, whose every member is documented as identical on both
 * people's screens: this one is not, and putting it there would quietly break
 * the property that type is relied on for.
 */
export interface CrumbGiftOffer {
  givenToday: boolean;
  /**
   * What a gift to this person would put in their purse — `giftAmount` of the
   * pair's settled figure, so it varies from row to row (docs/5 § D260807-0202).
   * A pair with no ladder between them is the base amount, which is what makes
   * this well-defined for a stranger (docs/5 § D260808-2014).
   *
   * SENT SO THE BUTTON CAN SAY IT, and read back by nothing. The server
   * recomputes on the write; this field is a rendering.
   */
  amount: number;
}

/**
 * Somebody in the dove tab's drawer (docs/5 § D260806-2033b).
 *
 * THE DRAWER IS YOUR CORRESPONDENTS AND NOBODY ELSE, and it stays that way now
 * that bread may be given more widely (docs/5 § D260808-2014). The two
 * entrances answer two different thoughts: this one is "I want to give somebody
 * something" and has to name a set to pick from, and the only set this product
 * will draw a list of people from is the loft — a list of strangers to give
 * bread to is the browsing surface docs/1 § Deliberately absent refuses. The
 * other entrance is a card you are already looking at, which names nobody.
 */
export interface CrumbGiftTarget extends CrumbGiftOffer {
  other: PublicAuthor;
}

/**
 * What a dove has done, for its owner's eyes only.
 *
 * PRIVATE AND NEVER COMPARABLE. docs/4 forbids a global per-user aggregate of
 * correspondence history because it would be a status score; the resolution
 * (docs/5 D260731-1551) is that the ban is on anything *public or comparable*, and this
 * is a diary. It is never rendered on a social card, never on a post, never
 * beside anyone else's, and there is no leaderboard, percentile or rank
 * anywhere in the product that could consume it.
 *
 * Note what is deliberately absent: **no ring total.** Rings are per-pair and
 * private to the pair, and summing them would build precisely the leaderboard
 * of the people you love that docs/1 refuses. Places, not people.
 */
export interface DoveLogbook {
  /** Real distance between postmarked cities, so it cannot be farmed. */
  milesFlown: number;
  flights: number;
  /** Cities your dove has carried to, at city level only (docs/8). */
  cities: readonly string[];
  stampsFound: number;
  /** Factual label. Advances nothing, the same rule as first contact. */
  flyingSinceLabel: string;
}

/**
 * How a person is rendered anywhere public. The pigeon travels with the name
 *; it is shown beside it on the feed, the profile and every letter (docs/1),
 * so it belongs to the author reference rather than to any one surface.
 */
export interface PublicAuthor {
  handle: string;
  name: string;
  verified: boolean;
  /**
   * Their photograph, as a `media` id rather than a URL (docs/5 § D260804-2359).
   *
   * AN ID, SO THE WIRE IS ENVIRONMENT-AGNOSTIC. The API's origin differs per
   * channel — a path is relative to the app itself inside a Capacitor WebView,
   * and an absolute URL baked server-side would pin every cached response to
   * one host. The client already knows where the API is, so it composes the URL.
   *
   * ABSENT UNTIL SOMEBODY HAS TAKEN ONE, which is most accounts on their first
   * day and some accounts forever: a photograph is not required to use the
   * product, and onboarding lets a person past this step. Every surface
   * therefore has to have something to draw without one, and that thing is the
   * monogram — their initials, `monogramStyle` (docs/5 § D260805-0422). Never a stock
   * face: there is no longer one in the bundle to reach for.
   */
  avatarMediaId?: string;
  /**
   * Which plumage to draw. Earned or chosen, NEVER purchasable, and it carries
   * no rarity, count or rank, a full attractive set is free from day one
   * (docs/1). It is decoration with a name on it, nothing more.
   *
   * STILL HERE ALONGSIDE `bird`, AND NOT REDUNDANTLY. `bird` is an address; this
   * is the value the *fallback* drawing needs. A device with no WebGL, or one
   * whose capture failed, draws `Pigeon.tsx` in these colours, and the eight
   * plumages are also the CSS custom properties that stylesheet lifts in dark
   * mode. Parsing the id back out of the code to recover a fact the wire could
   * simply carry would make every surface depend on the code's grammar.
   */
  plumage: string;
  /**
   * Their bird's appearance, as one compiled code (docs/5 § D260806-1118).
   *
   * WHAT A CLIENT DOES WITH IT: asks for `/birds/<bird>.png`, and captures it
   * locally if nobody has yet. It is opaque to every surface — only the portrait
   * store reads it, and only `parseBirdCode` understands it.
   *
   * COMPILED SERVER-SIDE, WHICH IS THE LOAD-BEARING PART. Two viewers of one
   * person must ask for the same address or the shared capture is worthless, and
   * a client one release behind would spell a code this build does not compute.
   * So `toPublicAuthor` compiles it from the three columns and no client ever
   * builds one for anybody but its own account.
   *
   * THE SPECIES NOW RIDES EVERY FEED ROW, WHICH § D260805-1237 DECIDED AGAINST.
   * It stopped at `toPublicProfile` on the reasoning that a species is only ever
   * seen on its owner's own sill — which was true when the bird beside a name was
   * a flat drawing of a pigeon whatever bird you kept. That inconsistency is the
   * honest cost that entry recorded, and this is it being paid: the bird beside
   * the name is now the bird, so the byline needs to know which one it is. It
   * costs one short string per row and it is derived, so nothing on this tier has
   * two facts to keep in step.
   *
   * OPTIONAL ON THE WIRE, so a cached response written before this shipped still
   * draws a bird: absent means the client composes the default species and pose
   * around `plumage`, which is exactly what those accounts have.
   */
  bird?: string;
}

/**
 * Community level. Six tiers, earned, and meant to be worn.
 *
 * WHAT IT ANSWERS. Whether an account is a real, active member of the community
 * or something made last week. docs/2 treats a fresh throwaway as the main
 * harassment and catfish vector, so a viewer deciding whether to write to
 * someone is entitled to read that off the card at a glance.
 *
 * EARNED FROM RECIPROCAL ACTIVITY: interactions given and received, letters
 * carried, friendships that held, time in the community. Computed server-side.
 * The underlying figures are never sent, so a client cannot render a number, a
 * meter or a distance to the next tier, and nobody can chase one.
 *
 * PRODUCT NOTE, RECORDED RATHER THAN GLOSSED. docs/1 rules out "no level that
 * unlocks anything" and docs/4 rules out a "global per-user aggregate (that
 * would be a status score)". This is a visible earned tier and it is therefore
 * a deliberate divergence from that spec, made on the product owner's explicit
 * and repeated instruction. The constraints kept anyway, because they are the
 * ones that stop it becoming a ladder people optimise for: it unlocks NOTHING,
 * no surface sorts or ranks by it, no ranking path reads it, and it cannot
 * decrease. docs/1 and docs/4 need reconciling with this.
 *
 * PROGRESS TOWARD THE NEXT TIER IS NOW EXPOSED TOO (`levelProgress`, docs/5
 * § D260801-1821), on the same instruction, and that was the fourth constraint until it
 * was overruled. What survives it is the part that was a leak rather than a
 * matter of taste: the underlying FIGURES never reach a client. One rounded
 * fraction of the way to the next tier is sent; the interactions, letters and
 * friendships it is computed from are not, so there is still no count to farm
 * and no arithmetic to reverse.
 */
export const LEVEL = [
  'bronze',
  'silver',
  'gold',
  'platinum',
  'emerald',
  'diamond',
] as const;
export type Level = (typeof LEVEL)[number];

/**
 * What a tier costs, and the ceiling that stops one friendship buying all six
 * (docs/5 § D260808-0209).
 *
 * THE COLUMNS EXISTED AND NOTHING EVER WROTE THEM. `user.level` defaulted to
 * `bronze` and `user.level_progress` was null for every account in the product,
 * so the ring on the card never drew, the meter in the sheet never drew, and a
 * Bronze account opening its own tier sheet was told it was at the top of the
 * scale. The tier was a schema column and a component with no arithmetic in
 * between; this is the arithmetic.
 *
 * THE SCORE IS THE MUTUAL TERM, SUMMED OVER CORRESPONDENCES, AND NOTHING ELSE.
 * `mutualPoints` is the half of `pairPoints` that only rises when both people
 * pay, so nothing a person does alone moves this by a single point — which is
 * what docs/1's "earned from **reciprocated** activity" has to mean if it means
 * anything. The one-sided allowance `pairPoints` grants inside a pair is
 * deliberately not carried across: filling half a rung unanswered is a kindness
 * to a pair who are out of step, and it is not standing in a community.
 *
 * `PAIR_CAP` IS WHAT MAKES IT BREADTH RATHER THAN INTENSITY, and it is the
 * anti-farming property. Without it two accounts writing to each other all year
 * reach Diamond together, which is precisely the sock-puppet shape docs/2 treats
 * as the main vector; with it, the fourth tier takes six correspondences that
 * each became a friendship, and no amount of one correspondence substitutes.
 * "Friendships that held" is therefore not a separate term that had to be
 * counted — it is what the sum counts, once a single pair stops being able to
 * carry it.
 *
 * NOTHING HERE COUNTS ELAPSED TIME, and nothing may be added that does — the
 * same rule `RETURNED_POINTS` states and for the same reason (docs/5
 * § D260801-1622). docs/1 lists "time here" among the inputs and it is honoured
 * the way the bond ladder honours it: the daily caps mean points cannot arrive
 * faster than a real correspondence produces them, so Emerald is years away by
 * construction and no clock term is needed to make it so. A term that ticked on
 * its own would let a dormant account climb, which is the hollow thing
 * D260730-1840 refused for rings.
 *
 * THE FIGURES BEHIND IT STILL NEVER REACH A CLIENT. `communityStanding` returns
 * a tier and one quantised fraction; the score, the per-pair contributions and
 * the correspondence count stay on the server, so the half of the fourth
 * constraint that was a leak survives intact (docs/5 § D260801-1821).
 */
export const COMMUNITY_LADDER = {
  /**
   * The most one correspondence may contribute, however far it goes past it.
   *
   * DERIVED, NEVER AUTHORED — it is the pair total at which two people become
   * friends, so the unit the tiers below are counted in is "a correspondence
   * that became a friendship". Writing the figure out here would be a second
   * set of numbers describing the same ladder.
   */
  PAIR_CAP: stageEntryPoints(STAGE.FRIENDS),
  /**
   * Silver, in points rather than in friendships, because it is below one:
   * mutual back-and-forth with one person that got halfway to a friendship. It
   * is the tier whose line reads "people answer you, and you answer them", and
   * that is a description of having been answered, not of a friendship.
   *
   * HALF A `PAIR_CAP` RATHER THAN ONE LEVEL OF ACQUAINTED (docs/5
   * § D260808-0309). It was `ACQUAINTED_LEVEL_POINTS`, a flat per-level figure
   * that no longer exists — the levels of a stage now cost different amounts, so
   * "a single closed level" names three different numbers and the first of them
   * is 4, which would put Silver within one like each way of Bronze. Half of the
   * unit the tiers above are counted in says the same thing the sentence above
   * says, keeps Silver strictly below one friendship, and — like `PAIR_CAP` —
   * follows the ladder rather than having to be retuned behind it.
   */
  SILVER_POINTS: Math.floor(stageEntryPoints(STAGE.FRIENDS) / 2),
  /** Gold: two correspondences that came back. */
  GOLD_FRIENDSHIPS: 2,
  /** Platinum: enough of them that no one of them is why. */
  PLATINUM_FRIENDSHIPS: 6,
  /** Emerald: a decade's worth at the pace the caps permit, and the tier whose
   *  line says "years of it". */
  EMERALD_FRIENDSHIPS: 15,
  /** Diamond: rare on purpose, and the only tier with nothing above it. */
  DIAMOND_FRIENDSHIPS: 30,
} as const;

/** What a tier costs, in score. Bronze is 0: it is where everybody starts, on
 *  the day they arrive, so it has no threshold to clear. */
export function communityThreshold(level: Level): number {
  const cap = COMMUNITY_LADDER.PAIR_CAP;
  switch (level) {
    case 'bronze':
      return 0;
    case 'silver':
      return COMMUNITY_LADDER.SILVER_POINTS;
    case 'gold':
      return COMMUNITY_LADDER.GOLD_FRIENDSHIPS * cap;
    case 'platinum':
      return COMMUNITY_LADDER.PLATINUM_FRIENDSHIPS * cap;
    case 'emerald':
      return COMMUNITY_LADDER.EMERALD_FRIENDSHIPS * cap;
    case 'diamond':
      return COMMUNITY_LADDER.DIAMOND_FRIENDSHIPS * cap;
  }
}

/**
 * One correspondence's contribution to its two people's standing.
 *
 * The score is capped per pair, so this is where `PAIR_CAP` is applied rather
 * than at the summation: a caller that summed first and clamped after would get
 * one ceiling for a whole life of correspondence instead of one per pair, and
 * the difference is invisible until somebody has two friends.
 */
export function pairStanding(given: number, received: number): number {
  return Math.min(mutualPoints(given, received), COMMUNITY_LADDER.PAIR_CAP);
}

/**
 * Where a score puts somebody: which tier, and how far through it.
 *
 * `progress` IS NULL AT DIAMOND, and that is the same rule the card draws by —
 * a full ring at the top of a scale reads as "complete", which invites the
 * question of what happens next, and nothing does.
 *
 * QUANTISED TO `BOND_LADDER.FRACTION_STEPS`, THE SAME EIGHT, and the anti-
 * differencing argument is far weaker here than it needs to be: a step of this
 * bar is an eighth of a whole tier — tens of interactions, spread across every
 * correspondence somebody has, summed — so no reading of it resolves an act, a
 * day or a person. The eight is reused rather than chosen because two figures
 * describing one rule are two figures that eventually disagree.
 */
export function communityStanding(score: number): {
  level: Level;
  progress: number | null;
} {
  /* Downward, so the first tier whose threshold is cleared is the highest one.
     Bronze is 0 and therefore always clears, which is what makes the loop total
     rather than needing a default. */
  let level: Level = LEVEL[0];
  let index = 0;
  for (let i = LEVEL.length - 1; i >= 0; i--) {
    const candidate = LEVEL[i] as Level;
    if (score >= communityThreshold(candidate)) {
      level = candidate;
      index = i;
      break;
    }
  }

  const next = LEVEL[index + 1] as Level | undefined;
  if (!next) return { level, progress: null };

  const floor = communityThreshold(level);
  const span = communityThreshold(next) - floor;
  const raw = Math.min(Math.max((score - floor) / span, 0), 1);

  const steps = BOND_LADDER.FRACTION_STEPS;
  return { level, progress: Math.round(raw * steps) / steps };
}

/**
 * Where the arrival badge stops meaning anything.
 *
 * Your member number is your join ordinal — 1 is the first account ever — and
 * these are the ceilings of the six bands it can fall in, smallest first. Under
 * 10 is the first band, 11–100 the second, and past the last ceiling there is no
 * badge at all rather than a badge saying "ordinary".
 *
 * IT IS THE ONE MARKER ON A CARD THAT IS NOT EARNED, AND THAT IS WHY IT IS SAFE.
 * A community level rewards behaviour, so it can be chased and it has to be
 * fenced (see `LEVEL`). An ordinal rewards nothing: it is fixed at signup,
 * nobody can move it, and no amount of anything changes it. It unlocks nothing,
 * sorts nothing, and cannot decrease — for the trivial reason that it cannot
 * change at all.
 *
 * THE SCALE RUNS BACKWARDS ON PURPOSE. The badge wears the tier colours in
 * reverse — diamond for the first ten, bronze for the first ten thousand
 * (docs/5 § D260805-0304) — because scarcity runs the other way here: the badge gets
 * commoner as the number grows, and the bands are wide enough that nobody can
 * mistake this for a rank. The last band is a tenth of a percent of a real
 * community.
 *
 * Bands rather than a raw ordinal on the chip is deliberate at the top end: it
 * says "first ten thousand", not "you are number 9,412 of 9,600", which would be
 * a population count and a growth curve for anybody watching it.
 */
export const MEMBER_BANDS = {
  FIRST_TEN: 10,
  FIRST_HUNDRED: 100,
  FIRST_TWO_HUNDRED_FIFTY: 250,
  FIRST_THOUSAND: 1000,
  FIRST_TWENTY_FIVE_HUNDRED: 2500,
  FIRST_TEN_THOUSAND: 10000,
} as const;

/**
 * What a captured photograph has to clear before it is sent (docs/5 § D260805-1206).
 *
 * THREE QUESTIONS, ASKED ON THE DEVICE: is there light on the face, is there a
 * face at all, and is it at a sensible distance. A face detector runs in the
 * browser (BlazeFace short-range, Apache-2.0, weights committed under
 * `public/models/`) and the exposure numbers are read off the pixels inside the
 * box it returns. Nothing is computed on the server — a Worker cannot decode an
 * image inside its CPU budget, which is the same reason the downscale is the
 * client's job (docs/5 § D260804-2359).
 *
 * EVERY FRACTION IS OF THE SQUARE THAT IS ACTUALLY KEPT, not of the camera
 * frame. The avatar is a circle and the capture is cropped to the centred square
 * before anything measures it, so "half the frame" means half of what the person
 * is looking at and half of what other people will see.
 *
 * THE THRESHOLDS ARE CALIBRATED, NOT GUESSED. They are set against
 * `src/assets/faces/` — sixteen real amateur phone selfies, deliberately
 * including bad light and odd framing — with the requirement that every one of
 * them passes, plus synthetically dimmed and blown copies to find where each
 * metric actually crosses. A threshold nobody measured is a threshold that
 * rejects somebody's face for a reason nobody can state.
 *
 * WHICH WAY THESE FAIL IS A DESIGN DECISION AND IT IS OUTWARDS. If the detector
 * cannot be loaded or cannot run, the photograph goes through unchecked: the
 * alternative is a person who cannot set a photograph and cannot be told why.
 * `Done` skips the step entirely on every path, as it already did, because an
 * account without a photograph is an ordinary account.
 *
 * THERE IS NO CONFIDENCE THRESHOLD IN HERE, and its absence is deliberate rather
 * than an omission. The detector neither returns a score nor accepts one: the
 * face/no-face decision is a fixed threshold compiled into the model's own
 * non-max suppression. A parameter in this group that nothing reads would be
 * worse than none, because it would document a knob that does not exist. (Kept in
 * this comment rather than among the entries below, where `gen-parameters.mjs`
 * would attach it as the note on whichever parameter happened to follow it.)
 */
export const CAPTURE_CHECKS = {
  /**
   * Face-box height as a fraction of the kept square, below which it is too far.
   *
   * BlazeFace's box is brow to chin and ear to ear — it excludes hair, so this is
   * smaller than the fraction of the circle a head appears to fill.
   */
  FACE_MIN_HEIGHT_FRACTION: 0.22,
  /** And above which it is too close, where a lens is closer than anybody holds
   *  a phone and the crop takes the top of the head off. */
  FACE_MAX_HEIGHT_FRACTION: 0.82,
  /**
   * How far the face may reach out of the circle, as a multiple of its radius.
   * 1 would mean the chin, forehead and ears all have to be strictly inside it.
   *
   * THE CIRCLE IS THE GATE'S OWN GEOMETRY AND NO LONGER THE FRAME'S
   * (docs/5 § D260805-1523). A photograph is worn as postage now — there is no round avatar
   * left in the product — and the printed area keeps the full height of the square
   * with a tenth trimmed off each side. So this rule is STRICTER than the frame
   * vertically and a little looser at the extreme edges, and it is deliberately not
   * re-tuned: the threshold below was fitted against sixteen real selfies whose
   * harness is gone with the fixtures (docs/5 § D260805-0422), and re-deriving a rectangle
   * rule by eye would risk refusing ordinary photographs to protect a tenth of the
   * width where the same harness measured every real face as centred. What it
   * protects is unchanged — a face correctly sized but pushed to one side is a face
   * the frame cuts, and the preview cannot show that at the size it is drawn.
   *
   * MEASURED AGAINST THE CIRCLE RATHER THAN AS AN OFFSET FROM CENTRE, and the
   * first attempt here was the offset, which is worth recording because the data
   * refused it. Capping the distance from the middle rejected `face-12.jpg` — a
   * perfectly ordinary selfie — and the harness said why: across all sixteen the
   * horizontal offset is small (0.21 at worst) while the vertical runs to −0.27,
   * because a portrait is composed with the face ABOVE centre. An offset rule
   * therefore punishes normal composition, and there is room above centre in a
   * circle anyway. Asking whether the face fits is both the actual question and
   * one number instead of three.
   *
   * Hair is not in it. The detector's box is brow to chin and ear to ear, so a
   * fringe clipped by the mask is not a complaint — that is what every avatar in
   * every product does, and the parts this protects are the ones whose loss makes
   * a photograph unusable.
   *
   * A TENTH OF THE RADIUS OF SLACK, AND IT IS NOT ROUNDING. At exactly 1 the
   * harness rejected `face-09.jpg`, the largest face in the set, which reaches
   * 1.02 — and the same photograph measured either side of the line as the
   * exposure changed, which is the useful part: the box is not the face, and it
   * moves a few percent under different light. A threshold with no room for that
   * refuses the same photograph intermittently, which is the worst way for a gate
   * to be wrong. The cost is that a sliver of a very large face can be cut, and
   * that is the right side to err on: a grazed forehead is cosmetic, and a person
   * who cannot set a photograph of their own face is not.
   */
  FACE_MAX_CIRCLE_REACH: 1.1,
  /**
   * The floor for light on the face: the 90th-percentile luma inside the box.
   *
   * A PERCENTILE AND NOT THE MEAN, and this is the single most important choice
   * in this group. Mean face brightness measures skin as much as it measures
   * light, so a floor under it rejects dark-skinned faces in rooms where
   * light-skinned faces pass — the same bug as a light meter that assumes one
   * subject. What "lit" actually means is that the bright parts of a face are
   * bright: forehead, cheekbone, nose. That holds across every skin tone, and it
   * is what this measures.
   */
  FACE_MIN_HIGHLIGHT_LUMA: 96,
  /** Luma at or above which a pixel has no detail left in it. */
  LUMA_CLIPPED_AT: 250,
  /**
   * Fraction of the face allowed to be clipped before it is too bright.
   *
   * The failure this catches is not a bright room, it is hard side light and
   * front-camera flash: half a face at 255 with a plausible average. A small
   * amount of clipping is ordinary (a specular highlight on a nose is two dozen
   * pixels), so this is a ceiling on how much of a face may be missing rather
   * than a demand that none of it is.
   *
   * The blown-out selfie in the calibration set (`face-16.jpg`, a real one, kept
   * for the mock feed precisely because it is badly lit) measures 0.16, so this
   * sits above it rather than at the round number that would have refused it.
   */
  FACE_MAX_CLIPPED_FRACTION: 0.25,
} as const;

/**
 * A person as their social card renders them, `/u/:handle`, the ONE projection
 * of a person in the product (docs/1 § One projection of a person).
 *
 * Everything here is public to any logged-in viewer, and the list is closed:
 * docs/1 § Profile enumerates it as handle · display name · avatar · bio · city ·
 * joined · counts · posts · photos per tier · verification · pigeon · religion
 * *if displayed*. Anything beyond that is either per-pair (`ViewerRelation`) or
 * does not exist.
 *
 * DELIBERATELY ABSENT, each one load-bearing:
 *
 *   - **A banner / cover photo.** Removed from the spec, not merely unimplemented
 *     (docs/5 § D260801-1640). The card is a centred column with the face at the top of
 *     it, so there is no band for a cover image to fill, and `banner` is gone
 *     from `MEDIA_KIND` for the same reason.
 *
 *   - **`followers` / `following` counts.** docs/1 lists counts as public and
 *     invariant I4 keeps them out of ranking, so they were legal, but a
 *     follower count is still the one number on a profile that people read as a
 *     score for the person, and it is the number a community optimises itself
 *     toward. `standing` answers the question counts were standing in for (is
 *     this account real and active) without being an amount anyone can chase.
 *     Not sent at all, rather than sent and hidden.
 *   - **`viewedBy` / "who viewed you"**, the most direct inference leak
 *     available and permanently declined (docs/8 § D). It will be requested.
 *   - **`followsYou`**, follows are public, but rendering "follows you" on a
 *     card turns a nothing-signal into a reciprocal-interest signal, which is
 *     exactly what docs/8 § D refuses. Following "grants no access and there is
 *     no reciprocal-interest signal attached to it" (docs/1).
 *   - **`lastActiveAt` / online status**, no presence, ever (docs/1).
 *   - **`familiarityScore`**, service-role only (docs/4 § RLS). Neither party
 *     ever sees the number — nor any sentence built from its components, since
 *     docs/5 § D260803-1552.
 *   - **dating metadata**, which no longer reaches a client on any type at any
 *     stage (docs/5 § D260803-1552). It was never in a public profile response; now it
 *     is not on `ViewerRelation` either.
 */
export interface PublicProfile extends PublicAuthor {
  /** Up to 280 chars (docs/4 `profiles.bio`). Optional, most are blank. */
  bio?: string;
  /** City and nothing finer. Never a coordinate, never a neighbourhood. */
  city?: string;
  /** Pre-formatted: the server never hands a client a raw timestamp. */
  joinedLabel: string;
  /** Earned community tier. See `LEVEL`; never a number, never a ranking. */
  level: Level;
  /**
   * How far through the current tier they are, 0–1, for the ring on the chip
   * and the meter in its drilldown (docs/5 § D260801-1821).
   *
   * A FRACTION, AND NEVER THE FIGURES UNDER IT. The interactions, letters and
   * friendships the tier is computed from stay server-side: one rounded number
   * cannot be reversed into them, so there is still nothing to farm and nothing
   * to compare like for like. Coarse on purpose — it is rounded before it is
   * sent, not rounded for display.
   *
   * ABSENT AT `diamond`, because there is nothing above it. A full ring at the
   * top would read as "complete", which invites the question of what happens
   * next; nothing does, and the chip should say that by having no ring.
   */
  levelProgress?: number;
  /**
   * Join ordinal — 1 is the first account ever. See `MEMBER_BANDS`.
   *
   * PUBLIC, LIKE THE JOIN MONTH BESIDE IT, and for the same reason: when
   * somebody arrived is part of what makes an account legible as a real one, and
   * an ordinal is a coarser fact than the date already on the card. Rendered as
   * a band rather than as a raw position past the first few hundred, so a card
   * never doubles as a population counter (see `MEMBER_BANDS`).
   *
   * THE CLIENT DECIDES WHERE THE BADGE STOPS, not the wire. Past the last band
   * the ordinal is still a fact about a public account, and it is still sent;
   * what it does not get is a chip, because a badge everybody has is not one.
   * Optional only because there is no backend yet to know it.
   */
  memberNumber?: number;
  /**
   * NO `vouchedBy` HERE, AND NO DATING PROJECTION EITHER (docs/5 § D260803-1552). The
   * vouch layer is unbuilt (docs/7) and dating metadata is deferred, so neither
   * is on the wire: a field a card cannot render is a field that gets rendered
   * by the next person who finds it.
   */
  /**
   * A name the owner gave their bird. The profile is the one surface with room
   * to say it, on the feed the pigeon is a companion beside a name, here it is
   * a thing a person chose and named (docs/1 § Your pigeon).
   */
  pigeonName?: string;
  /**
   * WHICH bird they keep — see `SPECIES_IDS` (docs/5 § D260805-1237).
   *
   * STILL HERE NOW THAT `PublicAuthor.bird` ENCODES IT (docs/5 § D260806-1118),
   * and for a reason the byline field cannot serve: the sill takes a species id
   * and loads a GLB by it, so `BirdStage` would otherwise have to parse a cache
   * address to discover which bird to build. § D260805-1237's placement argument —
   * that a species is only rendered on its owner's own sill — is what stopped
   * holding; the *use* on this type did not change.
   *
   * Optional so that a client meeting a server that predates the field, or a
   * profile fetched before it was set, falls through `speciesById` to the default
   * rather than to an empty sill.
   */
  species?: string;
  /**
   * WHICH posture their portrait is caught in — see `BIRD_POSE_IDS`
   * (docs/5 § D260806-1118).
   *
   * ON THE PROFILE FOR ONE READER ONLY: the closet, which has to show the pose a
   * person already chose as the selected one. Every other surface wants the
   * compiled `bird` instead, and a pose id on the byline would be a second copy of
   * a fact that address already carries.
   *
   * Optional, falling through `birdPoseById` to `DEFAULT_BIRD_POSE_ID` — the same
   * argument as `species` directly above.
   */
  pose?: string;
  /**
   * Rendered ONLY when the owner set `show_religion` (docs/4 § RLS:
   * "`profiles.religion`, self; others only if `show_religion`"). Declaring is
   * not displaying, and the server omits this field rather than sending a flag.
   */
  religionLabel?: string;
  /** A distinct mark above `verified` (docs/2 § Verification). Opt-in. */
  idVerified?: boolean;
  /**
   * Verified privately, with the displayed photo covered, the accommodation
   * for face-covering users and any reviewed safety reason (docs/2). A distinct
   * marker, never a lesser one.
   */
  privatelyVerified?: boolean;
  /**
   * The occupied marker (docs/1 § The occupied marker). Visible to EVERYONE,
   * and it says only *that* the slot is taken, never with whom unless both
   * opted in, which is why there is no partner field here.
   *
   * A CONNECTION IS A CONNECTION, ON THE APP OR OFF IT (docs/5 § D260801-1623). It is
   * set by reaching CONNECTED with someone here, and it is set by the owner
   * saying they are in one — married, engaged, partnered elsewhere. A person
   * does not need this product to have found somebody, and one that only lets
   * you be taken *by itself* asks married people to either lie or stay away.
   *
   * ONE STATE, NOT TWO SHARING A GLYPH. This is a single boolean with
   * deliberately nothing beside it: no variant, no reason, no second field a
   * client could branch on. Any of those would report whether your partner is
   * on the app — a fact about somebody who may have no account here and never
   * agreed to anything. Both take the same `connection_slots` row (I2 is
   * untouched: one connection above Friends, ever), stop declarations arriving
   * the same way, and clear through the same jittered cooldown, because a
   * marker that vanishes broadcasts a breakup (docs/1).
   */
  inConnection?: boolean;
  /**
   * `paused` and `graduated` change what the card says. Suspended and deleted
   * profiles never reach a client at all (docs/4 § RLS), so they are not here:
   * the route 404s instead, and a deleted author is tombstoned in threads.
   */
  accountState?: Extract<AccountState, 'active' | 'paused' | 'graduated'>;
}

/**
 * Where a pair is inside the stage they are on (docs/5 § D260801-1821, reshaped by
 * § D260802-2151).
 *
 * TWO NUMBERS, AND NEITHER OF THEM IS A TOTAL. The level names the rung; the
 * fraction is how far across it the pair is. What is deliberately NOT here is
 * the point count behind them — a figure with a target above it is a quota, and
 * quotas produce filler (docs/5 § D260730-1836). The only place a total is ever sent is
 * `ViewerRelation.points`, at Partnered, where there is nothing above it to be
 * a fraction of.
 *
 * THE LIST OF STEPS IS GONE (§ D260802-2151). It said things like "it has been spread
 * over weeks, not one evening", which described a requirement the ladder no
 * longer has and never should have had; and once every level is carried by the
 * same four returned interactions, a per-level list of sentences is the same
 * sentence written four times.
 *
 * SYMMETRIC, WHICH IS WHAT MAKES IT SAFE. Both numbers are built from returned
 * pairs — interactions that happened in both directions — so both people are
 * sent identical values. Nothing derived from `familiarity.score` appears in
 * them, and nothing one-directional does either: reading depth in particular
 * would tell you how much somebody has been reading you (docs/4 § RLS).
 */
/**
 * One thing this pair reached, and the day they reached it.
 *
 * THREE RULES, AND EVERY ONE OF THEM IS A LEAK RATHER THAN A STYLE:
 *
 *   - **Milestones live on `ViewerRelation` and never on `PublicProfile`.**
 *     They are populated for the two people in the pair and for nobody else. A
 *     dated Connected milestone on a public card is the correlated-flip attack
 *     the occupied marker spends a paragraph defeating (docs/1) — two cards
 *     changing on the same day identifies the pair.
 *   - **Anything above the line is dated from the DISCLOSED instant**, never
 *     from the row that recorded the event. Mutual declarations are revealed to
 *     both people at once and jittered on purpose (docs/1 § Declaring); a raw
 *     transition timestamp would tell whoever declared first exactly when the
 *     other one did, permanently undoing the jitter that was paid for.
 *   - **Some things are never milestones.** The mirror-photo reveal is
 *     deliberately not an event — no notification, no unlock, no animation — and
 *     a row reading "body photo, 2 April" would convert a designed non-event
 *     into an event after the fact. Nor is a pause, ever (docs/5 § D260801-1120).
 */
export interface Milestone {
  /** What was reached, in the ladder's own words: "Friends", "Friends III",
   *  "Mutual interest declared". Never a sentence about it. */
  label: string;
  /**
   * When, ALREADY FORMATTED — "12 March". Formatted by the server because a
   * date is a rendering decision that depends on locale, and a raw instant on
   * the wire invites a client to compute an age, a gap, or a "you haven't moved
   * in 40 days".
   */
  at: string;
}

export interface StageProgress {
  /**
   * Which level inside the stage, 1-based, and never above `levelsIn(stage)`.
   * The chip renders it as a Roman numeral beside the stage's name.
   */
  level: number;
  /**
   * 0–1 through the CURRENT level, for the ring on the chip and the meter in
   * its drilldown. No unit under it, and no total beside it.
   *
   * QUANTISED TO `BOND_LADDER.FRACTION_STEPS`, WHICH IS NOW THE WHOLE OF THE
   * DEFENCE (I5, docs/5 § D260808-0213). A bar at full precision is an engagement tally
   * with the digits filed off — difference it and you read off what the other
   * person did — and coarse steps are what make the smallest visible move bigger
   * than any single interaction can cause. The bar also used to settle once a
   * day, which closed the timing channel on top of that; it settles on the
   * interaction now, so a step CAN be attributed to a moment. What it cannot be
   * attributed to is an ACT, and every act that moves it is separately on the
   * notifications surface anyway.
   */
  fraction: number;
}

/**
 * What is true BETWEEN the viewer and the person whose card this is.
 *
 * Symmetric by construction. Every field here renders identically for both
 * people, which is what makes it safe to show: nothing in it can reveal a
 * one-sided romantic declaration (I3), and nothing in it is a number either
 * party could farm.
 */
export interface ViewerRelation {
  /** Per-pair, and 0 for the overwhelming majority of cards you will open. */
  stage: Stage;
  /** Public, and grants nothing. The only action a social card offers. */
  following: boolean;
  /**
   * NO SENTENCE OF SHARED HISTORY (docs/5 § D260803-1552). There used to be a
   * `familiarityLine` here — "you've been in six conversations together" — and
   * every version of it was a rendering of behaviour the product does not
   * measure: co-reading is not observed at all, and a topic in common is not
   * something anything computes. What the pair reached is `progress` and
   * `milestones`; nothing here narrates them to each other.
   */
  /**
   * Which level this pair is on and how far through it. Absent below
   * ACQUAINTED — strangers are not on the ladder — and absent at PARTNERED,
   * which has no levels (see `points`).
   */
  progress?: StageProgress;
  /**
   * The pair's running total, sent ONLY where the ladder has stopped: at
   * PARTNERED, and at the top of FRIENDS for a mutually marked friendship.
   *
   * A FIGURE IS SHOWN EXACTLY WHERE THERE IS NOTHING LEFT TO REACH, and that is
   * what reconciles it with the ban on counters (docs/5 § D260730-1836, § D260802-2151). Below a
   * ceiling a figure sits under a threshold and becomes "so many to go", which
   * is a quota and produces filler. At one there is no next level, no ceiling
   * and nothing to compare against — only one pair in a person's life can ever
   * be Partnered (I2), so there is no second number to rank it beside.
   *
   * A FRIENDSHIP GETS THE SAME TREATMENT AS PARTNERED, and that is the whole
   * argument for the shape: a friendship that ran out of rungs and was left
   * with a full bar under a locked one would be a romance that stalled, drawn
   * exactly as docs/1 § Friendship forbids. Where the ladder stops, it stops
   * being a ladder and becomes accumulation.
   *
   * Symmetric like everything else on this type: it counts returned
   * interactions only (`RETURNED_POINTS`), so it is the same figure on both
   * screens and neither person can move it alone.
   */
  points?: number;
  /**
   * What this pair has reached, and when — oldest first.
   *
   * A RECORD, NOT A REQUIREMENT. Every date here is the date something already
   * happened; nothing in the ladder asks for time to pass, and no gate anywhere
   * reads these (docs/5 § D260801-1622). They are safe to render for the same reason the
   * rest of this type is: both people crossed each milestone at the same
   * instant, so both are sent the identical list.
   */
  milestones?: Milestone[];
  /**
   * A letters channel exists. Set for both, or for neither.
   *
   * THERE IS NOTHING TO ACCEPT, AND THAT IS THE POINT (docs/5 § D260801-1622). The
   * channel opens on its own when a pair reaches FRIENDS, for both people at
   * the same instant, because the total that drives it is symmetric and
   * pairwise. No offer, no invitation, no yes — so there is no answer either
   * person can read anything into, which is a stronger version of the leak-free
   * property the mutual opt-in was built to get.
   *
   * OPENING A CHANNEL OBLIGES NOBODY. Nothing arrives, nothing is owed, and no
   * letter has to be answered. Someone who wants nothing to do with it writes
   * nothing, which is indistinguishable from having no channel at all, forever
   * (docs/8 § C) — and "I'm not going to keep writing" is available from the
   * first letter if they would rather say so.
   */
  hasCorrespondence?: boolean;
  /** Marked "just friends", mutually: the ladder stops at the top of FRIENDS,
   *  which is where it already is. Identical pacing, no rungs above. */
  isFriendship?: boolean;
  /**
   * Whether the lead mirror shot has become visible.
   *
   * A SEPARATE FLAG FROM `stage`, ON PURPOSE, and this is the anti-harvesting
   * design, not a modelling convenience. docs/1 gates the mirror shot on
   * `LETTERS_BEFORE_MIRROR_REVEAL` **reciprocal letters each way** rather than
   * on reaching Friends, precisely so the highest-risk asset in the product
   * cannot be farmed by opening correspondences and waiting. Deriving this from
   * `stage` would hand it to anyone who got a channel accepted.
   */
  mirrorRevealed?: boolean;
}

export interface PostMedia {
  kind: PostMediaKind;
  /** Width / height, so the reader's layout does not jump when media loads. */
  aspect: number;
  /** Always present. Media without a description is media some people can't read. */
  alt: string;
  /**
   * WHAT THIS ATTACHMENT ACTUALLY IS.
   *
   * THE TWO KINDS FILL THIS DIFFERENTLY, which is the difference between an
   * embed and an upload rather than a modelling choice. A GIF carries the
   * PROVIDER's id and the provider's URLs — the bytes are never ours
   * (docs/5 § D260805-1220). A still carries the id of a row in `media`, and the
   * bytes are served from our own origin by `GET /media/:id`, so the client
   * composes the URL from the id rather than being handed one
   * (docs/5 § D260808-2252). Optional because a scaffold attachment has neither.
   */
  id?: string;
  /** The animated GIF, fetched only when the reader taps to play it. A still
   *  leaves this empty: its address is `mediaUrl(id)`, which is one origin's
   *  business and not a string to store on a row. */
  url?: string;
  /** One still frame. The poster, so nothing animates unasked (docs/1). */
  stillUrl?: string;
  /**
   * A stand-in for a URL there is no pipeline behind yet. The client draws a
   * deterministic placeholder from this instead; see lib/placeholder.ts.
   */
  seed?: string;
}

/**
 * A still as the upload hands it back, which is also what `POST /posts` accepts
 * (docs/5 § D260808-2252).
 *
 * THE BYTES ARE ALREADY STORED BY THE TIME THIS EXISTS. `PUT /media/post-image`
 * takes one scaled image, keeps it, and answers with this — so a letter's send is
 * a small JSON request whatever is attached to it, and a photograph that failed to
 * upload fails while the writer is still typing rather than when they press Send.
 *
 * `aspect` COMES BACK FROM THE SERVER rather than being sent to it. The dimensions
 * are read out of the stored file's own header (`imageShape`), so the ratio the
 * reader's layout is reserved from is a fact about the bytes rather than a number a
 * client asserted about them.
 */
export interface PostImagePick {
  /** The `media` row's id. `GET /media/:id` is where the bytes are. */
  mediaId: string;
  /** Width / height of the stored file. */
  aspect: number;
  /** What it shows, for somebody who cannot see it. Empty until it is written. */
  alt: string;
}

/**
 * A GIF as the picker hands it over, which is also what `POST /posts` accepts
 * (docs/5 § D260805-1220).
 *
 * IT IS THE PROVIDER'S RECORD, NORMALISED ONCE, ON THE SERVER. KLIPY answers with
 * four sizes × five encodings per result and a client that picked from it would be
 * a client that decides which URL every reader loads — so the Worker chooses, and
 * this is what comes back. `alt` starts as the provider's own description, which is
 * why a GIF is the one attachment that arrives described.
 *
 * THIS SHAPE OUTLIVED A PROVIDER (docs/5 § D260805-1521): Tenor's response looked
 * nothing like KLIPY's, and no client changed. That is the payoff of normalising
 * server-side rather than the reason it was easy.
 */
export interface GifPick {
  /** The provider's id — KLIPY's slug rather than its numeric id, because the slug
   *  is what a share is reported against (`lib/klipy.ts`). Stable, and what a
   *  report or a re-star names. */
  id: string;
  /** The one that plays in the letter: full column width, so a medium size tier — in
   *  whichever animated format is fewer bytes for that result (docs/5 § D260805-1521). */
  url: string;
  /**
   * The one the picker's grid draws: half a phone wide, so the small tier. A grid of
   * the encoding above would be seventeen megabytes to scroll past against this one's
   * four and a half, which is the whole reason the provider offers four sizes.
   */
  previewUrl: string;
  /** One still frame, where the provider has one. The poster, and what a reader
   *  with `prefers-reduced-motion` gets instead of motion. */
  stillUrl?: string;
  width: number;
  height: number;
  alt: string;
}

/** A page of GIFs, and the cursor for the next one. `next` is the provider's own
 *  opaque position — it is passed back untouched and never parsed. */
export interface GifPage {
  results: GifPick[];
  next?: string;
}

/** A choice in an open-letter poll. Counts describe the answers, never the
 * people who gave them; voter identities are intentionally not on the wire. */
export interface PollOptionData {
  id: string;
  text: string;
  votes: number;
}

export interface PollData {
  /** `multiple` lets a writer ask for more than one answer. */
  multiple: boolean;
  options: PollOptionData[];
  /** The current viewer's selections, so a refresh is not required to restore
   * the state of the controls. */
  selectedOptionIds: string[];
}

/*
 * There was a `QuotedPost` here, and a `quoted` field on `PostData` that
 * pointed at it. Quoting is gone from the product (docs/5 § D260803-0131), and so is
 * sending a letter on (§ D260806-1109): having something to SAY about a letter is a
 * comment, written underneath it where its author is present.
 * A quote was the union of the two, and the union is the thing this product
 * exists not to build — commentary about someone, delivered to your own
 * readers rather than to them.
 *
 * The shape it left behind is the one to protect: two bylines inside one unit
 * now mean a conversation and nothing else.
 */

export interface PostData {
  id: string;
  author: PublicAuthor;
  body: string;
  createdAtLabel: string;
  /** A member's arrival note in General. Its author remains the member, so the
   * usual public interactions work exactly as they do on any other open letter. */
  welcome?: true;
  /* No `replyScope`. Anyone who can see a post can reply to it — see the note
     where `REPLY_SCOPE` used to be defined, above `POST_MEDIA_KIND`. */
  /**
   * The attachment channel: one GIF (docs/5 § D260805-1220), or up to
   * POST_MAX_IMAGES stills (docs/5 § D260808-2252). Never both — the two are
   * different objects drawn in different shapes, and a letter carrying a GIF
   * beside three photographs is a collage. No video in v1 (docs/5 § D260731-0006).
   *
   * `POST /posts` TAKES `gif` OR `images` AND THIS RETURNS ONE ARRAY,
   * deliberately: the write side names what it is being given, because the two
   * are stored differently, and the read side is the one channel every card
   * already renders.
   */
  media?: PostMedia[];
  /** An optional interactive attachment. Polls never replace the post body: the
   * body is the question in the writer's own words, while these are the answers. */
  poll?: PollData;
  /**
   * Where it was written, CITY-LEVEL AND NOTHING FINER (docs/5 D260731-0207). Not a
   * venue, not a neighbourhood, and never a coordinate, the same rule that
   * governs postmarks and banded distance (docs/8 § E). A city is already
   * public on the author's profile, so this discloses nothing new about them;
   * a café at 9pm would.
   */
  place?: string;
  /**
   * WHICH SPACE IT WAS WRITTEN IN, AND IT IS REQUIRED.
   *
   * It used to be optional, meaning "set when the post was written into a space
   * rather than the main feed" — a space was a destination you could choose
   * instead of the default, and the default was nowhere in particular. That is
   * backwards for this product. The feed is not a place; it is a view over places,
   * assembled from your mutuals plus a capped discovery slice (docs/3). A post
   * with no space is a post with no room it belongs to, which makes it impossible
   * to say what it is discoverable THROUGH, and it makes "posting" the act of
   * broadcasting rather than of joining a conversation.
   *
   * So every public post has one, and `SPACE_KIND` carries a `general` for the
   * remark that is genuinely not about anything — see the composer, which
   * pre-selects it and offers no way to clear the field. Required in the type is
   * what makes that non-negotiable: an optional field is a field a surface can
   * forget, and the mock forgot it on every generated post for as long as it was
   * allowed to.
   *
   * A SEALED SPACE STILL CONTROLS VISIBILITY, and that is the whole of the
   * audience model now that reply scopes are gone: the query layer returns a
   * sealed space's posts to its members only (docs/4). Membership decides who sees
   * it; seeing it decides who may reply.
   */
  space: PublicSpace;
  /**
   * Who this post replies to. Threads are single-level (MAX_REPLY_DEPTH).
   * The whole author, not a handle: the feed shows display names rather than
   * handles, so a bare handle here would be the only one on the screen.
   */
  replyingTo?: PublicAuthor;
  /**
   * WHICH LETTER THIS COMMENTS ON, present exactly when `replyingTo` is.
   *
   * The id rather than a second copy of the author, because this answers a
   * different question: `replyingTo` is what the card SAYS ("Commenting on
   * Noor"), and this is where the card GOES — the conversation this piece of
   * writing belongs to, which is the letter and never the comment beside it.
   * Both are needed, and one cannot be derived from the other on a client.
   *
   * IT IS THE CODE'S OWN NOUN AND IT NEVER REACHES A STRING (docs/5 § D260803-1952).
   * `replyToId` is the column, this is the field, and what a reader sees is
   * *Commenting on* and a tap that opens the letter.
   *
   * A COMMENT IS IN ITS LETTER'S ROOM, so `space` on a comment is the parent's —
   * the server takes it from the parent rather than from the writer, which is
   * what makes "seeing a letter is what permits commenting on it" true of the
   * room as well as of the author.
   */
  replyToId?: string;
  /*
   * NO `repostOf`. Sending a letter on is removed (docs/5 § D260806-1109), so no card
   * ever carries somebody else's writing under your byline and there is no
   * attribution to invert.
   *
   * WHAT WENT WITH IT IS WORTH NAMING, because it was carrying real weight: reach
   * accrued to the SENDER rather than to the author (§ D260801-1119), which is the
   * arithmetic that stopped a letter travelling well from converting into an
   * audience. That protection is not lost — it is unnecessary, because nothing
   * travels. A letter reaches your mutuals and a capped discovery slice, and the
   * only way anybody else's writing reaches your readers now is that you wrote
   * about it yourself.
   */

  /**
   * Why a post from outside your mutuals is in your feed, in plain words.
   *
   * Present on discovery posts and absent on the rest, which is also how the
   * two are told apart on screen. It may only ever describe CONTENT or SHARED
   * CONVERSATIONS — never how popular something is, never how many people
   * engaged, and never anything about the author as a person.
   */
  discoveryReason?: string;
  /**
   * The body has been rewritten since it was first written
   * (docs/5 § D260805-0418).
   *
   * A FLAG AND NOT A TIME, AND NOT A COUNT. `(edited)` is the whole of what a
   * reader is owed: that the words in front of them are not the words that
   * travelled. "Edited 4 minutes ago" is a timestamp a reader can difference
   * against the letter's own age, and "edited 3 times" is a tally of somebody's
   * second thoughts — a vanity metric pointing the wrong way.
   *
   * The previous versions are kept (`post_revision`), which is what makes the
   * mark honest rather than decorative: the claim reach was earned on still
   * exists. Nothing reads them yet.
   */
  edited?: boolean;
  /**
   * The viewer's own state, and the ONLY engagement state that exists on the
   * wire. There are no counts here — not likes, not comments, at any privacy
   * level. docs/5 § D260801-1119: a public tally is a ranking of people that renders
   * itself, and a number nobody can see is a number somebody eventually ships.
   * You see what you did; there is nothing to compare.
   *
   * `reposted` WAS HERE AND IS GONE (docs/5 § D260806-1109). The four things a person
   * may do with a letter are like it, comment on it, save it and share its
   * address — and only two of those are a state the server holds, because a
   * comment is a letter of its own and sharing is a URL leaving the device.
   */
  liked?: boolean;
  saved?: boolean;
  /**
   * Somebody has answered this letter. That is the whole of what is said
   * (docs/5 § D260813-0012).
   *
   * A BOOLEAN, AND THE TYPE IS THE POINT. The field above says there are no counts
   * here and that is still true: this is not a small count, it is a different kind of
   * fact. § D260801-1119 refuses a tally because "a visible tally is a ranking of
   * people that renders itself" — and a flag cannot rank. One comment and four hundred
   * comments are indistinguishable on the wire and on the card, so there is no
   * gradient to sort by, to compete on, or to farm. Every clause of that argument
   * survives; what does not survive is the silence it left behind, which § D260801-1119
   * itself booked as a cost ("a writer cannot tell whether anything landed").
   *
   * IF THIS EVER BECOMES A NUMBER, THREE DECISIONS HAVE BEEN BROKEN AT ONCE. The
   * schema holds no counter column, the query that fills this is a `selectDistinct`
   * that cannot return an integer (`lib/engagement.ts`), and the card has nowhere to
   * put one. Widening the type here is the first move in undoing all three.
   *
   * ABSENT WHEN NOBODY HAS, like every other optional flag. It is only ever set on a
   * top-level letter: threads are single-level at `MAX_REPLY_DEPTH`, so a comment
   * cannot carry comments.
   *
   * IT IS THE VIEWER'S ANSWER, NOT THE LETTER'S. A comment the viewer may not see —
   * blocked, muted, in a room they have left — does not set it, so the mark never
   * opens an empty conversation.
   */
  hasComments?: boolean;
  /**
   * Present only when every visible comment has been read by this viewer.
   *
   * This is deliberately viewer-local: it never tells an author whether anybody
   * has read their writing, and it remains a boolean rather than a tally.
   */
  commentsSeen?: boolean;
}

/**
 * Whose move it is on a correspondence — one letter's journey, twice, once in
 * each direction.
 *
 * This enum is the reason there is no read receipt in the product. Almost every
 * value below is computable from facts the viewer already owns: whether they have
 * opened a letter, whether they have started answering it, whether their own
 * letter is still waiting for the dawn flight. Nothing here can be inverted to
 * tell the *other* person anything — there is deliberately no `theyHaveRead` and
 * no `theyAreWriting`, and adding one would break a rule docs/1 states in three
 * separate places.
 *
 * `incoming` IS THE ONE STATE THAT IS NOT ABOUT YOU, and the line it does not
 * cross is worth stating precisely, because it is the line every product that
 * has this rule eventually argues itself across (docs/5 § D260803-1923).
 *
 * It says a letter has been SENT to you and has not landed. That is a dispatch
 * notice, and it is none of the three things docs/1 bans: not a read receipt
 * (nothing about what anyone has opened), not an online status (nothing about
 * whether anyone is here), and — the near miss — not a typing indicator. A
 * typing indicator reports INTENT and can be retracted: it appears while someone
 * writes and vanishes if they stop, which is what makes it surveillance and what
 * makes watching one hurt. This appears only after the letter is irrevocably
 * gone, and it resolves into the letter itself at the next
 * `LETTER_DELIVERY_HOUR_LOCAL`. There is no version of it that can be watched
 * for a change of mind, because by the time it exists there is no mind left to
 * change.
 *
 * WHAT IS STILL FORBIDDEN, AND THIS ENUM IS WHERE IT STAYS FORBIDDEN. The
 * mirror image of each state below is a state this product does not have:
 * `incoming` has no outbound twin ("your letter has arrived"), `unread` has no
 * outbound twin ("they have opened it"), and `drafting` has no outbound twin
 * ("they are writing to you"). The first would be a delivery receipt, the second
 * a read receipt, the third a typing indicator wearing the word "draft".
 * Symmetry looks like the obvious tidy-up here and it is the whole prohibition.
 *
 * Note what `settled` does NOT do. When you wrote last and it has flown, the
 * Loft renders no status at all: not "delivered", not "waiting", not a greyed
 * clock. Silence never punishes (I4), and a row that visibly waits on someone
 * is a pressure mechanic with a polite label. `incoming` does not weaken that:
 * it appears when a letter is actually on its way and never as a countdown, so
 * a quiet correspondence is as silent as it always was.
 */
export const LOFT_TURN = [
  /**
   * A letter has been sent to you and lands at the next delivery hour. NOT a
   * typing indicator and not a promise of one — see above.
   */
  'incoming',
  /** A letter has arrived and you have not opened it. */
  'unread',
  /** You have read it and not answered. The only state that gets a nudge. */
  'yours',
  /**
   * You have read it and STARTED answering: a draft of yours is open on this
   * correspondence. Your own unfinished writing, which docs/1 already promises
   * is kept and editable until it flies — this is the Loft finally saying so.
   */
  'drafting',
  /** You have written; it leaves on the next dawn flight. */
  'queued',
  /** You wrote last and it has flown. NOTHING IS RENDERED. */
  'settled',
] as const;
export type LoftTurn = (typeof LOFT_TURN)[number];

/**
 * One row in the Loft: a correspondence.
 *
 * Named for the surface rather than for `threads`, because a thread is chat
 * vocabulary and this is not chat — there is one letter per pair per day, no
 * reply box beneath a letter, and no bubbles.
 *
 * ABSENT ON PURPOSE, each for a rule the docs already settled:
 *
 *   - **No unread count.** A number would make a quiet week look like a debt.
 *     A letter is capped at LETTERS_PER_PAIR_PER_DAY anyway, so the count a
 *     badge could show is at most one per correspondence.
 *   - **No `lastReadByThem`, no presence, no typing.** See `LoftTurn`, which now
 *     has to draw the line rather than merely assert it: a letter already in the
 *     air is `incoming` and a letter someone is still writing is nothing at all.
 *   - **No progress toward the next stage.** SUBSTANTIVE_LETTERS_EACH is real
 *     and must never reach a client as a fraction; "2 of 4" is a quota and
 *     quotas produce filler (docs/5 § D260730-1836). Advancement being automatic makes
 *     this MORE tempting, not less — a gate nobody has to answer is one a
 *     client could helpfully count down to — and it stays banned.
 *   - **Anything that is not a letter.** The loft sends and receives letters
 *     and does nothing else. It used to carry a pending letters offer with its
 *     own buttons, which put a decision about whether to have a channel in the
 *     surface that exists for using one; the channel now opens on its own
 *     (docs/5 § D260801-1622) and there is no decision left to put anywhere.
 *   - **Nothing about pauses at all.** Not a tally, and not the boolean that
 *     replaced it. An earlier version carried `delaysRemainingMine` and
 *     `delaysRemainingTheirs` — the scoreboard docs/5 § D260730-1834 rejects — and then
 *     a `theyAskedForTime` flag, which was still an answer to a question the
 *     product no longer asks. Nothing moves forward by default, so a pause
 *     discloses nothing and is not surfaced (docs/5 § D260801-1120).
 *   - **No rings, miles or stamp counts.** They are per-pair keepsakes and they
 *     belong inside the correspondence. On this screen they would sort into a
 *     league table of the people you love (docs/4).
 */
export interface LoftEntry {
  id: string;
  other: PublicAuthor;
  /**
   * The pair's rung on the ladder — which is no longer the same question as
   * whether they have a channel (docs/5 § D260813-0014).
   *
   * IT MAY NOW BE BELOW FRIENDS, and every surface reading it has to expect
   * that. A mutual follow opens a correspondence on its own, so a row here can
   * sit at STRANGERS with nothing earned; the ladder is unchanged and simply has
   * not been climbed yet. What a row must NOT do is print that rung as a label —
   * see `viaMutual`, and `stageLabelFor`, which is the one place the answer
   * lives.
   */
  stage: Stage;
  /**
   * Which level inside that stage, so a row and a letterhead can draw the same
   * badge the card draws. Absent at PARTNERED, which has no levels — and the
   * pair's running total is deliberately NOT here: a loft is a list, and a
   * number in a list is a column to sort people by.
   */
  level?: number;
  /**
   * How long ago the last letter landed. The Loft sorts on this and on nothing
   * else — never on history, which would be a leaderboard (docs/4).
   */
  lastLetterLabel: string;
  /**
   * The same instant spelled out — "four hours ago". A row's accessible name
   * is a sentence, and "4h" is a column heading rather than something anyone
   * says aloud.
   */
  lastLetterFullLabel: string;
  /**
   * First contact, as a month and year. DISPLAY ONLY: "since March" is a
   * factual label and advances nothing (docs/5 § D260730-1840).
   */
  sinceLabel: string;
  /** Non-romantic: stays at FRIENDS forever, on identical pacing (docs/1). */
  isFriendship?: boolean;
  /**
   * This channel is open because the two of you follow each other, and the pair
   * has not reached FRIENDS yet (docs/5 § D260813-0014).
   *
   * IT IS THE LABEL, AND THAT IS ALL IT IS. A row draws *Mutuals* where this is
   * set, because the alternative was drawing the pair's actual rung — and
   * *Strangers* over a correspondence you are writing into is the product
   * calling two people something neither of them is. It grants nothing, gates
   * nothing, and no capability anywhere reads it.
   *
   * NOT SENT ONCE THE PAIR REACH FRIENDS, even though they are usually still
   * mutual. The channel stopped depending on the follow at that point and the
   * rung is the truer name, so the row goes back to drawing the ladder.
   *
   * ABSENT RATHER THAN FALSE WHEN A LAPSED MUTUAL KEEPS A USED CHANNEL. Two
   * people who unfollow after writing keep what they wrote (the channel closes
   * only while it is empty), and they are no longer mutuals — so the row draws no
   * label at all rather than a stale one. That state is rare and it is the only
   * honest thing to put there.
   */
  viaMutual?: boolean;
  /**
   * YOUR OWN DRAWER: the one row where `other` is you (docs/5 § D260807-2009).
   *
   * A CORRESPONDENCE WITH NO PAIR IN IT, which is the whole of what this flag
   * warns every surface about. There is no bond, no stage worth naming, no level
   * and no second person, so `stage` on this row is `FRIENDS` because the field
   * is not optional and NOT because anything was earned — nothing on screen may
   * read it. It is also never anyone else's move: the Loft files it under your
   * own turn unconditionally, because a drawer that filed itself under *Their
   * turn* would be naming you as somebody who owes yourself a letter.
   *
   * IT IS NOT A SECOND KIND OF LETTER. What is inside is a direct letter — it is
   * sealed, it flies, it lands and it is opened — and the only difference is how
   * long the flight takes (`SELF_LETTER_FLIGHT_SECONDS`) and that both names on
   * it are yours. docs/5 § D260803-1954 still holds: there are two kinds of
   * letter and this is one of them, addressed to the one person who is always
   * already somebody you write to.
   */
  isSelf?: boolean;
  turn: LoftTurn;
  /**
   * The opening of the last letter. Absent on an offer, because an offer has
   * no letters behind it yet.
   */
  preview?: string;
  /** Whether that last letter was yours. */
  previewIsMine?: boolean;
  /**
   * Which letter that one is: its ordinal in the correspondence, counted from
   * the first ever written. The twelfth letter between two people is `12`.
   *
   * AN ORDINAL, NOT A TALLY, and the distinction is the only reason this is
   * allowed to exist next to the "no counts" rule above. A count answers "how
   * much have we done", which is a score two pairs can be compared on; an
   * ordinal answers "which letter is this", which is what numbering a letter has
   * always meant and is a property of the document rather than of the
   * relationship. It is also unavoidable in the surface: a sheet that says
   * `Letter 12` needs the twelve from somewhere, and deriving it from the length
   * of whatever page of letters a client happens to be holding would number the
   * same letter differently on two screens.
   *
   * It is NOT a progress signal and nothing may treat it as one: it advances
   * nothing, it is not compared against a threshold, and no surface sorts on it
   * (docs/4 forbids that outright — sorting correspondences by history is a
   * league table of the people you love).
   */
  letterNumber: number;
}

/**
 * One letter in a correspondence, as the pair's own surface renders it.
 *
 * A LETTER, NOT A MESSAGE, and the fields are what enforce the difference. There
 * is no `readAt`, no `deliveredAt` and no editing: a letter is written, it flies
 * at dawn, and it arrives whole. `LETTERS_PER_PAIR_PER_DAY` caps the pace, so a
 * correspondence is a short list of long things rather than a long list of short
 * ones — which is why this carries the full `body` rather than a preview.
 *
 * ABSENT ON PURPOSE:
 *
 *   - **No read receipt of any kind.** `LoftTurn` explains why at length: every
 *     state the product renders is computable from facts the viewer already owns,
 *     and nothing can be inverted to tell the other person anything.
 *   - **No reactions, no likes, no threading under a letter.** A reply to a
 *     letter is the next letter.
 *   - **No attachments.** "Photos cannot be attached to letters at any stage"
 *     (docs/1), which is a safety rule and not a missing feature.
 *   - **No word count, no time-to-write, no "they are writing".**
 */
export interface LetterData {
  id: string;
  /** Yours, or theirs. There is no third case: letters are per-pair. */
  mine: boolean;
  /** Pre-formatted; the server never hands a client a raw timestamp. */
  dateLabel: string;
  /** The whole letter. See above: there is no expand, because there is no feed
   *  of these to keep short. */
  body: string;
  /**
   * Written, and waiting for the dawn flight. Only ever set on one of YOUR
   * letters — the equivalent state on theirs is not a thing this client is told,
   * because "their letter is queued" is a presence signal.
   */
  queued?: boolean;
  /**
   * Arrived, and you have not opened it. The correspondence draws this one
   * SEALED — an envelope with the letterhead on the outside and no body — and
   * opening it is a deliberate tap.
   *
   * ONLY EVER SET ON A LETTER THAT IS NOT YOURS — with one exception, and it is
   * the exception that proves what the rule was for: a letter you wrote to
   * YOURSELF (docs/5 § D260807-2009) carries both `mine` and `unread`, because
   * the reason this field is one-sided is that the mirrored version would report
   * on somebody else, and in a drawer there is nobody else to report on. It is a
   * fact about what you have opened, which is what it always was.
   *
   * It is the same fact `LoftTurn`'s `unread` reports at correspondence level,
   * not a second one:
   * a pair's turn is `unread` exactly when their newest letter is sealed. It
   * therefore discloses nothing new about the other person — it is a fact about
   * what YOU have opened, which is the only kind of read state this product has
   * (see `LoftTurn`, which explains why there is no inverse).
   */
  unread?: boolean;
  /**
   * Rewritten since it landed (docs/5 § D260805-0418). See `PostData.edited` for
   * why this is a flag rather than a time or a count.
   *
   * ONLY EVER SET ON A LETTER THAT HAS FLOWN. A queued letter is still in the
   * bag: nobody has read the version being replaced, so changing it is not an
   * edit and takes no mark — the same reasoning that keeps `queued` off the
   * recipient's copy.
   */
  edited?: boolean;
  /**
   * A postmark: the city a letter was sent from, at city level only
   * (docs/1 § Letters, docs/8 § E).
   */
  postmark?: string;
}

/*
 * `StrayData` WAS HERE, AND THE OBJECT IS GONE (docs/5 § D260803-1954).
 *
 * A stray was an unaddressed letter a pigeon carried to one stranger at a time,
 * and it was the single sanctioned exception to I1 — which is now absolute:
 * there is no path by which writing reaches a person who has not met you in
 * public. Two kinds of letter remain, and both already have their type: an open
 * letter is `PostData`, and a direct letter inside a correspondence is
 * `LetterData`.
 *
 * Deleted rather than left unused, for the reason docs/5 § D260803-1552 gives: a type a
 * client can see is a type the next screen renders.
 */

/**
 * What can land on the notifications surface.
 *
 * THIS IS THE IN-APP HALF OF docs/1 § Notifications, AND ONLY THAT HALF. That
 * table has two columns and they answer different questions: what buzzes a
 * phone, and what a person finds when they look. Everything below is something
 * that happened in PUBLIC — somebody replied, liked, followed, or a
 * post reached past your mutuals — plus the two pieces of housekeeping that have
 * nowhere else to be said.
 *
 * LETTERS ARE ON IT NOW, AND THEY ARE THE ONE PRIVATE THING THAT IS
 * (docs/5 § D260812-1637). The paragraph that stood here said a letter's surface
 * is the Loft and a second home would make it a feed of interruptions. Half of
 * that survived contact with the product: the Loft IS the surface, which is why
 * the dot for a letter is on the Loft's tab and never on the You tab. What did
 * not survive is the conclusion — a person who is not looking at the Loft is told
 * nothing at all, and the two moments of a letter's journey that are worth
 * knowing are the two nobody was told about. So `letter_on_way` and
 * `letter_arrived` file here, they carry no word of what the letter says, and
 * they light the tab that can act on them rather than the one that cannot.
 *
 * WHAT IS DELIBERATELY NOT A NOTIFICATION KIND:
 *
 *   - **What a letter SAYS.** Both letter kinds are dispatch notices and neither
 *     carries a `context` line, where every other kind that has one quotes the
 *     writing it is about. A row that previewed a sealed letter would open the
 *     envelope on the notifications page, which is the one thing the flight
 *     exists to prevent.
 *   - **Anything on the romance track.** Not a stage change, not a mutual
 *     declaration, not a decision falling due. Those are facts about a pair and
 *     they belong to the pair's own surface. A romance event sitting in a list
 *     next to "somebody liked your post" is also the fastest way to leak one
 *     over a shoulder.
 *   - **One-sided romantic interest**, never, in any form (I3).
 *   - **Anything about your dove.** No badge, no crumbs-ready, no wardrobe news
 *     (docs/1 § P4). "Your dove is waiting" is the sentence that design exists
 *     to make unwritable, and a notification row is a place it could be written.
 *     `crumbs` below is the currency's ledger, which is a different thing: it
 *     says an amount arrived, never that a bird wants attention.
 *   - **"You haven't written in a while"**, and nothing else shaped like it.
 *     Silence never punishes (I4).
 */
export const NOTIFICATION_KIND = [
  /** Somebody answered something you wrote. The signal this product wants. */
  'reply',
  /** Somebody mentioned you in a post. */
  'mention',
  'like',
  /* NO 'repost'. Sending on is removed (docs/5 § D260806-1109), so nothing writes such
     a row and no surface has to word one. Existing rows are deleted by the same
     migration that drops the table they described. */
  'follow',
  /**
   * Somebody you invited has an account now (docs/5 § D260805-0328).
   *
   * THE ONE ROW ON THIS SURFACE ABOUT A PERSON WHO WAS NOT HERE YET, and the only
   * one that carries an action of its own — following them back, which is a
   * choice, because the auto-follow an invitation makes runs one way only. Filed
   * here rather than pushed: nothing about it needs answering today.
   *
   * It is a fact about an arrival, never about a channel. There is no
   * counterpart for "somebody used your link and did not finish", because that
   * would report a stranger's abandoned signup to a third party.
   */
  'invite_joined',
  /**
   * A post of yours is being read beyond your mutuals. NOT a number and not a
   * threshold anyone can chase: docs/5 § D260801-1119 removes every engagement count,
   * and FEED_DISCOVERY_REACH_CAP means this is a ceiling being approached
   * rather than a curve going up.
   */
  'traction',
  /**
   * Breadcrumbs earned — the currency's ledger, never the bird (docs/1).
   *
   * THE ONE KIND THAT SOMETIMES HAS AN ACTOR (docs/5 § D260807-0202). Crumbs
   * that accrued carry none, because nobody did that; crumbs a friend GAVE you
   * carry the giver, and the row names them. It stayed one kind rather than
   * splitting into two, and the reason it gave — that the CHECK on
   * `notification.kind` could not be widened — turned out to be false
   * (docs/5 § D260812-1637b). The reason that survives is the one beside it: the
   * two are the same fact anyway, which is that breadcrumbs arrived. It is not
   * worth a migration to say so twice.
   *
   * IT IS STILL NOT A PIGEON ROW. docs/1 P4 forbids the BIRD asking for
   * attention — a sill that wants filling, a companion that wants opening — and
   * this is a person having done something for you, which is what every other
   * row on that surface is.
   */
  'crumbs',
  /** A lead photo is coming up on LEAD_PHOTO_EXPIRY_MONTHS. */
  'photo_expiring',
  /**
   * Somebody you write to has sent a letter, and it is in the air
   * (docs/5 § D260812-1637).
   *
   * FILED WHEN IT IS SENT, WHICH DISCLOSES NOTHING THE LOFT DOES NOT ALREADY
   * SHOW. The correspondence's row has said *On the way* from the instant of
   * sending since the turn labels were written, so the fact is already the
   * viewer's; what changes is that they no longer have to be on that screen to
   * learn it. It is a dispatch notice after the letter is irrevocably gone,
   * which is the argument `respondState`'s `incoming` branch makes at length
   * about why this is not the read receipt or the typing indicator docs/1 bans.
   *
   * THE ROW HAS A FACE AND THE SENTENCE HAS NO PERSON IN IT — *A letter is on
   * the way*, beside their bird. The moment somebody becomes the grammatical
   * subject the row is reporting a person's behaviour rather than a letter's
   * position, and "Miriam is writing to you" is the sentence a typing indicator
   * writes. The face says who without the sentence saying it.
   *
   * NOTHING LIKE IT IN THE OTHER DIRECTION. There is no kind for your own letter
   * leaving, and none may be added: you wrote it, you watched it go, and the
   * Loft row says *Arrives at dawn*. A notice for a thing you just did is the
   * product congratulating you on using it.
   */
  'letter_on_way',
  /**
   * It landed (docs/5 § D260812-1637). The event docs/1 § Notifications has
   * specified as a push since the table was written, finally filed.
   *
   * THE HOURLY SWEEP WRITES IT, and that is the job `letter.deliverOn` and
   * `letter_deliver_idx` were built for — `db/schema.ts` says outright that the
   * sweep's only job is notification. Delivery itself stays derived, so there is
   * still no `delivered` boolean; `letter.notifiedAt` is a note about this row
   * having been written, which is a different fact and the only one that cannot
   * be recomputed.
   *
   * IT IS NOT THE BADGE. The dot over the Loft is read from the letters
   * themselves — landed, unopened — because opening the letter is what should
   * clear it, and marking a notifications page read is not opening anything.
   * These two facts about one letter are deliberately cleared by two different
   * acts, and each is cleared by the act that answers it.
   */
  'letter_arrived',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KIND)[number];

/**
 * One row on the notifications surface.
 *
 * NEVER AGGREGATED, and this is the one thing about this type that is load
 * bearing. Every comparable product collapses rows into "Aisha and 4 others
 * liked your post", and that string is a public engagement count — the exact
 * thing docs/5 § D260801-1119 removes from posts, re-introduced on another screen with
 * the same effect: a tally of how a post did, which its author reads as a score
 * for themselves. One row per event, or nothing.
 *
 * The consequence is accepted rather than worked around: a well-received post
 * produces a long list. That is honest, it is bounded by the reach cap, and a
 * long list of individual people is a different object from a number going up.
 */
export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  /**
   * Who did it. Absent on the kinds nobody did: `traction` and `photo_expiring`
   * are the app talking to you about your own account, and so is a `crumbs` row
   * for crumbs that merely accrued.
   *
   * `crumbs` IS THE ONE KIND THAT VARIES (docs/5 § D260807-0202): a gift has a
   * giver and names them, the daily ration has nobody. A surface reading this
   * must therefore word that kind twice — see `NOTIFICATION_KIND.crumbs`.
   */
  actor?: PublicAuthor;
  /** Pre-formatted; the server never hands a client a raw timestamp. */
  ageLabel: string;
  /** The same instant spelled out, for the row's accessible name. */
  ageFullLabel: string;
  /**
   * What it was about: the opening of the post that was answered, the reply
   * itself, the reason crumbs arrived. One line, already trimmed server-side.
   */
  context?: string;
  /**
   * THE WRITING THIS ROW IS ABOUT, so the row leads to it (docs/5 § D260805-1222).
   *
   * Present on the kinds that happened to something you wrote — a comment, a like,
   * a letter sent on — and absent on the kinds that happened to *you*: a follow, an
   * arrival, crumbs, a photograph due for renewal. So the row's destination is the
   * letter where there is one and the person where there is not, and a row with
   * neither leads nowhere, which is what those three already did.
   *
   * IT IS THE ID OF THE ROW THAT WAS WRITTEN, which for a comment is the COMMENT and
   * not the letter it answers. Both work as an address: `GET /posts/:id` answers a
   * comment's id with the conversation it is in (§ D260805-1112), so the client links
   * to this value whatever kind it is, and which comment it was survives on the wire
   * for the day the screen can scroll to it.
   *
   * ONLY SENT WHEN THE VIEWER CAN STILL SEE IT. A withdrawn letter, a room they have
   * since left and an author who has since blocked them all arrive here as an absent
   * field rather than as a link to a dead end — so the row quietly falls back to
   * leading nowhere instead of promising something it cannot open. It is a read
   * predicate rather than a delete, so it comes back if the reason goes away.
   */
  postId?: string;
  /**
   * Not yet seen. A boolean per row, and there is deliberately no count
   * anywhere: the bell carries a dot, never a number, for the same reason a
   * post carries no tallies. An unread badge that counts is a queue with a
   * backlog, and this is a list of things that already happened.
   */
  unread?: boolean;
  /**
   * Whether YOU already follow the actor — sent on `invite_joined` and on no
   * other kind.
   *
   * IT IS THE STATE OF A BUTTON, WHICH IS WHY IT IS THIS NARROW. That row is the
   * only one offering an action, so it is the only one that needs to know which
   * of the two labels to draw; a follow edge on every row of a long list would be
   * a per-row lookup nothing renders.
   *
   * NOT A FACT ABOUT THE OTHER PERSON. It is the viewer's own edge, the same one
   * `ViewerRelation.following` already carries, so it discloses nothing new — and
   * in particular there is no inbound twin. "They follow you" is the `follows
   * you` marker docs/8 § D refuses, because a card that renders the reciprocal
   * case turns a nothing-signal into an interest signal.
   */
  youFollow?: boolean;
}

/**
 * What somebody holding an invitation is shown, before they have an account
 * (docs/5 § D260805-0328).
 *
 * THE SMALLEST TYPE IN THE PRODUCT, AND ITS SIZE IS THE SECURITY PROPERTY.
 * `GET /invites/:code` is the only unauthenticated read in Dove — everything else
 * is behind the login wall because user content is never public (docs/2
 * § Indexability) — so what it may answer with is a closed list rather than
 * "whatever a profile happens to carry".
 *
 * IT IS DELIBERATELY NOT A `PublicAuthor`, AND IT IS NOT A NARROWING OF ONE
 * EITHER. That type carries `verified` and `avatarMediaId`, and **neither belongs
 * to a caller with no account**: a face is the one asset docs/2 keeps out of even
 * an OG preview, and a verification mark is a safety signal for people deciding
 * whether to write to a stranger, not a credential to advertise to one. It also
 * has no `pigeonName`, which is the field the welcome screen is built around — so
 * these are a different set of fields rather than a subset, and reusing the
 * byline type would have meant sending two things that must stay behind the login
 * wall in order to get one that is not on it. A separate type also means no future
 * edit to `toPublicAuthor` can widen this endpoint by accident.
 *
 * WHAT IS HERE IS WHAT THE SCREEN IS FOR: enough to recognise the person who
 * asked you here. The name, so the sentence can be written; the handle, so it is
 * the person you think it is; the bird and the name they gave it, because that is
 * the piece of themselves a member chose (docs/1 § Your pigeon) and it is what
 * makes an invitation read as *from somebody* rather than as a marketing page.
 */
export interface InviteWelcome {
  name: string;
  handle: string;
  /** Which plumage to draw. The client owns the palette, so an id is enough —
   *  and it is also the accent the welcome screen is tinted with. */
  plumage: string;
  /**
   * Their bird's appearance, compiled (docs/5 § D260806-1118). See
   * `PublicAuthor.bird`; the same field, for the same reason, on the one screen
   * that draws a member's bird to somebody who does not have an account yet.
   *
   * IT IS NOT A WIDENING OF WHAT THIS TYPE SENDS. A plumage is already here, and a
   * species and a posture say no more about a person than a colour does — all
   * three are decoration they chose. What stays off this type is unchanged: no
   * face, no verification mark.
   */
  bird?: string;
  pigeonName?: string;
}
